// Cotacoes.js - VERSÃO COMPLETA CORRIGIDA - COM PAGINAÇÃO CORRIGIDA
import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import axios from 'axios';
import './Cotacoes.css';
import { Icon } from './SharedComponents';
import ClienteModal from './ClienteModal';
import ProdutoModal from './ProdutoModal';
import ServicoModal from './ServicoModal';

const API_BASE_URL = '/api';

// 🔥 STATUS DA COTAÇÃO
const STATUS_COTACAO = [
    { value: 'Rascunho', label: 'Rascunho', color: '#6b7280', icon: 'file' },
    { value: 'Pendente', label: 'Pendente', color: '#f59e0b', icon: 'pending' },
    { value: 'Aprovado', label: 'Aprovado', color: '#10b981', icon: 'check_circle' },
    { value: 'Expirado', label: 'Expirado', color: '#dc2626', icon: 'block' },
    { value: 'Cancelado', label: 'Cancelado', color: '#6b7280', icon: 'cancel' },
    { value: 'Duplicado', label: 'Duplicado', color: '#8b5cf6', icon: 'content_copy' },
];

const PERCENTUAIS_IVA_PADRAO = [0, 5, 15, 16];
const ITEMS_PER_PAGE = 6;
const SEARCH_ITEMS_PER_PAGE = 5;

const getStatusInfo = (status) => {
    if (!status) return { value: 'Pendente', label: 'Pendente', color: '#f59e0b', icon: 'pending' };
    const found = STATUS_COTACAO.find(s => s.value === status);
    return found || { value: status, label: status, color: '#6b7280', icon: 'help' };
};

// =============================================
// COMPONENTE TOAST PARA MODAIS - POSIÇÃO INFERIOR DIREITA
// =============================================
function Toast({ message, type, onClose }) {
    useEffect(() => {
        if (message) {
            const timer = setTimeout(() => {
                onClose();
            }, 4000);
            return () => clearTimeout(timer);
        }
    }, [message, onClose]);

    if (!message) return null;

    const icons = {
        success: 'check_circle',
        danger: 'error',
        warning: 'warning',
        info: 'info'
    };

    const colors = {
        success: '#10b981',
        danger: '#ef4444',
        warning: '#f59e0b',
        info: '#3b82f6'
    };

    return (
        <div className="toast-container" style={{
            position: 'fixed',
            bottom: '20px',
            right: '20px',
            zIndex: 99999,
            display: 'flex',
            flexDirection: 'column',
            gap: '8px',
            maxWidth: '400px',
            width: '100%',
            pointerEvents: 'none'
        }}>
            <div className={`toast-message toast-${type}`} style={{
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                padding: '12px 16px',
                background: '#ffffff',
                borderRadius: '12px',
                boxShadow: '0 10px 40px rgba(0,0,0,0.15), 0 4px 12px rgba(0,0,0,0.05)',
                borderLeft: `4px solid ${colors[type] || '#6b7280'}`,
                pointerEvents: 'auto',
                animation: 'slideInRight 0.3s ease-out',
                transition: 'all 0.3s ease'
            }}>
                <Icon
                    name={icons[type] || 'info'}
                    size={20}
                    style={{ color: colors[type] || '#6b7280', flexShrink: 0 }}
                />
                <span style={{
                    flex: 1,
                    fontSize: '0.875rem',
                    color: '#1a1a2e',
                    fontWeight: 500,
                    lineHeight: 1.4
                }}>
                    {message}
                </span>
                <button
                    onClick={onClose}
                    style={{
                        background: 'none',
                        border: 'none',
                        cursor: 'pointer',
                        color: '#9ca3af',
                        padding: '4px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        borderRadius: '50%',
                        transition: 'all 0.2s ease',
                        flexShrink: 0
                    }}
                    onMouseEnter={(e) => {
                        e.currentTarget.style.background = '#f3f4f6';
                        e.currentTarget.style.color = '#4b5563';
                    }}
                    onMouseLeave={(e) => {
                        e.currentTarget.style.background = 'transparent';
                        e.currentTarget.style.color = '#9ca3af';
                    }}
                >
                    <Icon name="close" size={16} />
                </button>
            </div>
            <style>{`
                @keyframes slideInRight {
                    from {
                        opacity: 0;
                        transform: translateX(40px);
                    }
                    to {
                        opacity: 1;
                        transform: translateX(0);
                    }
                }
                @keyframes slideOutRight {
                    from {
                        opacity: 1;
                        transform: translateX(0);
                    }
                    to {
                        opacity: 0;
                        transform: translateX(40px);
                    }
                }
                .toast-message {
                    animation: slideInRight 0.3s ease-out;
                }
                .toast-message.removing {
                    animation: slideOutRight 0.3s ease-in forwards;
                }
            `}</style>
        </div>
    );
}

// =============================================
// COMPONENTE DE PAGINAÇÃO PARA ITENS
// =============================================
function ItensPagination({ currentPage, totalPages, onPageChange }) {
    if (totalPages <= 1) return null;

    const getPageNumbers = () => {
        const pages = [];

        if (totalPages <= 5) {
            for (let i = 1; i <= totalPages; i++) {
                pages.push(i);
            }
            return pages;
        }

        pages.push(1);

        let startPage = Math.max(2, currentPage - 1);
        let endPage = Math.min(totalPages - 1, currentPage + 1);

        if (currentPage <= 3) {
            startPage = 2;
            endPage = Math.min(totalPages - 1, 4);
        }

        if (currentPage >= totalPages - 2) {
            startPage = Math.max(2, totalPages - 3);
            endPage = totalPages - 1;
        }

        if (startPage > 2) {
            pages.push('...');
        }

        for (let i = startPage; i <= endPage; i++) {
            pages.push(i);
        }

        if (endPage < totalPages - 1) {
            pages.push('...');
        }

        pages.push(totalPages);

        return pages;
    };

    const renderPageNumbers = () => {
        const pageNumbers = getPageNumbers();

        return pageNumbers.map((p, index) => {
            if (p === '...') {
                return (
                    <span key={`dots-${index}`} className="pagination-dots">
                        …
                    </span>
                );
            }
            return (
                <button
                    key={p}
                    className={`pagination-number ${p === currentPage ? 'active' : ''}`}
                    onClick={() => onPageChange(p)}
                >
                    {p}
                </button>
            );
        });
    };

    return (
        <div className="itens-pagination" style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            gap: '0.25rem',
            padding: '0.5rem 0.5rem 0.25rem 0.5rem',
            marginTop: '0.25rem',
            borderTop: '1px solid #e5e7eb'
        }}>
            <button
                className="pagination-btn"
                onClick={() => onPageChange(currentPage - 1)}
                disabled={currentPage === 1}
                style={{
                    width: '26px',
                    height: '26px',
                    border: '1px solid #e5e7eb',
                    borderRadius: '50%',
                    background: 'transparent',
                    color: '#1a1a2e',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    transition: 'all 0.2s ease',
                    opacity: currentPage === 1 ? 0.4 : 1,
                    pointerEvents: currentPage === 1 ? 'none' : 'auto'
                }}
            >
                <Icon name="chevron_left" size={14} />
            </button>
            {renderPageNumbers()}
            <button
                className="pagination-btn"
                onClick={() => onPageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
                style={{
                    width: '26px',
                    height: '26px',
                    border: '1px solid #e5e7eb',
                    borderRadius: '50%',
                    background: 'transparent',
                    color: '#1a1a2e',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    transition: 'all 0.2s ease',
                    opacity: currentPage === totalPages ? 0.4 : 1,
                    pointerEvents: currentPage === totalPages ? 'none' : 'auto'
                }}
            >
                <Icon name="chevron_right" size={14} />
            </button>
        </div>
    );
}

// =============================================
// COMPONENTE SEARCH INPUT REUTILIZÁVEL COM PAGINAÇÃO
// =============================================
function SearchInput({
    value,
    onChange,
    onSelect,
    onClear,
    placeholder,
    items = [],
    getItemLabel,
    getItemSub,
    getItemTag,
    getItemPrice,
    getItemSearchFields,
    getItemStatus,
    selectedId,
    iconName = 'search',
    noResultsMessage = 'Nenhum resultado encontrado',
    className = '',
    inputStyle = {},
    dropdownStyle = {},
    itemsPerPage = SEARCH_ITEMS_PER_PAGE,
    ...props
}) {
    const [isOpen, setIsOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState(value || '');
    const [currentPage, setCurrentPage] = useState(1);
    const inputRef = useRef(null);
    const dropdownRef = useRef(null);
    const wrapperRef = useRef(null);

    useEffect(() => {
        setSearchTerm(value || '');
    }, [value]);

    const filteredItems = useMemo(() => {
        if (!items || !Array.isArray(items) || items.length === 0) {
            return [];
        }

        if (!searchTerm || searchTerm.trim() === '') {
            return items;
        }

        const search = searchTerm.toLowerCase().trim();

        return items.filter(item => {
            if (!item) return false;

            const searchableFields = [];

            if (getItemLabel) {
                searchableFields.push(getItemLabel(item).toLowerCase());
            } else if (item.nome) {
                searchableFields.push(item.nome.toLowerCase());
            }

            if (getItemSearchFields) {
                const extraFields = getItemSearchFields(item);
                if (Array.isArray(extraFields)) {
                    searchableFields.push(...extraFields.map(f => String(f).toLowerCase()));
                }
            }

            if (getItemStatus) {
                const status = getItemStatus(item);
                if (status) {
                    searchableFields.push(String(status).toLowerCase());
                }
            } else if (item.status) {
                searchableFields.push(String(item.status).toLowerCase());
            } else if (item.estado) {
                searchableFields.push(String(item.estado).toLowerCase());
            }

            if (item.codigo) searchableFields.push(item.codigo.toLowerCase());
            if (item.nif) searchableFields.push(item.nif.toLowerCase());
            if (item.nuit) searchableFields.push(item.nuit.toLowerCase());
            if (item.email) searchableFields.push(item.email.toLowerCase());
            if (item.telefone) searchableFields.push(item.telefone.toLowerCase());
            if (item.documento) searchableFields.push(item.documento.toLowerCase());
            if (item.referencia) searchableFields.push(item.referencia.toLowerCase());
            if (item.tipo) searchableFields.push(String(item.tipo).toLowerCase());
            if (item.categoria) {
                if (typeof item.categoria === 'string') {
                    searchableFields.push(item.categoria.toLowerCase());
                } else if (item.categoria?.nome) {
                    searchableFields.push(item.categoria.nome.toLowerCase());
                }
            }

            if (item.morada) {
                if (item.morada.cidade) searchableFields.push(item.morada.cidade.toLowerCase());
                if (item.morada.bairro) searchableFields.push(item.morada.bairro.toLowerCase());
                if (item.morada.provincia) searchableFields.push(item.morada.provincia.toLowerCase());
                if (item.morada.rua) searchableFields.push(item.morada.rua.toLowerCase());
                if (item.morada.pais) searchableFields.push(item.morada.pais.toLowerCase());
            }

            if (item.contacto) {
                if (item.contacto.nome) searchableFields.push(item.contacto.nome.toLowerCase());
                if (item.contacto.telefone) searchableFields.push(item.contacto.telefone.toLowerCase());
                if (item.contacto.email) searchableFields.push(item.contacto.email.toLowerCase());
            }

            if (item.empresa) {
                if (item.empresa.nome) searchableFields.push(item.empresa.nome.toLowerCase());
                if (item.empresa.nif) searchableFields.push(item.empresa.nif.toLowerCase());
            }

            return searchableFields.some(field => field && field.includes(search));
        });
    }, [items, searchTerm, getItemLabel, getItemSearchFields, getItemStatus]);

    const totalPages = Math.ceil(filteredItems.length / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const paginatedItems = filteredItems.slice(startIndex, startIndex + itemsPerPage);

    useEffect(() => {
        setCurrentPage(1);
    }, [searchTerm]);

    const getPageNumbers = useCallback(() => {
        const pages = [];
        const total = totalPages;

        if (total <= 5) {
            for (let i = 1; i <= total; i++) {
                pages.push(i);
            }
            return pages;
        }

        pages.push(1);

        let startPage = Math.max(2, currentPage - 2);
        let endPage = Math.min(total - 1, currentPage + 2);

        if (currentPage <= 4) {
            startPage = 2;
            endPage = Math.min(total - 1, 5);
        }

        if (currentPage >= total - 3) {
            startPage = Math.max(2, total - 4);
            endPage = total - 1;
        }

        if (startPage > 2) {
            pages.push('...');
        }

        for (let i = startPage; i <= endPage; i++) {
            pages.push(i);
        }

        if (endPage < total - 1) {
            pages.push('...');
        }

        pages.push(total);

        return pages;
    }, [currentPage, totalPages]);

    const handleSelect = (item) => {
        if (onSelect) onSelect(item);
        setIsOpen(false);
        if (inputRef.current) inputRef.current.blur();
    };

    const handleClear = () => {
        if (onClear) onClear();
        setSearchTerm('');
        setIsOpen(false);
        if (inputRef.current) inputRef.current.focus();
    };

    const handleInputChange = (e) => {
        const val = e.target.value;
        setSearchTerm(val);
        if (onChange) onChange(val);
        setIsOpen(true);
        if (val === '' && onClear) {
            onClear();
        }
    };

    const handleInputFocus = () => {
        const hasItems = items && Array.isArray(items) && items.length > 0;
        if (searchTerm.trim() !== '' || hasItems) {
            setIsOpen(true);
        }
    };

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const hasValue = !!selectedId || searchTerm?.trim() !== '';

    const preventFormSubmit = (e) => {
        if (e) {
            e.preventDefault();
            e.stopPropagation();
        }
    };

    const renderPagination = () => {
        if (totalPages <= 1) return null;

        const pageNumbers = getPageNumbers();

        return (
            <div
                className="search-dropdown-pagination"
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '0.15rem',
                    padding: '0.4rem 0.5rem',
                    borderTop: '1px solid #e5e7eb',
                    background: 'rgba(0,0,0,0.02)',
                    flexWrap: 'wrap'
                }}
                onMouseDown={preventFormSubmit}
                onClick={preventFormSubmit}
            >
                <button
                    type="button"
                    className="pagination-btn"
                    onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setCurrentPage(prev => Math.max(1, prev - 1));
                    }}
                    disabled={currentPage === 1}
                    style={{
                        width: '24px',
                        height: '24px',
                        border: '1px solid #e5e7eb',
                        borderRadius: '50%',
                        background: 'transparent',
                        color: '#1a1a2e',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        transition: 'all 0.2s ease',
                        opacity: currentPage === 1 ? 0.4 : 1,
                        pointerEvents: currentPage === 1 ? 'none' : 'auto',
                        fontSize: '12px',
                        padding: 0
                    }}
                >
                    <Icon name="chevron_left" size={12} />
                </button>

                {pageNumbers.map((p, index) => {
                    if (p === '...') {
                        return (
                            <span key={`dots-${index}`} style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                minWidth: '20px',
                                height: '24px',
                                color: '#6b7280',
                                fontSize: '0.7rem',
                                fontWeight: 600,
                                userSelect: 'none'
                            }}>
                                …
                            </span>
                        );
                    }
                    return (
                        <button
                            key={p}
                            type="button"
                            onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                setCurrentPage(p);
                            }}
                            style={{
                                minWidth: '24px',
                                height: '24px',
                                padding: '0 0.15rem',
                                border: p === currentPage ? '1px solid #4f46e5' : '1px solid transparent',
                                borderRadius: '50%',
                                background: p === currentPage ? '#4f46e5' : 'transparent',
                                color: p === currentPage ? '#ffffff' : '#1a1a2e',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontSize: '0.6rem',
                                fontWeight: p === currentPage ? 700 : 500,
                                transition: 'all 0.2s ease'
                            }}
                            onMouseEnter={(e) => {
                                if (p !== currentPage) {
                                    e.currentTarget.style.background = '#f3f4f6';
                                    e.currentTarget.style.borderColor = '#e5e7eb';
                                }
                            }}
                            onMouseLeave={(e) => {
                                if (p !== currentPage) {
                                    e.currentTarget.style.background = 'transparent';
                                    e.currentTarget.style.borderColor = 'transparent';
                                }
                            }}
                        >
                            {p}
                        </button>
                    );
                })}

                <button
                    type="button"
                    className="pagination-btn"
                    onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setCurrentPage(prev => Math.min(totalPages, prev + 1));
                    }}
                    disabled={currentPage === totalPages}
                    style={{
                        width: '24px',
                        height: '24px',
                        border: '1px solid #e5e7eb',
                        borderRadius: '50%',
                        background: 'transparent',
                        color: '#1a1a2e',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        transition: 'all 0.2s ease',
                        opacity: currentPage === totalPages ? 0.4 : 1,
                        pointerEvents: currentPage === totalPages ? 'none' : 'auto',
                        fontSize: '12px',
                        padding: 0
                    }}
                >
                    <Icon name="chevron_right" size={12} />
                </button>

                <span style={{
                    fontSize: '0.55rem',
                    color: '#9ca3af',
                    marginLeft: '0.2rem'
                }}>
                    {filteredItems.length} itens
                </span>
            </div>
        );
    };

    const darkModeStyles = `
        .dark-mode-variables .search-dropdown-pagination {
            border-top-color: rgba(255, 255, 255, 0.08);
            background: rgba(255, 255, 255, 0.03);
        }
        .dark-mode-variables .search-dropdown-pagination .pagination-btn {
            border-color: rgba(255, 255, 255, 0.1);
            color: rgba(255, 255, 255, 0.7);
        }
        .dark-mode-variables .search-dropdown-pagination .pagination-btn:hover:not(:disabled) {
            background: rgba(255, 255, 255, 0.1);
        }
        .dark-mode-variables .search-dropdown-pagination button:not(.pagination-btn) {
            color: rgba(255, 255, 255, 0.7);
        }
        .dark-mode-variables .search-dropdown-pagination button:not(.pagination-btn):hover {
            background: rgba(255, 255, 255, 0.08) !important;
            border-color: rgba(255, 255, 255, 0.15) !important;
        }
        .dark-mode-variables .search-dropdown-pagination .pagination-number.active {
            background: #4f46e5 !important;
            color: #ffffff !important;
            border-color: #4f46e5 !important;
        }
        .dark-mode-variables .search-dropdown-pagination .pagination-dots {
            color: rgba(255, 255, 255, 0.4);
        }
        .dark-mode-variables .search-dropdown-pagination .itens-count {
            color: rgba(255, 255, 255, 0.3);
        }
    `;

    return (
        <div className={`search-input-wrapper ${className}`} ref={wrapperRef}>
            <style>{darkModeStyles}</style>
            <div className="search-input-inner">
                <Icon name={iconName} size={16} className={`search-icon ${hasValue ? 'has-value' : ''}`} />
                <input
                    ref={inputRef}
                    type="text"
                    className={`search-input-field ${hasValue ? 'has-value' : ''}`}
                    placeholder={placeholder}
                    value={searchTerm}
                    onChange={handleInputChange}
                    onFocus={handleInputFocus}
                    style={inputStyle}
                    {...props}
                />
                {selectedId && (
                    <button type="button" className="search-clear-btn" onClick={handleClear} title="Limpar seleção">
                        <Icon name="close" size={14} />
                    </button>
                )}
            </div>
            {isOpen && (
                <div className="search-dropdown" ref={dropdownRef} style={dropdownStyle}>
                    {filteredItems.length === 0 ? (
                        <div className="search-dropdown-empty">
                            {items && Array.isArray(items) && items.length > 0
                                ? noResultsMessage
                                : 'Carregando itens...'}
                        </div>
                    ) : (
                        <>
                            {paginatedItems.map((item, index) => (
                                <div
                                    key={item._id || index}
                                    className="search-dropdown-item"
                                    onClick={() => handleSelect(item)}
                                >
                                    <div className="search-dropdown-item-info">
                                        <span className="item-name">
                                            {getItemLabel ? getItemLabel(item) : item.nome}
                                        </span>
                                        {getItemSub && getItemSub(item) && (
                                            <span className="item-sub">{getItemSub(item)}</span>
                                        )}
                                        {getItemStatus && getItemStatus(item) && (
                                            <span className="item-status" style={{
                                                fontSize: '0.6rem',
                                                padding: '0.05rem 0.5rem',
                                                borderRadius: '100px',
                                                background: '#e5e7eb',
                                                color: '#6b7280',
                                                fontWeight: 500,
                                                marginLeft: '0.3rem'
                                            }}>
                                                {getItemStatus(item)}
                                            </span>
                                        )}
                                    </div>
                                    <div className="search-dropdown-item-actions">
                                        {getItemTag && getItemTag(item) && (
                                            <span className="item-tag">{getItemTag(item)}</span>
                                        )}
                                        {getItemPrice && getItemPrice(item) && (
                                            <span className="item-price">{getItemPrice(item)}</span>
                                        )}
                                    </div>
                                </div>
                            ))}
                            {renderPagination()}
                        </>
                    )}
                </div>
            )}
        </div>
    );
}

// =============================================
// 🔥 COMPONENTE TOGGLE PRODUTO/SERVIÇO - CORRIGIDO COM SUBMODULOS
// =============================================
function ToggleProdutoServico({
    tipo,
    onChange,
    onAddProduto,
    onAddServico,
    disabled = false,
    showProduto = true,
    showServico = true
}) {
    console.log('🔧 ToggleProdutoServico recebeu:', { showProduto, showServico, tipo });

    const tipoEfetivo = useMemo(() => {
        if (!showProduto && showServico) {
            console.log('📌 Apenas Serviços disponível');
            return 'servico';
        }
        if (showProduto && !showServico) {
            console.log('📌 Apenas Produtos disponível');
            return 'produto';
        }
        console.log('📌 Ambos disponíveis, mantendo tipo:', tipo);
        return tipo;
    }, [showProduto, showServico, tipo]);

    const mostrarProduto = showProduto === true;
    const mostrarServico = showServico === true;

    const temAlgumVisivel = mostrarProduto || mostrarServico;
    const mostrarProdutoFinal = temAlgumVisivel ? mostrarProduto : false;
    const mostrarServicoFinal = temAlgumVisivel ? mostrarServico : true;

    const isSingleButton = !(mostrarProdutoFinal && mostrarServicoFinal);

    console.log('🎯 Renderizando toggle:', {
        isSingleButton,
        mostrarProdutoFinal,
        mostrarServicoFinal,
        tipoEfetivo,
        showProduto,
        showServico
    });

    return (
        <div className="toggle-wrapper">
            <div className={`toggle-produto-servico ${isSingleButton ? 'single-button' : ''}`}>
                {mostrarProdutoFinal && (
                    <button
                        type="button"
                        className={`toggle-option ${tipoEfetivo === 'produto' ? 'active' : ''}`}
                        onClick={() => !disabled && onChange('produto')}
                        disabled={disabled}
                    >
                        <Icon name="inventory_2" size={14} /> Produtos
                    </button>
                )}
                {mostrarServicoFinal && (
                    <button
                        type="button"
                        className={`toggle-option ${tipoEfetivo === 'servico' ? 'active' : ''}`}
                        onClick={() => !disabled && onChange('servico')}
                        disabled={disabled}
                    >
                        <Icon name="build" size={14} /> Serviços
                    </button>
                )}
                {mostrarProdutoFinal && mostrarServicoFinal && (
                    <div
                        className="toggle-slider"
                        style={{
                            transform: tipoEfetivo === 'produto'
                                ? 'translateX(0)'
                                : 'translateX(calc(100% + 3px))'
                        }}
                    />
                )}
            </div>
            <div className="toggle-actions">
                {tipoEfetivo === 'produto' && mostrarProdutoFinal ? (
                    <button
                        type="button"
                        className="btn-add-toggle"
                        onClick={onAddProduto}
                        title="Adicionar novo produto"
                    >
                        <Icon name="add" size={16} />
                    </button>
                ) : mostrarServicoFinal ? (
                    <button
                        type="button"
                        className="btn-add-toggle"
                        onClick={onAddServico}
                        title="Adicionar novo serviço"
                    >
                        <Icon name="add" size={16} />
                    </button>
                ) : null}
            </div>
        </div>
    );
}

// =============================================
// COMPONENTE PRINCIPAL
// =============================================
export default function Cotacoes() {
    // =============================================
    // ESTADOS PRINCIPAIS
    // =============================================
    const [cotacoes, setCotacoes] = useState([]);
    const [filteredCotacoes, setFilteredCotacoes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [tableLoading, setTableLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [filtroStatus, setFiltroStatus] = useState('todos');

    const [expandedRows, setExpandedRows] = useState({});
    const [itensPage, setItensPage] = useState({});

    // =============================================
    // ESTADO PARA TOAST
    // =============================================
    const [toast, setToast] = useState({ message: '', type: '' });

    const showToast = (text, type = 'success') => {
        setToast({ message: text, type });
    };

    const closeToast = () => {
        setToast({ message: '', type: '' });
    };

    // =============================================
    // 🔥 REF PARA GUARDAR O IVA PERMANENTEMENTE
    // =============================================
    const ivaRef = useRef(null);

    // =============================================
    // CONFIGURAÇÕES DA EMPRESA - COM SUBMODULOS
    // =============================================
    const [empresaConfig, setEmpresaConfig] = useState({
        id: null,
        nome: '',
        nomeCurto: '',
        slug: '',
        imagemPerfil: null,
        nuit: '',
        alvara: '',
        email: '',
        emailAlternativo: '',
        telefone: '',
        telefoneAlternativo: '',
        whatsapp: '',
        website: '',
        morada: {
            rua: '',
            numero: '',
            complemento: '',
            bairro: '',
            quarteirao: '',
            casa: '',
            cidade: '',
            provincia: '',
            distrito: '',
            postoAdministrativo: '',
            pais: '',
            codigoPostal: '',
            coordenadas: {},
            enderecoCompleto: ''
        },
        dadosBancarios: [],
        bancoPrincipal: null,
        regimeIVA: 'Normal',
        retencoaFonte: false,
        moeda: {
            simbolo: 'MT',
            codigo: 'MZN',
            casasDecimais: 2,
            separadorDecimal: ',',
            separadorMilhar: '.',
            posicaoSimbolo: 'depois',
            nome: 'Metical'
        },
        formatacao: {
            data: { formato: 'DD/MM/YYYY', separador: '/', incluirHora: false },
            numero: { separadorDecimal: ',', separadorMilhar: '.', casasDecimais: 2 }
        },
        configuracao: {
            plano: {},
            setorActividade: '',
            ramoAtividade: ''
        },
        status: 'Ativa',
        arquivado: false,
        cafe: '',
        criadoEm: null,
        actualizadoEm: null,
        dataArquivo: null,
        motivoArquivo: '',
        utilizadores: [],
        actualizadoPor: null,
        contactosEmergencia: [],
        sedes: [],
        actividadesEconomicas: [],
        estatisticas: {},
        timezone: 'Africa/Maputo',
        idioma: 'pt-PT',
        formatoHora: '24h',
        modulos: {
            gestaoStock: false,
            gestaoProdutos: false,
            gestaoServicos: false,
            gestaoEstoque: false,
            gestaoArmazens: false,
            gestaoInventario: false,
            gestaoCategorias: false,
            gestaoFrotas: false,
            gestaoProjetos: false,
            gestaoRh: false,
            gestaoAtivos: false
        }
    });

    const [percentuaisIva, setPercentuaisIva] = useState(PERCENTUAIS_IVA_PADRAO);

    // =============================================
    // MODAIS
    // =============================================
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [showDetailsModal, setShowDetailsModal] = useState(false);
    const [showApproveModal, setShowApproveModal] = useState(false);
    const [showCancelModal, setShowCancelModal] = useState(false);
    const [showClienteModal, setShowClienteModal] = useState(false);
    const [showProdutoModal, setShowProdutoModal] = useState(false);
    const [showServicoModal, setShowServicoModal] = useState(false);

    const [cotacaoToDelete, setCotacaoToDelete] = useState(null);
    const [cotacaoToApprove, setCotacaoToApprove] = useState(null);
    const [cotacaoToCancel, setCotacaoToCancel] = useState(null);
    const [cotacaoDetalhes, setCotacaoDetalhes] = useState(null);
    const [motivoCancelamento, setMotivoCancelamento] = useState('');
    const [message, setMessage] = useState({ text: '', type: '' });

    const [ivaIncluso, setIvaIncluso] = useState(false);
    const [empresaId, setEmpresaId] = useState(null);

    // 🔥 Inicializar tipoItem baseado no módulo Stock
    const [tipoItem, setTipoItem] = useState(() => {
        const userStr = localStorage.getItem('usuario');
        let stockActive = false;
        try {
            if (userStr) {
                const user = JSON.parse(userStr);
                const empresaModulos = user.empresa?.modulos || {};
                stockActive = empresaModulos.gestaoStock === true ||
                    empresaModulos.stock === true ||
                    empresaModulos.estoque === true;
            }
        } catch (e) {
            console.warn('⚠️ Erro ao verificar módulo Stock:', e);
        }
        return stockActive ? 'produto' : 'servico';
    });

    // =============================================
    // ESTATÍSTICAS
    // =============================================
    const [stats, setStats] = useState({
        total: 0,
        rascunho: 0,
        pendente: 0,
        aprovado: 0,
        expirado: 0,
        cancelado: 0,
        totalValor: 0,
        totalValorFormatado: '0 MT'
    });

    // =============================================
    // PAGINAÇÃO
    // =============================================
    const [pagination, setPagination] = useState({
        page: 1,
        limit: 11,
        total: 0,
        pages: 0
    });

    // =============================================
    // DADOS AUXILIARES
    // =============================================
    const [clientes, setClientes] = useState([]);
    const [clientesFiltrados, setClientesFiltrados] = useState([]);
    const [produtos, setProdutos] = useState([]);
    const [servicos, setServicos] = useState([]);

    // =============================================
    // FORM DATA - CRIAÇÃO
    // =============================================
    const [formData, setFormData] = useState({
        clienteId: '',
        produtos: [],
        ivaPercent: null,
        descontoGlobal: 0,
        dataEmissao: new Date().toISOString().split('T')[0],
        validade: '',
        observacoes: '',
        status: 'Pendente'
    });

    const [editFormData, setEditFormData] = useState(null);
    const [novoItem, setNovoItem] = useState({
        id: '',
        quantidade: 1,
        precoUnitario: 0,
        descontoPercentual: 0,
        tipo: 'produto'
    });

    // =============================================
    // 🔥 ESTADO PARA EDIÇÃO DE ITEM
    // =============================================
    const [itemEditando, setItemEditando] = useState({
        index: null,
        isEdit: false,
        dados: null
    });

    // =============================================
    // ESTADOS PARA PESQUISA
    // =============================================
    const [searchCliente, setSearchCliente] = useState('');
    const [searchClienteEdit, setSearchClienteEdit] = useState('');
    const [searchItem, setSearchItem] = useState('');
    const [searchItemEdit, setSearchItemEdit] = useState('');

    //========================================================
    // 🔥 FUNÇÃO PARA VERIFICAR SE O MÓDULO STOCK ESTÁ ATIVO
    //========================================================

    const hasStockModule = useCallback(() => {
        return empresaConfig?.modulos?.gestaoStock === true;
    }, [empresaConfig]);

    // 🔥 FUNÇÃO PARA VERIFICAR SE DEVE MOSTRAR PRODUTOS - CORRIGIDA
    const shouldShowProdutos = useCallback(() => {
        if (!hasStockModule()) {
            console.log('❌ Stock não ativo, produtos não disponíveis');
            return false;
        }

        const modulos = empresaConfig?.modulos || {};

        console.log('🔍 Verificando submodulos de produtos:', {
            gestaoProdutos: modulos.gestaoProdutos,
            gestaoEstoque: modulos.gestaoEstoque,
            gestaoArmazens: modulos.gestaoArmazens,
            gestaoInventario: modulos.gestaoInventario
        });

        const hasProdutoSubmodulo =
            modulos.gestaoProdutos === true ||
            modulos.gestaoEstoque === true ||
            modulos.gestaoArmazens === true ||
            modulos.gestaoInventario === true;

        const hasServicoSubmodulo =
            modulos.gestaoServicos === true ||
            modulos.gestaoCategorias === true;

        if (hasServicoSubmodulo && !hasProdutoSubmodulo) {
            console.log('❌ Apenas submodulos de serviço ativos, produtos ocultos');
            return false;
        }

        if (hasProdutoSubmodulo) {
            console.log('✅ Submodulos de produto ativos, produtos visíveis');
            return true;
        }

        const hasProdutosCadastrados = produtos.length > 0;
        const result = hasProdutosCadastrados;

        console.log(`📦 Produtos: submodulo=${hasProdutoSubmodulo}, cadastrados=${hasProdutosCadastrados}, resultado=${result}`);

        return result;
    }, [empresaConfig, produtos, hasStockModule]);

    // 🔥 FUNÇÃO PARA VERIFICAR SE DEVE MOSTRAR SERVIÇOS - CORRIGIDA
    const shouldShowServicos = useCallback(() => {
        if (!hasStockModule()) {
            console.log('❌ Stock não ativo, serviços não disponíveis');
            return false;
        }

        const modulos = empresaConfig?.modulos || {};

        console.log('🔍 Verificando submodulos de serviços:', {
            gestaoServicos: modulos.gestaoServicos,
            gestaoCategorias: modulos.gestaoCategorias
        });

        const hasServicoSubmodulo =
            modulos.gestaoServicos === true ||
            modulos.gestaoCategorias === true;

        const hasProdutoSubmodulo =
            modulos.gestaoProdutos === true ||
            modulos.gestaoEstoque === true ||
            modulos.gestaoArmazens === true ||
            modulos.gestaoInventario === true;

        if (hasProdutoSubmodulo && !hasServicoSubmodulo) {
            console.log('❌ Apenas submodulos de produto ativos, serviços ocultos');
            return false;
        }

        if (hasServicoSubmodulo) {
            console.log('✅ Submodulos de serviço ativos, serviços visíveis');
            return true;
        }

        const fallbackServicos = hasStockModule();
        const hasServicosCadastrados = servicos.length > 0;

        const result = hasServicosCadastrados || fallbackServicos;

        console.log(`📦 Serviços: submodulo=${hasServicoSubmodulo}, cadastrados=${hasServicosCadastrados}, fallback=${fallbackServicos}, resultado=${result}`);

        return result;
    }, [empresaConfig, servicos, hasStockModule]);

    // 🔥 CALCULAR VISIBILIDADE DOS BOTÕES
    const showProduto = shouldShowProdutos();
    const showServico = shouldShowServicos();

    // 🔥 Determinar o tipo inicial baseado no que está disponível
    const tipoInicial = useMemo(() => {
        if (showProduto && !showServico) return 'produto';
        if (!showProduto && showServico) return 'servico';
        return hasStockModule() ? 'produto' : 'servico';
    }, [showProduto, showServico, hasStockModule]);

    // 🔥 DEBUG - MONITORAR MUDANÇAS NOS SUBMODULOS
    useEffect(() => {
        console.log('🔍 Monitorando submodulos:');
        console.log('📦 empresaConfig.modulos:', empresaConfig.modulos);
        console.log('📦 showProduto:', showProduto);
        console.log('📦 showServico:', showServico);
        console.log('📦 tipoItem:', tipoItem);
        console.log('📦 stockModuleActive:', hasStockModule());
    }, [empresaConfig.modulos, showProduto, showServico, tipoItem, hasStockModule]);

    // =============================================
    // API INSTANCE
    // =============================================
    const api = axios.create({
        baseURL: API_BASE_URL,
        headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`
        }
    });

    // =============================================
    // FUNÇÃO PARA FORMATAR MOEDA
    // =============================================
    const formatarMoeda = useMemo(() => {
        return (valor) => {
            const cfg = empresaConfig.moeda;
            const sepDec = cfg.separadorDecimal || ',';
            const sepMil = cfg.separadorMilhar || '.';
            const casasDec = cfg.casasDecimais || 2;
            const simbolo = cfg.simbolo || 'MT';

            if (valor === undefined || valor === null || isNaN(valor)) {
                const zeros = '0'.repeat(casasDec);
                return `0${sepDec}${zeros} ${simbolo}`;
            }

            let valorFormatado = Number(valor).toFixed(casasDec);
            valorFormatado = valorFormatado.replace('.', sepDec);

            const partes = valorFormatado.split(sepDec);
            partes[0] = partes[0].replace(/\B(?=(\d{3})+(?!\d))/g, sepMil);
            valorFormatado = partes.join(sepDec);

            return `${valorFormatado} ${simbolo}`;
        };
    }, [empresaConfig.moeda]);

    // =============================================
    // FUNÇÃO PARA FORMATAR DATA
    // =============================================
    const formatarData = useCallback((data) => {
        if (!data) return '-';
        const d = new Date(data);
        if (isNaN(d.getTime())) return '-';

        const cfg = empresaConfig.formatacao?.data || { formato: 'DD/MM/YYYY', separador: '/' };
        let formato = cfg.formato || 'DD/MM/YYYY';
        const dia = String(d.getDate()).padStart(2, '0');
        const mes = String(d.getMonth() + 1).padStart(2, '0');
        const ano = d.getFullYear();

        formato = formato.replace('DD', dia);
        formato = formato.replace('MM', mes);
        formato = formato.replace('YYYY', ano);
        formato = formato.replace('YY', String(ano).slice(-2));

        if (cfg.incluirHora) {
            const horas = String(d.getHours()).padStart(2, '0');
            const minutos = String(d.getMinutes()).padStart(2, '0');
            return `${formato} ${horas}:${minutos}`;
        }

        return formato;
    }, [empresaConfig.formatacao]);

    // =============================================
    // FUNÇÕES AUXILIARES
    // =============================================
    const getClienteNome = (cotacao) => {
        if (!cotacao) return 'N/A';
        if (cotacao.clienteInfo?.nome) return cotacao.clienteInfo.nome;
        if (cotacao.clienteId?.nome) return cotacao.clienteId.nome;
        if (typeof cotacao.clienteId === 'string') return 'Carregando...';
        return 'N/A';
    };

    const getClienteNif = (cotacao) => {
        if (!cotacao) return '';
        if (cotacao.clienteInfo?.nif) return cotacao.clienteInfo.nif;
        if (cotacao.clienteId?.nif) return cotacao.clienteId.nif;
        if (cotacao.clienteId?.nuit) return cotacao.clienteId.nuit;
        return '';
    };

    const getItemNome = (item) => {
        if (!item) return 'N/A';
        if (item.produtoInfo?.nome) return item.produtoInfo.nome;
        if (item.produtoId?.nome) return item.produtoId.nome;
        if (item.servicoId?.nome) return item.servicoId.nome;
        if (item.nomeItem) return item.nomeItem;
        return 'N/A';
    };

    const getItemCodigo = (item) => {
        if (!item) return '';
        if (item.produtoInfo?.codigo) return item.produtoInfo.codigo;
        if (item.produtoId?.codigo) return item.produtoId.codigo;
        if (item.servicoId?.codigo) return item.servicoId.codigo;
        return '';
    };

    const getDescontoDisplay = (cotacao) => {
        if (cotacao.descontoGlobal !== undefined && cotacao.descontoGlobal > 0) {
            return `${cotacao.descontoGlobal}%`;
        }
        return '-';
    };

    const getTotalDisplay = (cotacao) => {
        if (cotacao.totalFormatado) return cotacao.totalFormatado;
        return formatarMoeda(cotacao.total || 0);
    };

    const getValidadeDisplay = (cotacao) => {
        if (cotacao.validadeFormatado) return cotacao.validadeFormatado;
        return formatarData(cotacao.validade);
    };

    const getStatusFormatado = (cotacao) => {
        return cotacao.statusFormatado || getStatusInfo(cotacao.status).label;
    };

    const getStatusClass = (cotacao) => {
        return cotacao.statusClass || (cotacao.status || 'Pendente').toLowerCase();
    };

    const getStatusIcon = (cotacao) => {
        return cotacao.statusIcon || getStatusInfo(cotacao.status).icon;
    };

    const getIvaDescricao = (cotacao) => {
        if (cotacao.ivaIncluso) return 'Incluso';
        return cotacao.ivaDescricao || `${cotacao.ivaPercent || 0}%`;
    };

    const getTotais = (itens, ivaPercent, descontoGlobal = 0, ivaInclusoParam = false) => {
        if (!itens || itens.length === 0) {
            return { subtotal: 0, descontoValor: 0, subtotalComDesconto: 0, iva: 0, total: 0, ivaIncluso: ivaInclusoParam };
        }

        let subtotal = itens.reduce((acc, p) => acc + (p.quantidade * p.precoUnitario * (1 - (p.descontoPercentual || 0) / 100)), 0);

        let descontoValor = 0;
        let subtotalComDesconto = subtotal;
        if (descontoGlobal > 0 && subtotal > 0) {
            descontoValor = subtotal * (descontoGlobal / 100);
            subtotalComDesconto = subtotal - descontoValor;
        }

        let iva = 0;
        let total = subtotalComDesconto;

        if (!ivaInclusoParam) {
            iva = subtotalComDesconto * ((ivaPercent || 0) / 100);
            total = subtotalComDesconto + iva;
        }

        return { subtotal, descontoValor, subtotalComDesconto, iva, total, ivaIncluso: ivaInclusoParam };
    };

    // =============================================
    // FUNÇÕES DE PESQUISA DE CLIENTE
    // =============================================
    const selecionarCliente = (cliente) => {
        if (!cliente) return;
        setFormData(prev => ({ ...prev, clienteId: cliente._id }));
        setSearchCliente(cliente.nome);
    };

    const selecionarClienteEdit = (cliente) => {
        if (!cliente) return;
        setEditFormData(prev => ({ ...prev, clienteId: cliente._id }));
        setSearchClienteEdit(cliente.nome);
    };

    const limparSelecaoCliente = () => {
        setFormData(prev => ({ ...prev, clienteId: '' }));
        setSearchCliente('');
    };

    const limparSelecaoClienteEdit = () => {
        setEditFormData(prev => ({ ...prev, clienteId: '' }));
        setSearchClienteEdit('');
    };

    // =============================================
    // FUNÇÕES DE PESQUISA DE ITEM
    // =============================================
    const getItemsPorTipo = useCallback(() => {
        if (tipoItem === 'produto') {
            return produtos || [];
        } else {
            return servicos || [];
        }
    }, [tipoItem, produtos, servicos]);

    const getItemLabel = useCallback((item) => {
        return item?.nome || 'Item sem nome';
    }, []);

    const getItemSub = useCallback((item) => {
        return item?.codigo ? `Cód: ${item.codigo}` : '';
    }, []);

    const getItemTag = useCallback((item) => {
        if (tipoItem === 'produto') {
            return item?.categoria?.nome || 'Produto';
        }
        return item?.categoria?.nome || 'Serviço';
    }, [tipoItem]);

    const getItemPrice = useCallback((item) => {
        return formatarMoeda(item?.precoVenda || 0);
    }, [formatarMoeda]);

    const selecionarItem = (item) => {
        if (!item) return;
        setNovoItem(prev => ({
            ...prev,
            id: item._id,
            precoUnitario: item.precoVenda || 0,
            descontoPercentual: 0,
            tipo: tipoItem
        }));
        setSearchItem(item.nome);
    };

    const selecionarItemEdit = (item) => {
        if (!item) return;
        setNovoItem(prev => ({
            ...prev,
            id: item._id,
            precoUnitario: item.precoVenda || 0,
            descontoPercentual: 0,
            tipo: tipoItem
        }));
        setSearchItemEdit(item.nome);
    };

    const limparSelecaoItem = () => {
        setNovoItem({
            id: '',
            quantidade: 1,
            precoUnitario: 0,
            descontoPercentual: 0,
            tipo: tipoItem
        });
        setSearchItem('');
    };

    const limparSelecaoItemEdit = () => {
        setNovoItem({
            id: '',
            quantidade: 1,
            precoUnitario: 0,
            descontoPercentual: 0,
            tipo: tipoItem
        });
        setSearchItemEdit('');
    };

    // =============================================
    // HANDLERS PARA ADICIONAR PRODUTO/SERVIÇO
    // =============================================
    const handleAddProduto = () => {
        setShowProdutoModal(true);
    };

    const handleAddServico = () => {
        setShowServicoModal(true);
    };

    const handleProdutoCriado = (produto) => {
        fetchProdutos();
        if (produto) {
            setTipoItem('produto');
            setNovoItem(prev => ({
                ...prev,
                id: produto._id,
                precoUnitario: produto.precoVenda || 0,
                descontoPercentual: 0,
                tipo: 'produto'
            }));
            setSearchItem(produto.nome);
        }
        showToast(`Produto "${produto.nome}" adicionado com sucesso!`, 'success');
    };

    const handleServicoCriado = (servico) => {
        fetchServicos();
        if (servico) {
            setTipoItem('servico');
            setNovoItem(prev => ({
                ...prev,
                id: servico._id,
                precoUnitario: servico.precoVenda || 0,
                descontoPercentual: 0,
                tipo: 'servico'
            }));
            setSearchItem(servico.nome);
        }
        showToast(`Serviço "${servico.nome}" adicionado com sucesso!`, 'success');
    };

    // =============================================
    // 🔥 FUNÇÃO PARA INICIAR EDIÇÃO DE ITEM
    // =============================================
    const iniciarEdicaoItem = (index, isEdit = false) => {
        const produtos = isEdit ? editFormData.produtos : formData.produtos;
        const item = produtos[index];

        if (!item) return;

        const isServico = !!item.servicoId;
        const itemId = isServico ? item.servicoId : item.produtoId;
        const itemsList = isServico ? servicos : produtos;
        const itemData = itemsList.find(p => p._id === itemId);
        const nomeItem = itemData ? itemData.nome : item.nomeItem || 'Item';

        const tipoItemAtual = isServico ? 'servico' : 'produto';

        setItemEditando({
            index: index,
            isEdit: isEdit,
            dados: {
                ...item,
                nomeItem: nomeItem,
                tipoItem: tipoItemAtual,
                itemId: itemId,
                itemData: itemData
            }
        });

        setNovoItem({
            id: itemId || '',
            quantidade: item.quantidade || 1,
            precoUnitario: item.precoUnitario || 0,
            descontoPercentual: item.descontoPercentual || 0,
            tipo: tipoItemAtual
        });

        setTipoItem(tipoItemAtual);

        if (isEdit) {
            setSearchItemEdit(nomeItem);
        } else {
            setSearchItem(nomeItem);
        }
    };

    // =============================================
    // 🔥 FUNÇÃO PARA SALVAR EDIÇÃO DE ITEM
    // =============================================
    const salvarEdicaoItem = () => {
        const { index, isEdit } = itemEditando;

        if (index === null) return;

        if (!novoItem.id) {
            showToast(`Selecione um ${tipoItem === 'produto' ? 'produto' : 'serviço'}`, 'danger');
            return;
        }

        if (novoItem.quantidade <= 0) {
            showToast('Quantidade deve ser maior que zero', 'danger');
            return;
        }

        if (novoItem.precoUnitario <= 0) {
            showToast('Preço unitário deve ser maior que zero', 'danger');
            return;
        }

        let nomeItem = '';
        let codigoItem = '';
        let tipoLabel = tipoItem === 'produto' ? 'Produto' : 'Servico';
        const itemsList = tipoItem === 'produto' ? produtos : servicos;
        const itemData = itemsList.find(p => p._id === novoItem.id);
        if (itemData) {
            nomeItem = itemData.nome || '';
            codigoItem = itemData.codigo || '';
        }

        const descontoPercentual = parseFloat(novoItem.descontoPercentual) || 0;

        const itemAtualizado = {
            quantidade: parseInt(novoItem.quantidade),
            precoUnitario: parseFloat(novoItem.precoUnitario),
            descontoPercentual: descontoPercentual,
            nomeItem: nomeItem,
            codigoItem: codigoItem,
            tipoItem: tipoLabel
        };

        if (tipoItem === 'produto') {
            itemAtualizado.produtoId = novoItem.id;
        } else {
            itemAtualizado.servicoId = novoItem.id;
        }

        if (isEdit && editFormData) {
            const novosProdutos = [...editFormData.produtos];
            novosProdutos[index] = itemAtualizado;
            setEditFormData(prev => ({
                ...prev,
                produtos: novosProdutos
            }));
        } else {
            const novosProdutos = [...formData.produtos];
            novosProdutos[index] = itemAtualizado;
            setFormData(prev => ({
                ...prev,
                produtos: novosProdutos
            }));
        }

        cancelarEdicaoItem();

        showToast('Item atualizado com sucesso!', 'success');
    };

    // =============================================
    // 🔥 FUNÇÃO PARA CANCELAR EDIÇÃO DE ITEM
    // =============================================
    const cancelarEdicaoItem = () => {
        setItemEditando({
            index: null,
            isEdit: false,
            dados: null
        });
        setNovoItem({
            id: '',
            quantidade: 1,
            precoUnitario: 0,
            descontoPercentual: 0,
            tipo: tipoItem
        });
        setSearchItem('');
        setSearchItemEdit('');
    };

    // =============================================
    // ADICIONAR ITEM (MODIFICADO PARA USAR A EDIÇÃO)
    // =============================================
    const adicionarItem = (isEdit = false) => {
        if (itemEditando.index !== null) {
            salvarEdicaoItem();
            return;
        }

        if (!novoItem.id) {
            showToast(`Selecione um ${tipoItem === 'produto' ? 'produto' : 'serviço'}`, 'danger');
            return;
        }

        if (novoItem.quantidade <= 0) {
            showToast('Quantidade deve ser maior que zero', 'danger');
            return;
        }

        if (novoItem.precoUnitario <= 0) {
            showToast('Preço unitário deve ser maior que zero', 'danger');
            return;
        }

        const produtosAtuais = isEdit ? editFormData.produtos : formData.produtos;

        const itemExistente = produtosAtuais.find(p => {
            if (tipoItem === 'produto') {
                return p.produtoId === novoItem.id;
            } else {
                return p.servicoId === novoItem.id;
            }
        });

        if (itemExistente) {
            showToast(`${tipoItem === 'produto' ? 'Produto' : 'Serviço'} já adicionado`, 'warning');
            return;
        }

        let nomeItem = '';
        let codigoItem = '';
        let tipoLabel = tipoItem === 'produto' ? 'Produto' : 'Servico';
        const itemsList = tipoItem === 'produto' ? produtos : servicos;
        const itemData = itemsList.find(p => p._id === novoItem.id);
        if (itemData) {
            nomeItem = itemData.nome || '';
            codigoItem = itemData.codigo || '';
        }

        const descontoPercentual = parseFloat(novoItem.descontoPercentual) || 0;

        const item = {
            quantidade: parseInt(novoItem.quantidade),
            precoUnitario: parseFloat(novoItem.precoUnitario),
            descontoPercentual: descontoPercentual,
            nomeItem: nomeItem,
            codigoItem: codigoItem,
            tipoItem: tipoLabel
        };

        if (tipoItem === 'produto') {
            item.produtoId = novoItem.id;
        } else {
            item.servicoId = novoItem.id;
        }

        if (isEdit) {
            setEditFormData(prev => ({
                ...prev,
                produtos: [...prev.produtos, item]
            }));
        } else {
            setFormData(prev => ({
                ...prev,
                produtos: [...prev.produtos, item]
            }));
        }

        setNovoItem({
            id: '',
            quantidade: 1,
            precoUnitario: 0,
            descontoPercentual: 0,
            tipo: tipoItem
        });
        setSearchItem('');
        setSearchItemEdit(isEdit ? '' : '');
    };

    const removerItem = (index, isEdit = false) => {
        if (isEdit && editFormData) {
            setEditFormData(prev => ({
                ...prev,
                produtos: prev.produtos.filter((_, i) => i !== index)
            }));
        } else {
            setFormData(prev => ({
                ...prev,
                produtos: prev.produtos.filter((_, i) => i !== index)
            }));
        }
    };

    // =============================================
    // BUSCAR DADOS
    // =============================================
    const fetchClientes = async () => {
        try {
            const response = await api.get('/clientes?limit=100');
            if (response.data && response.data.data) {
                setClientes(response.data.data);
                setClientesFiltrados(response.data.data);
            }
        } catch (error) {
            console.error('❌ Erro ao carregar clientes:', error);
        }
    };

    const fetchProdutos = async () => {
        try {
            console.log('📦 Buscando produtos...');
            const response = await api.get('/produtos?limit=100');
            if (response.data && response.data.data) {
                console.log(`📦 ${response.data.data.length} produtos carregados`);
                setProdutos(response.data.data);
            }
        } catch (error) {
            console.error('❌ Erro ao carregar produtos:', error);
        }
    };

    const fetchServicos = async () => {
        try {
            console.log('🔧 Iniciando busca de serviços...');
            const response = await api.get('/servicos?limit=1000');

            if (response.data && response.data.success !== false) {
                let servicosData = response.data.data;
                if (!servicosData) {
                    servicosData = response.data;
                }
                if (servicosData && !Array.isArray(servicosData) && servicosData.data) {
                    servicosData = servicosData.data;
                }
                if (Array.isArray(servicosData)) {
                    console.log(`✅ ${servicosData.length} serviços carregados com sucesso!`);
                    setServicos(servicosData);
                    return servicosData;
                } else {
                    console.warn('⚠️ Dados recebidos não são um array:', servicosData);
                    setServicos([]);
                    return [];
                }
            } else {
                console.warn('⚠️ Resposta da API indica erro:', response.data);
                setServicos([]);
                return [];
            }
        } catch (error) {
            console.error('❌ Erro ao carregar serviços:', error);
            try {
                console.log('🔄 Tentando rota alternativa sem /api...');
                const response2 = await api.get('/servicos?limit=1000');
                if (response2.data && response2.data.success !== false) {
                    let servicosData = response2.data.data || response2.data;
                    if (Array.isArray(servicosData)) {
                        console.log(`✅ ${servicosData.length} serviços carregados (rota alternativa)!`);
                        setServicos(servicosData);
                        return servicosData;
                    }
                }
            } catch (error2) {
                console.error('❌ Rota alternativa também falhou:', error2);
            }
            setServicos([]);
            return [];
        }
    };

    // =============================================
    // fetchCotacoes - CORRIGIDO COM PAGINAÇÃO
    // =============================================
    const fetchCotacoes = async (showLoading = true, pageOverride = null) => {
        if (showLoading) setTableLoading(true);
        setLoading(true);

        // Usar a página fornecida ou a do estado
        const currentPage = pageOverride !== null ? pageOverride : pagination.page;

        try {
            const params = new URLSearchParams();
            if (searchTerm) params.append('search', searchTerm);
            if (filtroStatus && filtroStatus !== 'todos') params.append('status', filtroStatus);
            params.append('page', currentPage);
            params.append('limit', pagination.limit);

            console.log(`📄 Buscando página ${currentPage}...`);

            const response = await api.get(`/cotacoes?${params.toString()}`);

            if (response.data) {
                if (response.data.empresa) {
                    const empresa = response.data.empresa;

                    const morada = empresa.morada || {};
                    const enderecoCompleto = [
                        morada.rua,
                        morada.numero,
                        morada.bairro,
                        morada.cidade,
                        morada.provincia,
                        morada.pais
                    ].filter(Boolean).join(', ');

                    const dadosBancarios = empresa.dadosBancarios || [];
                    const bancoPrincipal = dadosBancarios.find(b => b.principal === true) || dadosBancarios[0] || null;

                    const ivaPercent = empresa.percentagemIVA;

                    if (ivaPercent === undefined || ivaPercent === null) {
                        console.error('❌ ERRO: IVA da empresa não está definido!');
                        showToast('Erro: IVA da empresa não configurado. Contacte o administrador.', 'danger');
                        setLoading(false);
                        return;
                    }

                    console.log(`📊 IVA da empresa: ${ivaPercent}%`);
                    ivaRef.current = ivaPercent;

                    const modulos = empresa.modulos || empresa.modulosAtivos || {};

                    console.log('📦 Módulos recebidos da API:', modulos);

                    setEmpresaConfig(prev => ({
                        ...prev,
                        id: empresa.id || empresa._id,
                        nome: empresa.nome || '',
                        nomeCurto: empresa.nomeCurto || '',
                        slug: empresa.slug || '',
                        imagemPerfil: empresa.imagemPerfil || null,
                        nuit: empresa.nuit || '',
                        alvara: empresa.alvara || '',
                        email: empresa.email || '',
                        emailAlternativo: empresa.emailAlternativo || '',
                        telefone: empresa.telefone || '',
                        telefoneAlternativo: empresa.telefoneAlternativo || '',
                        whatsapp: empresa.whatsapp || '',
                        website: empresa.website || '',
                        morada: {
                            rua: morada.rua || '',
                            numero: morada.numero || '',
                            complemento: morada.complemento || '',
                            bairro: morada.bairro || '',
                            quarteirao: morada.quarteirao || '',
                            casa: morada.casa || '',
                            cidade: morada.cidade || '',
                            provincia: morada.provincia || '',
                            distrito: morada.distrito || '',
                            postoAdministrativo: morada.postoAdministrativo || '',
                            pais: morada.pais || 'Moçambique',
                            codigoPostal: morada.codigoPostal || '',
                            coordenadas: morada.coordenadas || {},
                            enderecoCompleto: enderecoCompleto
                        },
                        dadosBancarios: dadosBancarios,
                        bancoPrincipal: bancoPrincipal,
                        regimeIVA: empresa.regimeIVA || 'Normal',
                        percentagemIVA: ivaPercent,
                        retencoaFonte: empresa.retencoaFonte || false,
                        moeda: empresa.moeda || prev.moeda,
                        iva: {
                            ...prev.iva,
                            ...empresa.iva,
                            percentualPadrao: empresa.iva?.percentualPadrao ?? ivaPercent,
                            regimeAtivo: empresa.regimeIVA || 'Normal',
                            percentuaisPermitidos: empresa.iva?.percentuaisPermitidos || [0, 5, 16]
                        },
                        formatacao: empresa.formatacao || prev.formatacao,
                        configuracao: {
                            plano: empresa.configuracoes?.plano || {},
                            setorActividade: empresa.configuracoes?.setorActividade || '',
                            ramoAtividade: empresa.configuracoes?.ramoAtividade || ''
                        },
                        status: empresa.status || 'Ativa',
                        arquivado: empresa.arquivado || false,
                        cafe: empresa.cafe || '',
                        criadoEm: empresa.criadoEm || null,
                        actualizadoEm: empresa.actualizadoEm || null,
                        dataArquivo: empresa.dataArquivo || null,
                        motivoArquivo: empresa.motivoArquivo || '',
                        utilizadores: empresa.utilizadores || [],
                        actualizadoPor: empresa.actualizadoPor || null,
                        contactosEmergencia: empresa.contactosEmergencia || [],
                        sedes: empresa.sedes || [],
                        actividadesEconomicas: empresa.actividadesEconomicas || [],
                        estatisticas: empresa.estatisticas || {},
                        modulos: {
                            gestaoStock: modulos.gestaoStock === true || modulos.stock === true || modulos.estoque === true || false,
                            gestaoProdutos: modulos.gestaoProdutos === true || modulos.produtos === true || false,
                            gestaoServicos: modulos.gestaoServicos === true || modulos.servicos === true || false,
                            gestaoEstoque: modulos.gestaoEstoque === true || modulos.estoque === true || false,
                            gestaoArmazens: modulos.gestaoArmazens === true || modulos.armazens === true || false,
                            gestaoInventario: modulos.gestaoInventario === true || modulos.inventario === true || false,
                            gestaoCategorias: modulos.gestaoCategorias === true || modulos.categorias === true || false,
                            gestaoFrotas: modulos.gestaoFrotas === true || modulos.frotas === true || false,
                            gestaoProjetos: modulos.gestaoProjetos === true || modulos.projetos === true || false,
                            gestaoRh: modulos.gestaoRh === true || modulos.rh === true || false,
                            gestaoAtivos: modulos.gestaoAtivos === true || modulos.ativos === true || false
                        }
                    }));

                    if (empresa.iva?.percentuaisPermitidos) {
                        setPercentuaisIva(empresa.iva.percentuaisPermitidos);
                    }

                    const ivaPadrao = empresa.iva?.percentualPadrao ?? ivaPercent;
                    if (!showCreateModal) {
                        setFormData(prev => ({ ...prev, ivaPercent: ivaPadrao }));
                    }
                }

                if (response.data.data && Array.isArray(response.data.data)) {
                    setCotacoes(response.data.data);
                    setFilteredCotacoes(response.data.data);

                    if (response.data.pagination) {
                        const newPagination = {
                            ...response.data.pagination,
                            page: currentPage
                        };
                        setPagination(newPagination);
                    }

                    if (response.data.resumo) {
                        const resumo = response.data.resumo;
                        const statsData = {
                            total: response.data.pagination?.total || 0,
                            rascunho: 0,
                            pendente: 0,
                            aprovado: 0,
                            expirado: 0,
                            cancelado: 0,
                            totalValor: 0,
                            totalValorFormatado: '0 MT'
                        };

                        resumo.forEach(item => {
                            if (item.status === 'Rascunho') statsData.rascunho = item.count;
                            if (item.status === 'Pendente') statsData.pendente = item.count;
                            if (item.status === 'Aprovado') statsData.aprovado = item.count;
                            if (item.status === 'Expirado') statsData.expirado = item.count;
                            if (item.status === 'Cancelado') statsData.cancelado = item.count;
                            statsData.totalValor += item.total || 0;
                            if (item.totalFormatado) {
                                statsData.totalValorFormatado = item.totalFormatado;
                            }
                        });

                        setStats(statsData);
                    }
                } else {
                    setCotacoes([]);
                    setFilteredCotacoes([]);
                }
            }
        } catch (error) {
            console.error('❌ Erro ao carregar cotações:', error);
            showMessage('Erro ao carregar cotações', 'danger');
            setCotacoes([]);
            setFilteredCotacoes([]);
        } finally {
            setLoading(false);
            setTableLoading(false);
        }
    };

    // =============================================
    // FUNÇÃO PARA VERIFICAR SE OS CAMPOS OBRIGATÓRIOS ESTÃO PREENCHIDOS
    // =============================================
    const camposObrigatoriosPreenchidos = useCallback(() => {
        if (!formData.clienteId) return false;
        if (!formData.validade) return false;
        if (formData.produtos.length === 0) return false;
        for (const item of formData.produtos) {
            if (!item.quantidade || item.quantidade <= 0) return false;
            if (!item.precoUnitario || item.precoUnitario <= 0) return false;
        }
        return true;
    }, [formData.clienteId, formData.validade, formData.produtos]);

    // =============================================
    // FUNÇÃO PARA SALVAR COMO RASCUNHO (AUTO-SAVE)
    // =============================================
    const salvarComoRascunho = useCallback(async () => {
        if (!camposObrigatoriosPreenchidos()) {
            console.log('ℹ️ Campos obrigatórios não preenchidos, não salvando rascunho');
            return;
        }

        const existeRascunho = cotacoes.some(c =>
            c.status === 'Rascunho' &&
            c.clienteId?._id === formData.clienteId &&
            c.produtos?.length === formData.produtos.length
        );

        if (existeRascunho) {
            console.log('ℹ️ Já existe um rascunho com estes dados, não salvando novamente');
            return;
        }

        try {
            setLoading(true);
            const token = localStorage.getItem('token');
            if (!token) {
                console.warn('⚠️ Token não encontrado');
                setLoading(false);
                return;
            }

            const ivaPercentFinal = empresaConfig.percentagemIVA;
            if (ivaPercentFinal === undefined || ivaPercentFinal === null) {
                console.warn('⚠️ IVA não configurado, não salvando rascunho');
                setLoading(false);
                return;
            }

            const dadosEnvio = {
                clienteId: formData.clienteId,
                produtos: formData.produtos.map(p => {
                    const item = {
                        quantidade: p.quantidade,
                        precoUnitario: p.precoUnitario,
                        descontoPercentual: p.descontoPercentual || 0
                    };
                    if (p.produtoId) item.produtoId = p.produtoId;
                    if (p.servicoId) item.servicoId = p.servicoId;
                    return item;
                }),
                ivaPercent: ivaPercentFinal,
                ivaIncluso: ivaIncluso,
                descontoGlobal: parseFloat(formData.descontoGlobal) || 0,
                dataEmissao: formData.dataEmissao || new Date().toISOString().split('T')[0],
                validade: formData.validade,
                observacoes: formData.observacoes || '',
                status: 'Rascunho'
            };

            console.log('📤 Auto-Save: Salvando rascunho...', dadosEnvio);

            const response = await api.post('/cotacoes', dadosEnvio);

            if (response.data) {
                console.log('✅ Auto-Save: Rascunho salvo com sucesso!');
                showToast('💾 Rascunho salvo automaticamente', 'success');
                await fetchCotacoes(false);
            }
        } catch (error) {
            console.error('❌ Erro ao salvar rascunho:', error);
        } finally {
            setLoading(false);
        }
    }, [formData, empresaConfig.percentagemIVA, ivaIncluso, cotacoes, camposObrigatoriosPreenchidos, api, fetchCotacoes]);

    // =============================================
    // FUNÇÃO PARA FECHAR O MODAL COM AUTO-SAVE
    // =============================================
    const fecharModalCriacao = useCallback(async () => {
        const camposPreenchidos = camposObrigatoriosPreenchidos();

        if (camposPreenchidos) {
            await salvarComoRascunho();
        }

        setShowCreateModal(false);
        resetForm();
        cancelarEdicaoItem();
    }, [camposObrigatoriosPreenchidos, salvarComoRascunho]);

    // =============================================
    // CRIAR COTAÇÃO
    // =============================================
    const handleCreate = async (e) => {
        e.preventDefault();
        setLoading(true);

        if (!formData.clienteId) {
            showToast('Selecione um cliente', 'danger');
            setLoading(false);
            return;
        }

        if (formData.produtos.length === 0) {
            showToast('Adicione pelo menos um produto ou serviço', 'danger');
            setLoading(false);
            return;
        }

        if (!formData.validade) {
            showToast('Data de validade é obrigatória', 'danger');
            setLoading(false);
            return;
        }

        try {
            const ivaPercentFinal = empresaConfig.percentagemIVA;

            if (ivaPercentFinal === undefined || ivaPercentFinal === null) {
                showToast('Erro: IVA da empresa não configurado. Contacte o administrador.', 'danger');
                setLoading(false);
                return;
            }

            const dadosEnvio = {
                clienteId: formData.clienteId,
                produtos: formData.produtos.map(p => {
                    const item = {
                        quantidade: p.quantidade,
                        precoUnitario: p.precoUnitario,
                        descontoPercentual: p.descontoPercentual || 0
                    };
                    if (p.produtoId) item.produtoId = p.produtoId;
                    if (p.servicoId) item.servicoId = p.servicoId;
                    return item;
                }),
                ivaPercent: ivaPercentFinal,
                ivaIncluso: ivaIncluso,
                descontoGlobal: parseFloat(formData.descontoGlobal) || 0,
                dataEmissao: formData.dataEmissao || new Date().toISOString().split('T')[0],
                validade: formData.validade,
                observacoes: formData.observacoes || '',
                status: formData.status || 'Pendente'
            };

            console.log('📤 Enviando dados para API:', dadosEnvio);

            const response = await api.post('/cotacoes', dadosEnvio);

            if (response.data) {
                showToast('Cotação criada com sucesso!', 'success');
                setShowCreateModal(false);
                resetForm();
                cancelarEdicaoItem();
                fetchCotacoes(false);
            }
        } catch (error) {
            console.error('❌ Erro ao criar cotação:', error);
            showToast(error.response?.data?.erro || 'Erro ao criar cotação', 'danger');
        } finally {
            setLoading(false);
        }
    };

    // =============================================
    // ATUALIZAR COTAÇÃO
    // =============================================
    const handleUpdate = async (e) => {
        e.preventDefault();
        if (!editFormData) return;
        setLoading(true);

        try {
            const ivaPercentFinal = empresaConfig.percentagemIVA;

            if (ivaPercentFinal === undefined || ivaPercentFinal === null) {
                showToast('Erro: IVA da empresa não configurado. Contacte o administrador.', 'danger');
                setLoading(false);
                return;
            }

            const dadosEnvio = {
                clienteId: editFormData.clienteId,
                produtos: editFormData.produtos.map(p => {
                    const item = {
                        quantidade: p.quantidade,
                        precoUnitario: p.precoUnitario,
                        descontoPercentual: p.descontoPercentual || 0
                    };
                    if (p.produtoId) item.produtoId = p.produtoId;
                    if (p.servicoId) item.servicoId = p.servicoId;
                    return item;
                }),
                ivaPercent: ivaPercentFinal,
                ivaIncluso: ivaIncluso,
                descontoGlobal: parseFloat(editFormData.descontoGlobal) || 0,
                dataEmissao: editFormData.dataEmissao || new Date().toISOString().split('T')[0],
                validade: editFormData.validade,
                observacoes: editFormData.observacoes,
                status: editFormData.status
            };

            console.log('📤 Enviando atualização:', dadosEnvio);

            const response = await api.put(`/cotacoes/${editFormData._id}`, dadosEnvio);

            if (response.data) {
                showToast('Cotação atualizada com sucesso!', 'success');
                setShowEditModal(false);
                setEditFormData(null);
                cancelarEdicaoItem();
                fetchCotacoes(false);
            }
        } catch (error) {
            console.error('❌ Erro ao atualizar cotação:', error);
            showToast(error.response?.data?.erro || 'Erro ao atualizar cotação', 'danger');
        } finally {
            setLoading(false);
        }
    };

    // =============================================
    // 🔥 APROVAR COTAÇÃO - COM CRIAÇÃO DE FATURA
    // =============================================
    const aprovarCotacao = async (id) => {
        setLoading(true);
        try {
            const response = await api.post(`/cotacoes/${id}/aprovar`);
            if (response.data) {
                const faturaCriada = response.data.fatura;
                const mensagem = faturaCriada
                    ? `Cotação aprovada e fatura ${faturaCriada.numero} criada com sucesso!`
                    : 'Cotação aprovada com sucesso!';

                showToast(mensagem, 'success');
                setShowApproveModal(false);
                setCotacaoToApprove(null);
                fetchCotacoes(false);

                if (cotacaoDetalhes && cotacaoDetalhes._id === id) {
                    setCotacaoDetalhes(response.data.data);
                }

                if (faturaCriada) {
                    window.dispatchEvent(new CustomEvent('cotacao-aprovada', {
                        detail: {
                            cotacaoId: id,
                            fatura: faturaCriada
                        }
                    }));
                }
            }
        } catch (error) {
            console.error('❌ Erro ao aprovar cotação:', error);
            showToast(error.response?.data?.erro || 'Erro ao aprovar cotação', 'danger');
        } finally {
            setLoading(false);
        }
    };

    // =============================================
    // CANCELAR COTAÇÃO
    // =============================================
    const cancelarCotacao = async (id) => {
        if (!motivoCancelamento.trim()) {
            showToast('Informe o motivo do cancelamento', 'warning');
            return;
        }

        setLoading(true);
        try {
            const response = await api.post(`/cotacoes/${id}/cancelar`, {
                motivo: motivoCancelamento
            });

            if (response.data) {
                showToast('Cotação cancelada com sucesso!', 'success');
                setShowCancelModal(false);
                setCotacaoToCancel(null);
                setMotivoCancelamento('');
                fetchCotacoes(false);
                if (cotacaoDetalhes && cotacaoDetalhes._id === id) {
                    setCotacaoDetalhes(response.data.data);
                }
            }
        } catch (error) {
            console.error('❌ Erro ao cancelar cotação:', error);
            showToast(error.response?.data?.erro || 'Erro ao cancelar cotação', 'danger');
        } finally {
            setLoading(false);
        }
    };

    // =============================================
    // EXCLUIR COTAÇÃO
    // =============================================
    const handleDelete = async () => {
        if (!cotacaoToDelete) return;
        setLoading(true);

        try {
            const response = await api.delete(`/cotacoes/${cotacaoToDelete._id}`);
            if (response.data) {
                showToast('Cotação removida com sucesso!', 'success');
                setShowDeleteModal(false);
                setCotacaoToDelete(null);
                fetchCotacoes(false);
            }
        } catch (error) {
            console.error('❌ Erro ao remover cotação:', error);
            showToast(error.response?.data?.erro || 'Erro ao remover cotação', 'danger');
        } finally {
            setLoading(false);
        }
    };

    // =============================================
    // VER DETALHES
    // =============================================
    const verDetalhes = async (cotacao) => {
        setLoading(true);
        try {
            console.log('🔍 Buscando detalhes da cotação ID:', cotacao._id);

            const response = await api.get(`/cotacoes/${cotacao._id}`);
            console.log('📦 Resposta completa:', response.data);

            if (response.data) {
                const dadosCotacao = response.data.data || response.data;

                if (response.data.empresa) {
                    console.log('✅ Dados da empresa encontrados na resposta!');
                    const empresa = response.data.empresa;
                    console.log('🏢 Dados da empresa recebidos:', empresa);

                    const ivaPercent = empresa.percentagemIVA;

                    if (ivaPercent === undefined || ivaPercent === null) {
                        console.error('❌ ERRO: IVA da empresa não está definido!');
                        showToast('Erro: IVA da empresa não configurado. Contacte o administrador.', 'danger');
                        setLoading(false);
                        return;
                    }

                    console.log(`📊 IVA da empresa: ${ivaPercent}%`);
                    ivaRef.current = ivaPercent;

                    const modulos = empresa.modulos || empresa.modulosAtivos || {};

                    const novaConfig = {
                        id: empresa.id || empresa._id,
                        nome: empresa.nome || '',
                        nomeCurto: empresa.nomeCurto || '',
                        imagemPerfil: empresa.imagemPerfil || null,
                        nuit: empresa.nuit || '',
                        alvara: empresa.alvara || '',
                        email: empresa.email || '',
                        emailAlternativo: empresa.emailAlternativo || '',
                        telefone: empresa.telefone || '',
                        telefoneAlternativo: empresa.telefoneAlternativo || '',
                        whatsapp: empresa.whatsapp || '',
                        website: empresa.website || '',
                        morada: empresa.morada || {
                            rua: '',
                            numero: '',
                            complemento: '',
                            bairro: '',
                            quarteirao: '',
                            casa: '',
                            cidade: '',
                            provincia: '',
                            distrito: '',
                            postoAdministrativo: '',
                            pais: 'Moçambique',
                            codigoPostal: '',
                            coordenadas: {},
                            enderecoCompleto: ''
                        },
                        dadosBancarios: empresa.dadosBancarios || [],
                        bancoPrincipal: empresa.bancoPrincipal || null,
                        regimeIVA: empresa.regimeIVA || 'Normal',
                        percentagemIVA: ivaPercent,
                        retencoaFonte: empresa.retencoaFonte || false,
                        moeda: empresa.moeda || empresaConfig.moeda,
                        iva: {
                            ...empresaConfig.iva,
                            ...empresa.iva,
                            percentualPadrao: empresa.iva?.percentualPadrao ?? ivaPercent,
                            regimeAtivo: empresa.regimeIVA || 'Normal',
                            percentuaisPermitidos: empresa.iva?.percentuaisPermitidos || [0, 5, 16]
                        },
                        formatacao: empresa.formatacao || empresaConfig.formatacao,
                        configuracao: empresa.configuracao || empresaConfig.configuracao,
                        status: empresa.status || 'Ativa',
                        arquivado: empresa.arquivado || false,
                        cafe: empresa.cafe || '',
                        criadoEm: empresa.criadoEm || null,
                        actualizadoEm: empresa.actualizadoEm || null,
                        dataArquivo: empresa.dataArquivo || null,
                        motivoArquivo: empresa.motivoArquivo || '',
                        utilizadores: empresa.utilizadores || [],
                        actualizadoPor: empresa.actualizadoPor || null,
                        contactosEmergencia: empresa.contactosEmergencia || [],
                        sedes: empresa.sedes || [],
                        actividadesEconomicas: empresa.actividadesEconomicas || [],
                        estatisticas: empresa.estatisticas || {},
                        timezone: empresa.timezone || 'Africa/Maputo',
                        idioma: empresa.idioma || 'pt-PT',
                        formatoHora: empresa.formatoHora || '24h',
                        modulos: {
                            gestaoStock: modulos.gestaoStock === true || modulos.stock === true || modulos.estoque === true || false,
                            gestaoProdutos: modulos.gestaoProdutos === true || modulos.produtos === true || false,
                            gestaoServicos: modulos.gestaoServicos === true || modulos.servicos === true || false,
                            gestaoEstoque: modulos.gestaoEstoque === true || modulos.estoque === true || false,
                            gestaoArmazens: modulos.gestaoArmazens === true || modulos.armazens === true || false,
                            gestaoInventario: modulos.gestaoInventario === true || modulos.inventario === true || false,
                            gestaoCategorias: modulos.gestaoCategorias === true || modulos.categorias === true || false,
                            gestaoFrotas: modulos.gestaoFrotas === true || modulos.frotas === true || false,
                            gestaoProjetos: modulos.gestaoProjetos === true || modulos.projetos === true || false,
                            gestaoRh: modulos.gestaoRh === true || modulos.rh === true || false,
                            gestaoAtivos: modulos.gestaoAtivos === true || modulos.ativos === true || false
                        }
                    };

                    console.log('🏢 Atualizando empresaConfig:', novaConfig);
                    setEmpresaConfig(novaConfig);

                    dadosCotacao.empresa = {
                        _id: empresa.id || empresa._id,
                        nome: empresa.nome || '',
                        nomeCurto: empresa.nomeCurto || '',
                        imagemPerfil: empresa.imagemPerfil || null,
                        nuit: empresa.nuit || '',
                        email: empresa.email || '',
                        telefone: empresa.telefone || '',
                        website: empresa.website || '',
                        morada: empresa.morada || {},
                        dadosBancarios: empresa.dadosBancarios || [],
                        bancoPrincipal: empresa.bancoPrincipal || null,
                        regimeIVA: empresa.regimeIVA || 'Normal',
                        percentagemIVA: ivaPercent,
                        moeda: empresa.moeda || {},
                        cafe: empresa.cafe || '',
                        modulos: novaConfig.modulos
                    };
                } else {
                    console.warn('⚠️ Nenhum dado da empresa na resposta');
                    dadosCotacao.empresa = {
                        _id: empresaConfig.id,
                        nome: empresaConfig.nome || '',
                        imagemPerfil: empresaConfig.imagemPerfil || null,
                        nuit: empresaConfig.nuit || '',
                        email: empresaConfig.email || '',
                        telefone: empresaConfig.telefone || '',
                        website: empresaConfig.website || '',
                        morada: empresaConfig.morada || {},
                        dadosBancarios: empresaConfig.dadosBancarios || [],
                        bancoPrincipal: empresaConfig.bancoPrincipal || null,
                        regimeIVA: empresaConfig.regimeIVA || 'Normal',
                        percentagemIVA: empresaConfig.percentagemIVA,
                        moeda: empresaConfig.moeda || {},
                        cafe: empresaConfig.cafe || '',
                        modulos: empresaConfig.modulos || { gestaoStock: false }
                    };
                }

                console.log('📋 Dados finais da cotação:', dadosCotacao);
                setCotacaoDetalhes(dadosCotacao);
                setShowDetailsModal(true);
            }
        } catch (error) {
            console.error('❌ Erro ao buscar detalhes:', error);
            showToast('Erro ao carregar detalhes', 'danger');
        } finally {
            setLoading(false);
        }
    };

    // =============================================
    // CARREGAR PARA EDIÇÃO
    // =============================================
    const carregarParaEdicao = async (cotacao) => {
        setLoading(true);
        try {
            setShowDetailsModal(false);
            const response = await api.get(`/cotacoes/${cotacao._id}`);
            if (response.data) {
                const data = response.data.data;
                const clienteId = data.clienteId?._id || data.clienteId || '';

                const ivaPercentValue = data.ivaPercent !== undefined && data.ivaPercent !== null
                    ? data.ivaPercent
                    : empresaConfig.percentagemIVA;

                if (ivaPercentValue === undefined || ivaPercentValue === null) {
                    console.error('❌ ERRO: IVA não disponível para edição!');
                    showToast('Erro: IVA não configurado. Contacte o administrador.', 'danger');
                    setLoading(false);
                    return;
                }

                ivaRef.current = ivaPercentValue;

                const isIvaIncluso = data.ivaIncluso === true || ivaPercentValue === 0;

                setIvaIncluso(isIvaIncluso);

                const descontoGlobalValue = data.descontoGlobal || 0;
                const dataEmissaoValue = data.dataEmissao ? new Date(data.dataEmissao).toISOString().split('T')[0] : new Date().toISOString().split('T')[0];

                const produtosEnriquecidos = await Promise.all((data.produtos || []).map(async (item) => {
                    const itemEnriquecido = { ...item };

                    if (item.produtoId) {
                        const produtoId = typeof item.produtoId === 'object' ? item.produtoId._id : item.produtoId;
                        const produto = produtos.find(p => p._id === produtoId);
                        if (produto) {
                            itemEnriquecido.produtoData = produto;
                            itemEnriquecido.nomeItem = produto.nome;
                            itemEnriquecido.codigoItem = produto.codigo;
                            itemEnriquecido.tipoItem = 'Produto';
                        } else {
                            try {
                                const prodResponse = await api.get(`/produtos/${produtoId}`);
                                if (prodResponse.data) {
                                    const prodData = prodResponse.data.data || prodResponse.data;
                                    itemEnriquecido.produtoData = prodData;
                                    itemEnriquecido.nomeItem = prodData.nome;
                                    itemEnriquecido.codigoItem = prodData.codigo;
                                    itemEnriquecido.tipoItem = 'Produto';
                                }
                            } catch (e) {
                                console.warn('⚠️ Produto não encontrado:', produtoId);
                                itemEnriquecido.nomeItem = 'Produto não encontrado';
                                itemEnriquecido.tipoItem = 'Produto';
                            }
                        }
                    }

                    if (item.servicoId) {
                        const servicoId = typeof item.servicoId === 'object' ? item.servicoId._id : item.servicoId;
                        const servico = servicos.find(s => s._id === servicoId);
                        if (servico) {
                            itemEnriquecido.servicoData = servico;
                            itemEnriquecido.nomeItem = servico.nome;
                            itemEnriquecido.codigoItem = servico.codigo;
                            itemEnriquecido.tipoItem = 'Servico';
                        } else {
                            try {
                                const servResponse = await api.get(`/servicos/${servicoId}`);
                                if (servResponse.data) {
                                    const servData = servResponse.data.data || servResponse.data;
                                    itemEnriquecido.servicoData = servData;
                                    itemEnriquecido.nomeItem = servData.nome;
                                    itemEnriquecido.codigoItem = servData.codigo;
                                    itemEnriquecido.tipoItem = 'Servico';
                                }
                            } catch (e) {
                                console.warn('⚠️ Serviço não encontrado:', servicoId);
                                itemEnriquecido.nomeItem = 'Serviço não encontrado';
                                itemEnriquecido.tipoItem = 'Servico';
                            }
                        }
                    }

                    return itemEnriquecido;
                }));

                const ivaFinal = isIvaIncluso ? 0 : ivaPercentValue;

                const statusInicial = data.status === 'Rascunho' ? 'Pendente' : (data.status || 'Pendente');

                setEditFormData({
                    _id: data._id,
                    clienteId: clienteId,
                    produtos: produtosEnriquecidos,
                    ivaPercent: ivaFinal,
                    descontoGlobal: descontoGlobalValue,
                    dataEmissao: dataEmissaoValue,
                    validade: data.validade ? new Date(data.validade).toISOString().split('T')[0] : '',
                    observacoes: data.observacoes || '',
                    status: statusInicial
                });

                const clienteEncontrado = clientes.find(c => c._id === clienteId);
                if (clienteEncontrado) {
                    setSearchClienteEdit(clienteEncontrado.nome);
                }

                setShowEditModal(true);
            }
        } catch (error) {
            console.error('❌ Erro ao carregar cotação:', error);
            showToast('Erro ao carregar dados', 'danger');
        } finally {
            setLoading(false);
        }
    };

    // =============================================
    // TOGGLE EXPANDIR LINHA
    // =============================================
    const toggleExpandRow = (cotacaoId) => {
        setExpandedRows(prev => ({
            ...prev,
            [cotacaoId]: !prev[cotacaoId]
        }));
        if (!expandedRows[cotacaoId]) {
            setItensPage(prev => ({
                ...prev,
                [cotacaoId]: 1
            }));
        }
    };

    const changeItensPage = (cotacaoId, page) => {
        setItensPage(prev => ({
            ...prev,
            [cotacaoId]: page
        }));
    };

    // =============================================
    // 🔥 RESET FORM - CORRIGIDO COM SUBMODULOS
    // =============================================
    const resetForm = useCallback(() => {
        let ivaAtual = ivaRef.current;

        if (ivaAtual === undefined || ivaAtual === null) {
            ivaAtual = empresaConfig.percentagemIVA;
        }

        if (ivaAtual === undefined || ivaAtual === null) {
            ivaAtual = formData.ivaPercent;
        }

        if (ivaAtual === undefined || ivaAtual === null) {
            console.warn('⚠️ IVA não disponível para reset, mantendo valor atual');
            return;
        }

        ivaRef.current = ivaAtual;

        const hoje = new Date().toISOString().split('T')[0];

        const tipoInicial = showProduto ? 'produto' : 'servico';

        setFormData({
            clienteId: '',
            produtos: [],
            ivaPercent: ivaAtual,
            descontoGlobal: 0,
            dataEmissao: hoje,
            validade: '',
            observacoes: '',
            status: 'Rascunho'
        });
        setNovoItem({
            id: '',
            quantidade: 1,
            precoUnitario: 0,
            descontoPercentual: 0,
            tipo: tipoInicial
        });
        setIvaIncluso(ivaAtual === 0 || ivaAtual === 15);
        setSearchCliente('');
        setSearchItem('');
        setTipoItem(tipoInicial);
        cancelarEdicaoItem();
    }, [empresaConfig, formData.ivaPercent, showProduto]);

    // =============================================
    // 🔥 RESET EDIT FORM - CORRIGIDO COM SUBMODULOS
    // =============================================
    const resetEditForm = useCallback(() => {
        setEditFormData(null);
        setSearchClienteEdit('');
        setSearchItemEdit('');
        setIvaIncluso(false);
        const tipoInicial = showProduto ? 'produto' : 'servico';
        setTipoItem(tipoInicial);
        setNovoItem({
            id: '',
            quantidade: 1,
            precoUnitario: 0,
            descontoPercentual: 0,
            tipo: tipoInicial
        });
        cancelarEdicaoItem();
    }, [showProduto]);

    //============================================================
    // 🔥 Atualizar tipoItem quando showProduto/showServico mudar
    //============================================================

    useEffect(() => {
        console.log('🔄 useEffect - Atualizando tipoItem baseado em showProduto/showServico');
        console.log('showProduto:', showProduto, 'showServico:', showServico, 'tipoItem atual:', tipoItem);

        if (!showProduto && showServico) {
            console.log('📌 Apenas serviços disponíveis, forçando tipo=servico');
            setTipoItem('servico');
            setNovoItem(prev => ({
                ...prev,
                tipo: 'servico'
            }));
            return;
        }

        if (showProduto && !showServico) {
            console.log('📌 Apenas produtos disponíveis, forçando tipo=produto');
            setTipoItem('produto');
            setNovoItem(prev => ({
                ...prev,
                tipo: 'produto'
            }));
            return;
        }

        if (showProduto && showServico) {
            if (tipoItem !== 'produto' && tipoItem !== 'servico') {
                console.log('📌 Ambos disponíveis, tipo atual inválido, definindo como produto');
                setTipoItem('produto');
                setNovoItem(prev => ({
                    ...prev,
                    tipo: 'produto'
                }));
            } else {
                console.log('📌 Ambos disponíveis, mantendo tipo:', tipoItem);
            }
            return;
        }

        console.log('📌 Fallback: nenhum disponível, forçando serviços');
        setTipoItem('servico');
        setNovoItem(prev => ({
            ...prev,
            tipo: 'servico'
        }));
    }, [showProduto, showServico, tipoItem]);

    // =============================================
    // MENSAGENS (para a página principal)
    // =============================================
    const showMessage = (text, type = 'success') => {
        setMessage({ text, type });
        setTimeout(() => setMessage({ text: '', type: '' }), 5000);
    };

    // =============================================
    // FILTROS - CORRIGIDO
    // =============================================
    const aplicarFiltros = () => {
        setPagination(prev => ({ ...prev, page: 1 }));
        fetchCotacoes(true, 1);
    };

    const limparFiltros = () => {
        setSearchTerm('');
        setFiltroStatus('todos');
        setPagination(prev => ({ ...prev, page: 1 }));
        setTimeout(() => {
            fetchCotacoes(true, 1);
        }, 100);
    };

    // =============================================
    // PAGINAÇÃO - CORRIGIDA
    // =============================================
    const goToPage = (page) => {
        if (page < 1 || page > pagination.pages) return;
        setPagination(prev => ({ ...prev, page }));
        fetchCotacoes(true, page);
    };

    // =============================================
    // FUNÇÕES DE VALIDAÇÃO DE DESCONTO
    // =============================================
    const temItemComDesconto = useCallback(() => {
        if (!editFormData?.produtos || editFormData.produtos.length === 0) return false;
        return editFormData.produtos.some(item => (item.descontoPercentual || 0) > 0);
    }, [editFormData]);

    const descontoGlobalAtivo = useCallback(() => {
        return (editFormData?.descontoGlobal || 0) > 0;
    }, [editFormData]);

    const descontoItemDesabilitado = useCallback(() => {
        return descontoGlobalAtivo();
    }, [descontoGlobalAtivo]);

    const descontoGlobalDesabilitado = useCallback(() => {
        return temItemComDesconto();
    }, [temItemComDesconto]);

    // =============================================
    // USE EFFECTS
    // =============================================
    useEffect(() => {
        const carregarDados = async () => {
            try {
                const user = JSON.parse(localStorage.getItem('usuario') || '{}');
                if (user.empresaId) setEmpresaId(user.empresaId);
                else if (user.empresa?._id) setEmpresaId(user.empresa._id);
                else if (user.empresaId?._id) setEmpresaId(user.empresaId._id);
            } catch (e) {
                console.warn('⚠️ Não foi possível obter empresaId do usuário');
            }

            await Promise.all([
                fetchClientes(),
                fetchProdutos(),
                fetchServicos(),
                fetchCotacoes(true)
            ]);
        };
        carregarDados();
    }, []);

    // =============================================
    // ATUALIZAR O REF QUANDO O IVA DA EMPRESA MUDAR
    // =============================================
    useEffect(() => {
        if (empresaConfig.percentagemIVA !== undefined && empresaConfig.percentagemIVA !== null) {
            ivaRef.current = empresaConfig.percentagemIVA;
            console.log(`📊 IVA guardado no ref: ${ivaRef.current}%`);
        }
    }, [empresaConfig.percentagemIVA]);

    useEffect(() => {
        if (formData.clienteId) {
            const cliente = clientesFiltrados.find(c => c._id === formData.clienteId);
            if (cliente) setSearchCliente(cliente.nome);
        } else {
            setSearchCliente('');
        }
    }, [formData.clienteId, clientesFiltrados]);

    useEffect(() => {
        if (novoItem.id) {
            const items = tipoItem === 'produto' ? produtos : servicos;
            const item = items.find(p => p._id === novoItem.id);
            if (item) setSearchItem(item.nome);
        } else {
            setSearchItem('');
        }
    }, [novoItem.id, tipoItem, produtos, servicos]);

    // =============================================
    // RENDER PAGINAÇÃO PRINCIPAL
    // =============================================
    const renderPagination = () => {
        const { page, pages, total } = pagination;
        if (pages <= 1) return null;

        const startItem = (page - 1) * pagination.limit + 1;
        const endItem = Math.min(page * pagination.limit, total);

        const getPageNumbers = (currentPage, totalPages) => {
            const pagesArray = [];

            if (totalPages <= 5) {
                for (let i = 1; i <= totalPages; i++) {
                    pagesArray.push(i);
                }
                return pagesArray;
            }

            pagesArray.push(1);

            let startPage = Math.max(2, currentPage - 1);
            let endPage = Math.min(totalPages - 1, currentPage + 1);

            if (currentPage <= 3) {
                startPage = 2;
                endPage = Math.min(totalPages - 1, 4);
            }

            if (currentPage >= totalPages - 2) {
                startPage = Math.max(2, totalPages - 3);
                endPage = totalPages - 1;
            }

            if (startPage > 2) {
                pagesArray.push('...');
            }

            for (let i = startPage; i <= endPage; i++) {
                pagesArray.push(i);
            }

            if (endPage < totalPages - 1) {
                pagesArray.push('...');
            }

            pagesArray.push(totalPages);

            return pagesArray;
        };

        const pageNumbers = getPageNumbers(page, pages);

        return (
            <div className="cotacoes-pagination">
                <div className="pagination-info">
                    <span>Mostrando {startItem} - {endItem} de {total} cotações</span>
                </div>
                <div className="pagination-controls">
                    <button
                        className="pagination-btn"
                        onClick={() => goToPage(page - 1)}
                        disabled={page === 1}
                    >
                        <Icon name="chevron_left" size={18} />
                    </button>
                    <div className="pagination-numbers">
                        {pageNumbers.map((p, index) => {
                            if (p === '...') {
                                return (
                                    <span key={`dots-${index}`} className="pagination-dots">
                                        …
                                    </span>
                                );
                            }
                            return (
                                <button
                                    key={p}
                                    className={`pagination-number ${page === p ? 'active' : ''}`}
                                    onClick={() => goToPage(p)}
                                >
                                    {p}
                                </button>
                            );
                        })}
                    </div>
                    <button
                        className="pagination-btn"
                        onClick={() => goToPage(page + 1)}
                        disabled={page === pages}
                    >
                        <Icon name="chevron_right" size={18} />
                    </button>
                </div>
            </div>
        );
    };

    // =============================================
    // RENDER PRINCIPAL - MODAL DE CRIAÇÃO E EDIÇÃO
    // =============================================
    return (
        <div className="cotacoes-container">
            {/* HEADER */}
            <div className="cotacoes-header">
                <div className="header-title">
                    <h2><Icon name="receipt" size={24} /> Cotações</h2>
                    <p>Gerencie as cotações para clientes</p>
                </div>
                <div className="header-actions">
                    <button className="btn-refresh" onClick={() => fetchCotacoes(true)} title="Atualizar">
                        <Icon name="refresh" size={18} />
                    </button>
                    <button className="btn-create" onClick={() => setShowCreateModal(true)}>
                        <Icon name="add" size={18} /> Nova Cotação
                    </button>
                </div>
            </div>

            {/* MENSAGENS DA PÁGINA PRINCIPAL */}
            {message.text && (
                <div className={`alert alert-${message.type}`}>
                    <Icon name={message.type === 'success' ? 'check_circle' : 'error'} size={16} />
                    <span>{message.text}</span>
                    <button className="alert-close" onClick={() => setMessage({ text: '', type: '' })}>×</button>
                </div>
            )}

            {/* KPIs */}
            <div className="cotacoes-kpi-grid">
                <div className="kpi-card primary">
                    <div className="kpi-icon"><Icon name="receipt" /></div>
                    <div className="kpi-info">
                        <span className="kpi-value">{stats.total}</span>
                        <span className="kpi-label">Total Cotações</span>
                    </div>
                </div>
                <div className="kpi-card warning">
                    <div className="kpi-icon"><Icon name="pending" /></div>
                    <div className="kpi-info">
                        <span className="kpi-value">{stats.pendente}</span>
                        <span className="kpi-label">Pendentes</span>
                    </div>
                </div>
                <div className="kpi-card success">
                    <div className="kpi-icon"><Icon name="check_circle" /></div>
                    <div className="kpi-info">
                        <span className="kpi-value">{stats.aprovado}</span>
                        <span className="kpi-label">Aprovadas</span>
                    </div>
                </div>
            </div>

            {/* FILTROS */}
            <div className="cotacoes-filters">
                <div className="search-box">
                    <Icon name="search" size={18} />
                    <input
                        type="text"
                        placeholder="Pesquisar cotações (número, cliente)..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && aplicarFiltros()}
                    />
                    {searchTerm && (
                        <button className="search-clear" onClick={() => setSearchTerm('')}>
                            <Icon name="close" size={16} />
                        </button>
                    )}
                </div>
                <div className="filter-actions">
                    <select className="filter-select" value={filtroStatus} onChange={(e) => setFiltroStatus(e.target.value)}>
                        <option value="todos">Todos os Status</option>
                        {STATUS_COTACAO.map(s => (
                            <option key={s.value} value={s.value}>{s.label}</option>
                        ))}
                    </select>
                    <button className="btn-filter-apply" onClick={aplicarFiltros}>
                        <Icon name="search" size={16} /> Aplicar
                    </button>
                    <button className="btn-filter-clear" onClick={limparFiltros}>
                        <Icon name="clear" size={16} />
                    </button>
                </div>
            </div>

            {/* TABELA */}
            <div className="cotacoes-table-container">
                {tableLoading && (
                    <div className="table-loading-overlay active">
                        <div className="spinner"></div>
                        <span className="loading-text">A carregar cotações...</span>
                    </div>
                )}

                <div className="cotacoes-table-wrapper">
                    <table className="cotacoes-table">
                        <thead>
                            <tr>
                                <th className="col-expand" style={{ width: '40px', textAlign: 'center' }}></th>
                                <th className="col-numero">Nº</th>
                                <th className="col-cliente">Cliente</th>
                                <th className="col-desconto" style={{ textAlign: 'center' }}>Desconto</th>
                                <th className="col-iva" style={{ textAlign: 'right' }}>IVA</th>
                                <th className="col-total" style={{ textAlign: 'right' }}>Total</th>
                                <th className="col-validade" style={{ textAlign: 'center' }}>Validade</th>
                                <th className="col-status" style={{ textAlign: 'center' }}>Status</th>
                                <th className="col-acoes" style={{ textAlign: 'center' }}>Ações</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading && !tableLoading ? (
                                Array.from({ length: 5 }).map((_, index) => (
                                    <tr key={`skeleton-${index}`} className="skeleton-row">
                                        <td><div className="skeleton-line" style={{ width: '20%' }}></div></td>
                                        <td><div className="skeleton-line" style={{ width: '40%' }}></div></td>
                                        <td><div className="skeleton-line" style={{ width: '60%' }}></div></td>
                                        <td><div className="skeleton-line" style={{ width: '30%' }}></div></td>
                                        <td><div className="skeleton-line" style={{ width: '30%' }}></div></td>
                                        <td><div className="skeleton-line" style={{ width: '50%' }}></div></td>
                                        <td><div className="skeleton-line" style={{ width: '40%' }}></div></td>
                                        <td><div className="skeleton-badge"></div></td>
                                        <td>
                                            <div className="skeleton-actions">
                                                <div className="skeleton-circle small"></div>
                                                <div className="skeleton-circle small"></div>
                                                <div className="skeleton-circle small"></div>
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            ) : filteredCotacoes.length === 0 ? (
                                <tr className="empty-row">
                                    <td colSpan="9">
                                        <div className="empty-state">
                                            <Icon name="receipt" size={48} />
                                            <p>Nenhuma cotação encontrada</p>
                                            <button className="btn-create-small" onClick={() => setShowCreateModal(true)}>
                                                <Icon name="add" size={16} /> Criar primeira cotação
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ) : (
                                filteredCotacoes.map(cotacao => {
                                    const statusValue = cotacao.status || 'Pendente';
                                    const statusInfo = getStatusInfo(statusValue);
                                    const totalItens = cotacao.produtos?.length || 0;
                                    const isExpanded = expandedRows[cotacao._id] || false;

                                    const podeEditar = !['Aprovado', 'Cancelado'].includes(statusValue);
                                    const podeAprovar = statusValue === 'Pendente' || statusValue === 'Duplicado';
                                    const podeCancelar = !['Cancelado', 'Rascunho'].includes(statusValue);
                                    const podeExcluir = !['Aprovado', 'Cancelado'].includes(statusValue);

                                    const descontoDisplay = getDescontoDisplay(cotacao);
                                    const ivaPercent = cotacao.ivaPercent ?? empresaConfig.percentagemIVA;
                                    const isIvaIncluso = cotacao.ivaIncluso === true || ivaPercent === 0 || ivaPercent === 15;
                                    const ivaDisplay = ivaPercent === undefined || ivaPercent === null
                                        ? '⚠️ N/A'
                                        : isIvaIncluso
                                            ? (ivaPercent === 0 ? 'Isento' : 'Incluso')
                                            : `${ivaPercent}%`;
                                    const ivaDescricao = getIvaDescricao(cotacao);
                                    const totalDisplay = getTotalDisplay(cotacao);
                                    const validadeDisplay = getValidadeDisplay(cotacao);
                                    const statusDisplay = getStatusFormatado(cotacao);
                                    const statusClass = getStatusClass(cotacao);
                                    const statusIcon = getStatusIcon(cotacao);

                                    const nomeCliente = getClienteNome(cotacao);
                                    const nifCliente = getClienteNif(cotacao);

                                    const temItens = cotacao.produtos && cotacao.produtos.length > 0;

                                    const currentItensPage = itensPage[cotacao._id] || 1;
                                    const totalItensPages = Math.ceil(totalItens / ITEMS_PER_PAGE);
                                    const startIndex = (currentItensPage - 1) * ITEMS_PER_PAGE;
                                    const endIndex = Math.min(startIndex + ITEMS_PER_PAGE, totalItens);
                                    const itensPaginados = cotacao.produtos?.slice(startIndex, endIndex) || [];

                                    return (
                                        <React.Fragment key={cotacao._id}>
                                            <tr className={`cotacao-row ${statusValue === 'Aprovado' ? 'approved-row' : ''} ${statusValue === 'Cancelado' ? 'cancelled-row' : ''} ${statusValue === 'Duplicado' ? 'duplicated-row' : ''} ${isExpanded ? 'expanded' : ''}`}>
                                                <td className="col-expand" style={{ textAlign: 'center' }}>
                                                    {temItens && (
                                                        <button
                                                            className="btn-expand-row"
                                                            onClick={() => toggleExpandRow(cotacao._id)}
                                                            title={isExpanded ? 'Recolher itens' : 'Expandir itens'}
                                                            style={{
                                                                background: 'none',
                                                                border: 'none',
                                                                cursor: 'pointer',
                                                                color: '#6b7280',
                                                                transition: 'transform 0.3s ease',
                                                                transform: isExpanded ? 'rotate(90deg)' : 'rotate(0deg)',
                                                                padding: '4px',
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                justifyContent: 'center'
                                                            }}
                                                        >
                                                            <Icon name="chevron_right" size={18} />
                                                        </button>
                                                    )}
                                                    {!temItens && (
                                                        <span style={{ color: '#d1d5db', fontSize: '12px' }}>•</span>
                                                    )}
                                                </td>
                                                <td className="col-numero" >
                                                    <span className="numero-badge">{cotacao.numero}</span>
                                                </td>
                                                <td className="col-cliente">
                                                    <div className="cliente-info">
                                                        <strong>{nomeCliente}</strong>
                                                        {nifCliente && (
                                                            <span className="nif">NUIT: {nifCliente}</span>
                                                        )}
                                                    </div>
                                                </td>
                                                <td className="col-desconto" style={{ textAlign: 'center' }}>
                                                    <span style={{
                                                        fontWeight: descontoDisplay !== '-' ? 700 : 400,
                                                        color: descontoDisplay !== '-' ? '#dc2626' : '#9ca3af'
                                                    }}>
                                                        {descontoDisplay}
                                                    </span>
                                                </td>
                                                <td className="col-iva" style={{ textAlign: 'right' }}>
                                                    {ivaDescricao} - {ivaDisplay}
                                                </td>
                                                <td className="col-total" style={{ textAlign: 'right' }}>
                                                    <strong>{totalDisplay}</strong>
                                                </td>
                                                <td className="col-validade" style={{ textAlign: 'center' }}>
                                                    <span className={`validade-badge ${cotacao.validade && new Date(cotacao.validade) < new Date() && statusValue !== 'Aprovado' ? 'expirado' : ''}`}>
                                                        {validadeDisplay}
                                                    </span>
                                                </td>
                                                <td className="col-status" style={{ textAlign: 'center' }}>
                                                    <span className={`status-badge ${statusClass}`}>
                                                        <Icon name={statusIcon} size={12} />
                                                        {statusDisplay}
                                                    </span>
                                                </td>
                                                <td className="col-acoes" style={{ textAlign: 'center' }}>
                                                    <div className="actions-cell">
                                                        <button className="action-btn view" onClick={() => verDetalhes(cotacao)} title="Ver detalhes">
                                                            <Icon name="visibility" size={16} />
                                                        </button>
                                                        {podeEditar && (
                                                            <button className="action-btn edit" onClick={() => carregarParaEdicao(cotacao)} title="Editar">
                                                                <Icon name="edit" size={16} />
                                                            </button>
                                                        )}
                                                        {podeAprovar && (
                                                            <button className="action-btn approve" onClick={() => {
                                                                setCotacaoToApprove(cotacao);
                                                                setShowApproveModal(true);
                                                            }} title="Aprovar">
                                                                <Icon name="check_circle" size={16} />
                                                            </button>
                                                        )}
                                                        {podeCancelar && (
                                                            <button className="action-btn cancel" onClick={() => {
                                                                setCotacaoToCancel(cotacao);
                                                                setMotivoCancelamento('');
                                                                setShowCancelModal(true);
                                                            }} title="Cancelar">
                                                                <Icon name="cancel" size={16} />
                                                            </button>
                                                        )}
                                                        {podeExcluir && (
                                                            <button className="action-btn delete" onClick={() => {
                                                                setCotacaoToDelete(cotacao);
                                                                setShowDeleteModal(true);
                                                            }} title="Excluir">
                                                                <Icon name="delete" size={16} />
                                                            </button>
                                                        )}
                                                    </div>
                                                </td>
                                            </tr>
                                            {isExpanded && temItens && (
                                                <tr className="expanded-details-row">
                                                    <td colSpan="9" style={{ padding: 0 }}>
                                                        <div className="expanded-itens-container">
                                                            <div className="expanded-itens-header">
                                                                <span className="expanded-itens-title">
                                                                    <Icon name="list" size={14} /> Itens da Cotação
                                                                </span>
                                                                <span className="expanded-itens-count">
                                                                    Total: {totalItens} {totalItens === 1 ? 'item' : 'itens'}
                                                                    {totalItensPages > 1 && ` | Pág. ${currentItensPage} de ${totalItensPages}`}
                                                                </span>
                                                            </div>
                                                            <table className="itens-expanded-table">
                                                                <thead>
                                                                    <tr>
                                                                        <th className="col-item">Item</th>
                                                                        <th className="col-tipo">Tipo</th>
                                                                        <th className="col-qtd">Qtd</th>
                                                                        <th className="col-preco">Preço Unit.</th>
                                                                        <th className="col-desconto-header">Desconto %</th>
                                                                        <th className="col-subtotal-header">Subtotal</th>
                                                                    </tr>
                                                                </thead>
                                                                <tbody>
                                                                    {itensPaginados.map((item, idx) => {
                                                                        const isServico = !!item.servicoId;
                                                                        const itemId = isServico ? item.servicoId : item.produtoId;
                                                                        const itemsList = isServico ? servicos : produtos;
                                                                        const itemData = itemsList.find(p => p._id === itemId);
                                                                        const nomeItem = itemData ? itemData.nome : getItemNome(item);
                                                                        const codigoItem = itemData ? itemData.codigo : getItemCodigo(item);
                                                                        const descontoPercentual = item.descontoPercentual || 0;
                                                                        const subtotalItem = item.quantidade * item.precoUnitario * (1 - descontoPercentual / 100);
                                                                        const tipoLabel = isServico ? 'Serviço' : 'Produto';

                                                                        return (
                                                                            <tr key={idx} className="expanded-item-row">
                                                                                <td className="col-item">
                                                                                    <span className="item-nome">{nomeItem}</span>
                                                                                    {codigoItem && (
                                                                                        <span className="item-codigo">({codigoItem})</span>
                                                                                    )}
                                                                                </td>
                                                                                <td className="col-tipo">
                                                                                    <span className={`tipo-badge ${isServico ? 'servico' : 'produto'}`}>
                                                                                        {tipoLabel}
                                                                                    </span>
                                                                                </td>
                                                                                <td className="col-qtd">{item.quantidade}</td>
                                                                                <td className="col-preco">{formatarMoeda(item.precoUnitario)}</td>
                                                                                <td className="col-desconto">
                                                                                    {descontoPercentual > 0 ? (
                                                                                        <span className="desconto-positivo">{descontoPercentual}%</span>
                                                                                    ) : (
                                                                                        <span className="desconto-zero">0%</span>
                                                                                    )}
                                                                                </td>
                                                                                <td className="col-subtotal">{formatarMoeda(subtotalItem)}</td>
                                                                            </tr>
                                                                        );
                                                                    })}
                                                                </tbody>
                                                                {totalItens > 0 && (
                                                                    <tfoot>
                                                                        <tr className="subtotal-row">
                                                                            <td colSpan="5" className="subtotal-label">Subtotal:</td>
                                                                            <td className="subtotal-value">
                                                                                {(() => {
                                                                                    const subtotalCalculado = cotacao.produtos?.reduce((acc, item) => {
                                                                                        return acc + (item.quantidade * item.precoUnitario * (1 - (item.descontoPercentual || 0) / 100));
                                                                                    }, 0) || 0;
                                                                                    return formatarMoeda(subtotalCalculado);
                                                                                })()}
                                                                            </td>
                                                                        </tr>

                                                                        {cotacao.descontoGlobal > 0 && (
                                                                            <>
                                                                                <tr className="desconto-row">
                                                                                    <td colSpan="5" className="desconto-label">
                                                                                        Desconto ({cotacao.descontoGlobal}%):
                                                                                    </td>
                                                                                    <td className="desconto-value">
                                                                                        -{formatarMoeda(cotacao.descontoGlobalValor ||
                                                                                            (cotacao.produtos?.reduce((acc, item) => acc + (item.quantidade * item.precoUnitario * (1 - (item.descontoPercentual || 0) / 100)), 0) || 0) * (cotacao.descontoGlobal / 100))}
                                                                                    </td>
                                                                                </tr>
                                                                                <tr className="subtotal-com-desconto-row">
                                                                                    <td colSpan="5" className="subtotal-com-desconto-label">Subtotal c/ Desconto:</td>
                                                                                    <td className="subtotal-com-desconto-value">
                                                                                        {formatarMoeda(cotacao.subtotalComDesconto || cotacao.subtotal || 0)}
                                                                                    </td>
                                                                                </tr>
                                                                            </>
                                                                        )}

                                                                        <tr className="iva-row">
                                                                            <td colSpan="5" className="iva-label">IVA {cotacao.ivaPercent ? `(${cotacao.ivaPercent}%)` : ''}:</td>
                                                                            <td className="iva-value">
                                                                                {cotacao.ivaIncluso ? '-' : formatarMoeda(cotacao.iva || 0)}
                                                                            </td>
                                                                        </tr>

                                                                        <tr className="total-row">
                                                                            <td colSpan="5" className="total-label">TOTAL:</td>
                                                                            <td className="total-value">{formatarMoeda(cotacao.total || 0)}</td>
                                                                        </tr>

                                                                        {cotacao.ivaIncluso && (
                                                                            <tr>
                                                                                <td colSpan="6" className="iva-info">
                                                                                    <Icon name="info" size={10} /> IVA já está incluído no preço
                                                                                </td>
                                                                            </tr>
                                                                        )}
                                                                    </tfoot>
                                                                )}
                                                            </table>
                                                            {totalItensPages > 1 && (
                                                                <ItensPagination
                                                                    currentPage={currentItensPage}
                                                                    totalPages={totalItensPages}
                                                                    onPageChange={(page) => changeItensPage(cotacao._id, page)}
                                                                />
                                                            )}
                                                        </div>
                                                    </td>
                                                </tr>
                                            )}
                                        </React.Fragment>
                                    );
                                })
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* PAGINAÇÃO */}
            {renderPagination()}

            {/* ============================================= */}
            {/* MODAL DE CRIAÇÃO - COM AUTO-SAVE E CONTROLE DE STOCK */}
            {/* ============================================= */}
            {showCreateModal && (
                <div className="modal-overlay" onClick={() => fecharModalCriacao()}>
                    <div className="modal-content modal-large" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <h3><Icon name="add_box" size={22} /> Nova Cotação</h3>
                            <button className="modal-close" onClick={() => fecharModalCriacao()}>×</button>
                        </div>
                        <div className="modal-body-pdf-criar">
                            <form onSubmit={handleCreate}>
                                {camposObrigatoriosPreenchidos() && (
                                    <div className="auto-save-indicator" style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '0.5rem',
                                        padding: '0.3rem 0.8rem',
                                        background: 'rgba(16, 185, 129, 0.1)',
                                        borderRadius: '8px',
                                        marginBottom: '0.5rem',
                                        color: '#059669',
                                        fontSize: '0.7rem'
                                    }}>
                                        <Icon name="check_circle" size={14} />
                                        <span>💾 Todos os campos obrigatórios preenchidos. O rascunho será salvo automaticamente ao fechar.</span>
                                    </div>
                                )}

                                <div className="form-section">
                                    <h4>Dados Gerais</h4>
                                    <div className="form-row">
                                        <div className="form-group" style={{ flex: '1 1 100%' }}>
                                            <label>Cliente <span className="required">*</span></label>
                                            <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'flex-start' }}>
                                                <div style={{ flex: 1 }}>
                                                    <SearchInput
                                                        value={searchCliente}
                                                        onChange={(val) => {
                                                            setSearchCliente(val);
                                                            if (val === '') limparSelecaoCliente();
                                                        }}
                                                        onSelect={selecionarCliente}
                                                        onClear={limparSelecaoCliente}
                                                        placeholder="Buscar cliente por nome..."
                                                        items={clientesFiltrados}
                                                        getItemLabel={(c) => c.nome}
                                                        getItemSub={(c) => c.nif ? `NUIT: ${c.nif}` : (c.email || '')}
                                                        getItemTag={(c) => c.tipoCliente || 'Particular'}
                                                        selectedId={formData.clienteId}
                                                        iconName="person_search"
                                                        iconName={false}
                                                        itemsPerPage={SEARCH_ITEMS_PER_PAGE}
                                                        noResultsMessage="Nenhum cliente encontrado"
                                                    />
                                                </div>
                                                <button
                                                    type="button"
                                                    className="btn-add-cliente"
                                                    onClick={() => setShowClienteModal(true)}
                                                    title="Adicionar novo cliente"
                                                    style={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        width: '30px',
                                                        height: '30px',
                                                        borderRadius: '8px',
                                                        border: '1.5px dashed #d1d5db',
                                                        background: 'transparent',
                                                        color: '#9ca3af',
                                                        cursor: 'pointer',
                                                        transition: 'all 0.2s ease',
                                                        flexShrink: 0,
                                                        padding: 0
                                                    }}
                                                    onMouseEnter={(e) => {
                                                        e.currentTarget.style.borderColor = '#4f46e5';
                                                        e.currentTarget.style.color = '#4f46e5';
                                                        e.currentTarget.style.background = 'rgba(79, 70, 229, 0.05)';
                                                    }}
                                                    onMouseLeave={(e) => {
                                                        e.currentTarget.style.borderColor = '#d1d5db';
                                                        e.currentTarget.style.color = '#9ca3af';
                                                        e.currentTarget.style.background = 'transparent';
                                                    }}
                                                >
                                                    <Icon name="add" size={20} />
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="form-row">
                                        <div className="form-group">
                                            <label>Data de Emissão</label>
                                            <input
                                                type="date"
                                                value={formData.dataEmissao}
                                                onChange={(e) => setFormData({ ...formData, dataEmissao: e.target.value })}
                                                style={{
                                                    width: '100%',
                                                    padding: '0.6rem 0.8rem',
                                                    border: '1.5px solid #e5e7eb',
                                                    borderRadius: '8px',
                                                    fontSize: '0.85rem',
                                                    background: '#ffffff',
                                                    color: '#1a1a2e',
                                                    outline: 'none',
                                                    transition: 'all 0.25s ease',
                                                    height: '42px',
                                                    boxSizing: 'border-box'
                                                }}
                                                onFocus={(e) => {
                                                    e.target.style.borderColor = '#4f46e5';
                                                    e.target.style.boxShadow = '0 0 0 3px rgba(79, 70, 229, 0.1)';
                                                }}
                                                onBlur={(e) => {
                                                    e.target.style.borderColor = '#e5e7eb';
                                                    e.target.style.boxShadow = 'none';
                                                }}
                                            />
                                        </div>
                                        <div className="form-group">
                                            <label>Data de Validade <span className="required">*</span></label>
                                            <input
                                                type="date"
                                                value={formData.validade}
                                                onChange={(e) => setFormData({ ...formData, validade: e.target.value })}
                                                required
                                                style={{
                                                    width: '100%',
                                                    padding: '0.6rem 0.8rem',
                                                    border: '1.5px solid #e5e7eb',
                                                    borderRadius: '8px',
                                                    fontSize: '0.85rem',
                                                    background: '#ffffff',
                                                    color: '#1a1a2e',
                                                    outline: 'none',
                                                    transition: 'all 0.25s ease',
                                                    height: '42px',
                                                    boxSizing: 'border-box'
                                                }}
                                                onFocus={(e) => {
                                                    e.target.style.borderColor = '#4f46e5';
                                                    e.target.style.boxShadow = '0 0 0 3px rgba(79, 70, 229, 0.1)';
                                                }}
                                                onBlur={(e) => {
                                                    e.target.style.borderColor = '#e5e7eb';
                                                    e.target.style.boxShadow = 'none';
                                                }}
                                            />
                                        </div>
                                    </div>
                                    <div className="form-row">
                                        <div className="form-group">
                                            <label>Desconto Global (%)</label>
                                            <input
                                                type="number"
                                                value={formData.descontoGlobal}
                                                onChange={(e) => {
                                                    const valor = parseFloat(e.target.value) || 0;
                                                    const hasItemDesconto = formData?.produtos?.some(item => (item.descontoPercentual || 0) > 0) || false;

                                                    if (valor > 0 && hasItemDesconto) {
                                                        showToast('Não é possível aplicar desconto global porque existem itens com desconto individual. Remova os descontos dos itens primeiro.', 'warning');
                                                        return;
                                                    }
                                                    setFormData({ ...formData, descontoGlobal: Math.min(Math.max(valor, 0), 100) });
                                                }}
                                                placeholder="0"
                                                min="0"
                                                max="100"
                                                step="0.5"
                                                disabled={formData?.produtos?.some(item => (item.descontoPercentual || 0) > 0) || false}
                                                style={{
                                                    width: '100%',
                                                    padding: '0.6rem 0.8rem',
                                                    border: '1.5px solid #e5e7eb',
                                                    borderRadius: '8px',
                                                    fontSize: '0.85rem',
                                                    background: formData?.produtos?.some(item => (item.descontoPercentual || 0) > 0) ? '#f3f4f6' : '#ffffff',
                                                    color: formData?.produtos?.some(item => (item.descontoPercentual || 0) > 0) ? '#6b7280' : '#1a1a2e',
                                                    outline: 'none',
                                                    transition: 'all 0.25s ease',
                                                    height: '42px',
                                                    marginBottom: 'auto',
                                                    boxSizing: 'border-box',
                                                    cursor: formData?.produtos?.some(item => (item.descontoPercentual || 0) > 0) ? 'not-allowed' : 'text'
                                                }}
                                                onFocus={(e) => {
                                                    if (!formData?.produtos?.some(item => (item.descontoPercentual || 0) > 0)) {
                                                        e.target.style.borderColor = '#4f46e5';
                                                        e.target.style.boxShadow = '0 0 0 3px rgba(79, 70, 229, 0.1)';
                                                    }
                                                }}
                                                onBlur={(e) => {
                                                    e.target.style.borderColor = '#e5e7eb';
                                                    e.target.style.boxShadow = 'none';
                                                }}
                                            />
                                            {formData?.produtos?.some(item => (item.descontoPercentual || 0) > 0) && (
                                                <small style={{
                                                    color: '#dc2626',
                                                    display: 'block',
                                                    marginTop: '0.25rem',
                                                    fontSize: '0.55rem !important'
                                                }}>
                                                    Remova os descontos individuais dos itens primeiro
                                                </small>
                                            )}
                                            {!formData?.produtos?.some(item => (item.descontoPercentual || 0) > 0) && (formData?.descontoGlobal || 0) > 0 && (
                                                <small style={{
                                                    color: '#059669',
                                                    display: 'block',
                                                    marginTop: '0.25rem',
                                                    fontSize: '0.55rem'
                                                }}>
                                                    Desconto global de {formData.descontoGlobal}% aplicado
                                                </small>
                                            )}
                                        </div>
                                        <div className="form-row">
                                        <div className="form-group">
                                            <label>Status</label>
                                            <select
                                                value={formData.status}
                                                onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                                                style={{
                                                    width: '100%',
                                                    padding: '0.6rem 0.8rem',
                                                    border: '1.5px solid #e5e7eb',
                                                    borderRadius: '8px',
                                                    fontSize: '0.85rem',
                                                    background: '#ffffff',
                                                    color: '#1a1a2e',
                                                    outline: 'none',
                                                    transition: 'all 0.25s ease',
                                                    height: '42px',
                                                    boxSizing: 'border-box',
                                                    cursor: 'pointer'
                                                }}
                                                onFocus={(e) => {
                                                    e.target.style.borderColor = '#4f46e5';
                                                    e.target.style.boxShadow = '0 0 0 3px rgba(79, 70, 229, 0.1)';
                                                }}
                                                onBlur={(e) => {
                                                    e.target.style.borderColor = '#e5e7eb';
                                                    e.target.style.boxShadow = 'none';
                                                }}
                                            >
                                                <option value="Rascunho">Rascunho</option>
                                                <option value="Pendente">Pendente</option>
                                            </select>
                                        </div>
                                        
                                    </div>
                                    
                                        <div className="form-group">
                                            <label>Observações</label>
                                            <textarea
                                                value={formData.observacoes}
                                                onChange={(e) => setFormData({ ...formData, observacoes: e.target.value })}
                                                placeholder="Observações adicionais"
                                                rows="2"
                                                style={{
                                                    width: '100%',
                                                    padding: '0.6rem 0.8rem',
                                                    border: '1.5px solid #e5e7eb',
                                                    borderRadius: '8px',
                                                    fontSize: '0.75rem !important',
                                                    background: '#ffffff',
                                                    color: '#1a1a2e',
                                                    outline: 'none',
                                                    transition: 'all 0.25s ease',
                                                    boxSizing: 'border-box',
                                                    fontFamily: 'inherit',
                                                    resize: 'vertical',
                                                    minHeight: '60px'
                                                }}
                                                onFocus={(e) => {
                                                    e.target.style.borderColor = '#4f46e5';
                                                    e.target.style.boxShadow = '0 0 0 3px rgba(79, 70, 229, 0.1)';
                                                }}
                                                onBlur={(e) => {
                                                    e.target.style.borderColor = '#e5e7eb';
                                                    e.target.style.boxShadow = 'none';
                                                }}
                                            />
                                        </div>
                                    </div>
                                </div>

                                <div className="form-section">
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                                        <h4 style={{ margin: 0 }}>Itens</h4>
                                        <ToggleProdutoServico
                                            tipo={tipoItem}
                                            onChange={(novoTipo) => {
                                                console.log(`🔄 Toggle alterado para: ${novoTipo}`);
                                                setTipoItem(novoTipo);
                                                setNovoItem({
                                                    id: '',
                                                    quantidade: 1,
                                                    precoUnitario: 0,
                                                    descontoPercentual: 0,
                                                    tipo: novoTipo
                                                });
                                                setSearchItem('');
                                            }}
                                            onAddProduto={handleAddProduto}
                                            onAddServico={handleAddServico}
                                            showProduto={showProduto}
                                            showServico={showServico}
                                        />
                                    </div>

                                    <div className="itens-container">
                                        <div className="item-form-row">
                                            <div className="form-group-produto">
                                                <label> {tipoItem === 'produto' ? 'Produto' : 'Serviço'} <span className="required">*</span></label>
                                                <SearchInput
                                                    value={searchItem}
                                                    onChange={(val) => {
                                                        setSearchItem(val);
                                                        if (val === '') limparSelecaoItem();
                                                    }}
                                                    onSelect={selecionarItem}
                                                    onClear={limparSelecaoItem}
                                                    placeholder={`Buscar ${tipoItem === 'produto' ? 'produto' : 'serviço'}...`}
                                                    items={getItemsPorTipo()}
                                                    getItemLabel={getItemLabel}
                                                    getItemSub={getItemSub}
                                                    getItemTag={getItemTag}
                                                    getItemPrice={getItemPrice}
                                                    selectedId={novoItem.id}
                                                    iconName="search"
                                                    iconName={false}
                                                    itemsPerPage={SEARCH_ITEMS_PER_PAGE}
                                                    noResultsMessage={`Nenhum ${tipoItem === 'produto' ? 'produto' : 'serviço'} encontrado`}
                                                />
                                            </div>

                                            <div className="form-group-qtd">
                                                <label style={{ marginLeft: '0.2rem' }}>Qtd <span className="required">*</span></label>
                                                <input
                                                    type="number"
                                                    value={novoItem.quantidade}
                                                    onChange={(e) => setNovoItem({ ...novoItem, quantidade: parseInt(e.target.value) || 1 })}
                                                    min="1"
                                                    step="1"
                                                    style={{ marginLeft: '0.2rem' }}
                                                />
                                            </div>

                                            <div className="form-group-desconto">
                                                <label style={{ marginLeft: '-0.7rem' }}>Desconto %</label>
                                                <input
                                                    type="number"
                                                    value={novoItem.descontoPercentual}
                                                    onChange={(e) => {
                                                        const valor = parseFloat(e.target.value) || 0;
                                                        const hasGlobalDesconto = (formData?.descontoGlobal || 0) > 0;

                                                        if (valor > 0 && hasGlobalDesconto) {
                                                            showToast('Não é possível adicionar desconto individual porque há um desconto global ativo. Remova o desconto global primeiro.', 'warning');
                                                            return;
                                                        }
                                                        setNovoItem({ ...novoItem, descontoPercentual: Math.min(Math.max(valor, 0), 100) });
                                                    }}
                                                    placeholder="0"
                                                    min="0"
                                                    max="100"
                                                    step="0.5"
                                                    disabled={(formData?.descontoGlobal || 0) > 0}
                                                    style={{
                                                        width: '100%',
                                                        padding: '0.6rem 0.8rem',
                                                        border: '1.5px solid var(--color-border, #e5e7eb)',
                                                        borderRadius: '8px',
                                                        fontSize: '0.7rem',
                                                        background: (formData?.descontoGlobal || 0) > 0
                                                            ? 'var(--color-light, #f3f4f6)'
                                                            : 'var(--color-white, #ffffff)',
                                                        color: (formData?.descontoGlobal || 0) > 0
                                                            ? 'var(--color-info-dark, #6b7280)'
                                                            : 'var(--color-dark, #1a1a2e)',
                                                        outline: 'none',
                                                        transition: 'all 0.25s ease',
                                                        height: '38px',
                                                        marginBottom: 'auto',
                                                        boxSizing: 'border-box',
                                                        cursor: (formData?.descontoGlobal || 0) > 0 ? 'not-allowed' : 'text',
                                                        marginLeft: '-0.7rem'
                                                    }}
                                                    onFocus={(e) => {
                                                        if ((formData?.descontoGlobal || 0) === 0) {
                                                            e.target.style.borderColor = 'var(--primary, #4f46e5)';
                                                            e.target.style.boxShadow = '0 0 0 3px rgba(79, 70, 229, 0.1)';
                                                        }
                                                    }}
                                                    onBlur={(e) => {
                                                        e.target.style.borderColor = 'var(--color-border, #e5e7eb)';
                                                        e.target.style.boxShadow = 'none';
                                                    }}
                                                />
                                                {(formData?.descontoGlobal || 0) > 0 && (
                                                    <small style={{
                                                        color: 'var(--danger, #dc2626)',
                                                        display: 'block',
                                                        marginTop: '0.15rem',
                                                        fontSize: '0.6rem'
                                                    }}>
                                                    </small>
                                                )}
                                            </div>

                                            <div className="form-group-preco">
                                                <label style={{ marginLeft: '-1.5rem' }}>Preço Unit. <span className="required">*</span></label>
                                                <input
                                                    type="number"
                                                    value={novoItem.precoUnitario}
                                                    onChange={(e) => setNovoItem({ ...novoItem, precoUnitario: parseFloat(e.target.value) || 0 })}
                                                    placeholder="0.00"
                                                    min="0"
                                                    step="0.01"
                                                    style={{ marginLeft: '-1.5rem' }}
                                                />
                                            </div>

                                            <div className="form-group-add">
                                                <div style={{ marginLeft: '-8px' }}>
                                                    <button
                                                        type="button"
                                                        className="btn-add-item"
                                                        onClick={() => adicionarItem(false)}
                                                    >
                                                        <Icon name={itemEditando.index !== null ? "save" : "add"} size={16} />
                                                    </button>
                                                </div>
                                            </div>
                                        </div>

                                        {formData.produtos.length > 0 && (
                                            <div className="itens-list">
                                                <table className="itens-table">
                                                    <thead>
                                                        <tr>
                                                            <th style={{ width: '30%' }}>Item</th>
                                                            <th style={{ width: '10%', textAlign: 'center' }}>Qtd</th>
                                                            <th style={{ width: '15%', textAlign: 'right' }}>Preço Unit.</th>
                                                            <th style={{ width: '12%', textAlign: 'center' }}>Desconto %</th>
                                                            <th style={{ width: '18%', textAlign: 'right' }}>Subtotal</th>
                                                            <th style={{ width: '10%', textAlign: 'center' }}>Ações</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {formData.produtos.map((item, index) => {
                                                            const isServico = !!item.servicoId;
                                                            const itemId = isServico ? item.servicoId : item.produtoId;
                                                            const itemsList = isServico ? servicos : produtos;
                                                            const itemData = itemsList.find(p => p._id === itemId);
                                                            const subtotalItem = item.quantidade * item.precoUnitario * (1 - (item.descontoPercentual || 0) / 100);
                                                            const isEditando = itemEditando.index === index && !itemEditando.isEdit;

                                                            return (
                                                                <tr key={index} className={isEditando ? 'editing' : ''}>
                                                                    <td>
                                                                        <div className="produto-info">
                                                                            <span className="produto-nome" >
                                                                                {itemData ? itemData.nome : item.nomeItem || 'Item não encontrado'}
                                                                            </span>
                                                                            {itemData?.codigo && (
                                                                                <span className="produto-codigo" >{itemData.codigo}</span>
                                                                            )}
                                                                            {isEditando && (
                                                                                <span className="edit-badge" style={{
                                                                                    marginLeft: '0.5rem',
                                                                                    fontSize: '0.55rem',
                                                                                    color: '#4f46e5',
                                                                                    background: 'rgba(79, 70, 229, 0.1)',
                                                                                    padding: '0.05rem 0.5rem',
                                                                                    borderRadius: '100px'
                                                                                }}>
                                                                                    Editando...
                                                                                </span>
                                                                            )}
                                                                        </div>
                                                                    </td>
                                                                    <td style={{ textAlign: 'center' }}>{item.quantidade}</td>
                                                                    <td style={{ textAlign: 'right' }}>{formatarMoeda(item.precoUnitario)}</td>
                                                                    <td style={{ textAlign: 'center' }}>
                                                                        {item.descontoPercentual > 0 ? (
                                                                            <span style={{ color: '#dc2626', fontWeight: 600 }}>
                                                                                {item.descontoPercentual}%
                                                                            </span>
                                                                        ) : (
                                                                            <span style={{ color: '#9ca3af' }}>0%</span>
                                                                        )}
                                                                    </td>
                                                                    <td style={{ textAlign: 'right', fontWeight: 600, color: '#4f46e5' }}>
                                                                        {formatarMoeda(subtotalItem)}
                                                                    </td>
                                                                    <td style={{ textAlign: 'center' }}>
                                                                        <div style={{ display: 'flex', gap: '0.25rem', justifyContent: 'center' }}>
                                                                            <button
                                                                                type="button"
                                                                                className="btn-edit-item"
                                                                                onClick={() => iniciarEdicaoItem(index, false)}
                                                                                title="Editar item"
                                                                                style={{
                                                                                    background: 'none',
                                                                                    border: 'none',
                                                                                    cursor: 'pointer',
                                                                                    color: '#6b7280',
                                                                                    padding: '4px',
                                                                                    borderRadius: '4px',
                                                                                    transition: 'all 0.2s ease'
                                                                                }}
                                                                                onMouseEnter={(e) => {
                                                                                    e.currentTarget.style.background = 'rgba(79, 70, 229, 0.1)';
                                                                                    e.currentTarget.style.color = '#4f46e5';
                                                                                }}
                                                                                onMouseLeave={(e) => {
                                                                                    e.currentTarget.style.background = 'transparent';
                                                                                    e.currentTarget.style.color = '#6b7280';
                                                                                }}
                                                                            >
                                                                                <Icon name="edit" size={16} />
                                                                            </button>
                                                                            <button
                                                                                type="button"
                                                                                className="btn-remove-item"
                                                                                onClick={() => removerItem(index, false)}
                                                                                title="Remover item"
                                                                                style={{
                                                                                    background: 'none',
                                                                                    border: 'none',
                                                                                    cursor: 'pointer',
                                                                                    color: '#ef4444',
                                                                                    padding: '4px',
                                                                                    borderRadius: '4px',
                                                                                    transition: 'all 0.2s ease'
                                                                                }}
                                                                                onMouseEnter={(e) => {
                                                                                    e.currentTarget.style.background = 'rgba(239, 68, 68, 0.1)';
                                                                                }}
                                                                                onMouseLeave={(e) => {
                                                                                    e.currentTarget.style.background = 'transparent';
                                                                                }}
                                                                            >
                                                                                <Icon name="delete" size={16} />
                                                                            </button>
                                                                        </div>
                                                                    </td>
                                                                </tr>
                                                            );
                                                        })}
                                                    </tbody>
                                                </table>
                                            </div>
                                        )}
                                    </div>
                                </div>

                                <div className="form-section">
                                    <h4>Resumo da Cotação</h4>
                                    <div className="resumo-container">
                                        {(() => {
                                            const produtos = formData?.produtos || [];
                                            const descontoGlobal = formData?.descontoGlobal || 0;
                                            const ivaPercent = formData?.ivaPercent ?? ivaRef.current ?? 0;

                                            let descontoItensValor = 0;
                                            let itensComDesconto = 0;

                                            produtos.forEach((item) => {
                                                const descontoPercentual = item.descontoPercentual || 0;
                                                if (descontoPercentual > 0) {
                                                    const descontoItemValor = item.precoUnitario * (descontoPercentual / 100) * item.quantidade;
                                                    descontoItensValor += descontoItemValor;
                                                    itensComDesconto++;
                                                }
                                            });

                                            const subtotalOriginal = produtos.reduce((acc, item) => {
                                                return acc + (item.quantidade * item.precoUnitario);
                                            }, 0);

                                            const subtotalComDescontosItens = subtotalOriginal - descontoItensValor;
                                            const descontoGlobalValor = subtotalComDescontosItens * (descontoGlobal / 100);
                                            const descontoTotalValor = descontoItensValor + descontoGlobalValor;
                                            const subtotalFinal = subtotalOriginal - descontoTotalValor;
                                            const percentualTotal = subtotalOriginal > 0 ? (descontoTotalValor / subtotalOriginal) * 100 : 0;
                                            const hasDesconto = descontoGlobal > 0 || itensComDesconto > 0;

                                            const isIvaIncluso = ivaIncluso || ivaPercent === 0 || ivaPercent === 15;
                                            let ivaValor = 0;
                                            let totalFinal = subtotalFinal;

                                            if (!isIvaIncluso) {
                                                ivaValor = subtotalFinal * (ivaPercent / 100);
                                                totalFinal = subtotalFinal + ivaValor;
                                            }

                                            const fmt = (valor) => formatarMoeda(valor);
                                            const isZeroDesconto = descontoTotalValor === 0;

                                            return (
                                                <>
                                                    <div className="resumo-row">
                                                        <span className="resumo-label">Subtotal</span>
                                                        <span className="resumo-valor">{fmt(subtotalOriginal)}</span>
                                                    </div>

                                                    <div className="resumo-row" style={{
                                                        color: isZeroDesconto ? '#9ca3af' : '#dc2626',
                                                        opacity: isZeroDesconto ? 0.6 : 1
                                                    }}>
                                                        <span className="resumo-label" style={{ fontWeight: 700 }}>
                                                            Desconto {!isZeroDesconto && `(${percentualTotal.toFixed(1)}%)`}
                                                            {isZeroDesconto && '(0%)'}
                                                        </span>
                                                        <span className="resumo-valor" style={{
                                                            fontWeight: 600,
                                                            color: isZeroDesconto ? '#9ca3af' : '#dc2626'
                                                        }}>
                                                            {fmt(descontoTotalValor)}
                                                        </span>
                                                    </div>

                                                    {hasDesconto && (
                                                        <div className="resumo-row">
                                                            <span className="resumo-label" style={{ fontWeight: 700 }}>Subtotal c/ Desconto</span>
                                                            <span className="resumo-valor" style={{ fontWeight: 700 }}>
                                                                {fmt(subtotalFinal)}
                                                            </span>
                                                        </div>
                                                    )}

                                                    {!isIvaIncluso && (
                                                        <div className="resumo-row">
                                                            <span className="resumo-label">IVA {ivaPercent}%</span>
                                                            <span className="resumo-valor">{fmt(ivaValor)}</span>
                                                        </div>
                                                    )}

                                                    <div className="resumo-row resumo-total">
                                                        <span className="resumo-label"><strong>Total</strong></span>
                                                        <span className="resumo-valor"><strong>{fmt(totalFinal)}</strong></span>
                                                    </div>

                                                    {isIvaIncluso && (
                                                        <div className="resumo-info" style={{ fontSize: '7px' }}>
                                                            <Icon name="info" size={9} />
                                                            {ivaPercent === 15 ? 'IVA (15%) já está incluído no preço' :
                                                                ivaPercent === 0 ? 'Operação não sujeita a IVA' :
                                                                    'IVA já está incluído no preço'}
                                                        </div>
                                                    )}

                                                    {itensComDesconto > 0 && descontoGlobal === 0 && (
                                                        <div className="resumo-info" style={{ color: '#dc2626', fontSize: '7px' }}>
                                                            <Icon name="info" size={9} />
                                                            {itensComDesconto} {itensComDesconto === 1 ? 'item' : 'itens'} com desconto individual aplicado
                                                        </div>
                                                    )}

                                                    {descontoGlobal > 0 && itensComDesconto === 0 && (
                                                        <div className="resumo-info" style={{ color: '#059669', fontSize: '7px' }}>
                                                            <Icon name="info" size={9} />
                                                            Desconto global de {descontoGlobal}% aplicado a todos os itens
                                                        </div>
                                                    )}

                                                    {descontoGlobal > 0 && itensComDesconto > 0 && (
                                                        <div className="resumo-info" style={{ color: '#dc2626', background: '#fef2f2', padding: '4px 8px', borderRadius: '4px', fontSize: '7px' }}>
                                                            <Icon name="warning" size={9} />
                                                            ⚠️ Conflito: Desconto global e descontos individuais ativos simultaneamente!
                                                        </div>
                                                    )}
                                                </>
                                            );
                                        })()}
                                    </div>
                                </div>

                                <div className="modal-actions">
                                    <button type="submit" className="modal-btn-primary" disabled={loading}>
                                        {loading ? 'A criar...' : 'Criar Cotação'}
                                    </button>
                                    <button type="button" className="modal-btn-secondary" onClick={() => fecharModalCriacao()}>
                                        {camposObrigatoriosPreenchidos() ? 'Fechar' : 'Fechar'}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            )}

            {/* ============================================= */}
            {/* MODAL DE EDIÇÃO - COM CONTROLE DE STOCK E EDIÇÃO DE ITENS */}
            {/* ============================================= */}
            {showEditModal && editFormData && (
                <div className="modal-overlay" onClick={() => { setShowEditModal(false); resetEditForm(); }}>
                    <div className="modal-content modal-large" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <h3><Icon name="edit" size={22} /> Editar Cotação</h3>
                            <button className="modal-close" onClick={() => { setShowEditModal(false); resetEditForm(); }}>×</button>
                        </div>
                        <div className="modal-body-pdf-editar">
                            <form onSubmit={handleUpdate}>

                                <div className="form-section">
                                    <h4>Dados Gerais</h4>
                                    <div className="form-row">
                                        <div className="form-group" style={{ flex: '1 1 100%' }}>
                                            <label>Cliente <span className="required">*</span></label>
                                            <div style={{
                                                background: editFormData?.clienteId ? 'rgba(16, 185, 129, 0.55)' : 'transparent',
                                                borderRadius: '8px',
                                                padding: '2px',
                                                transition: 'all 0.3s ease'
                                            }}>
                                                <SearchInput
                                                    value={searchClienteEdit}
                                                    onChange={(val) => {
                                                        setSearchClienteEdit(val);
                                                        if (val === '') limparSelecaoClienteEdit();
                                                    }}
                                                    onSelect={selecionarClienteEdit}
                                                    onClear={limparSelecaoClienteEdit}
                                                    placeholder="Buscar cliente por nome..."
                                                    items={clientes}
                                                    getItemLabel={(c) => c.nome}
                                                    getItemSub={(c) => c.nif ? `NUIT: ${c.nif}` : (c.email || '')}
                                                    getItemTag={(c) => c.tipoCliente || 'Particular'}
                                                    selectedId={editFormData.clienteId}
                                                    iconName="person_search"
                                                    iconName={false}
                                                    itemsPerPage={SEARCH_ITEMS_PER_PAGE}
                                                    noResultsMessage="Nenhum cliente encontrado"
                                                />
                                            </div>
                                        </div>
                                    </div>
                                    <div className="form-row">
                                        <div className="form-group">
                                            <label>Data de Emissão</label>
                                            <input
                                                type="date"
                                                value={editFormData.dataEmissao || new Date().toISOString().split('T')[0]}
                                                onChange={(e) => setEditFormData({ ...editFormData, dataEmissao: e.target.value })}
                                                style={{
                                                    width: '100%',
                                                    padding: '0.6rem 0.8rem',
                                                    border: '1.5px solid #e5e7eb',
                                                    borderRadius: '8px',
                                                    fontSize: '0.85rem',
                                                    background: '#ffffff',
                                                    color: '#1a1a2e',
                                                    outline: 'none',
                                                    transition: 'all 0.25s ease',
                                                    height: '42px',
                                                    boxSizing: 'border-box'
                                                }}
                                                onFocus={(e) => {
                                                    e.target.style.borderColor = '#4f46e5';
                                                    e.target.style.boxShadow = '0 0 0 3px rgba(79, 70, 229, 0.1)';
                                                }}
                                                onBlur={(e) => {
                                                    e.target.style.borderColor = '#e5e7eb';
                                                    e.target.style.boxShadow = 'none';
                                                }}
                                            />
                                        </div>
                                        <div className="form-group">
                                            <label>Data de Validade <span className="required">*</span></label>
                                            <input
                                                type="date"
                                                value={editFormData.validade}
                                                onChange={(e) => setEditFormData({ ...editFormData, validade: e.target.value })}
                                                required
                                                style={{
                                                    width: '100%',
                                                    padding: '0.6rem 0.8rem',
                                                    border: '1.5px solid #e5e7eb',
                                                    borderRadius: '8px',
                                                    fontSize: '0.85rem',
                                                    background: '#ffffff',
                                                    color: '#1a1a2e',
                                                    outline: 'none',
                                                    transition: 'all 0.25s ease',
                                                    height: '42px',
                                                    boxSizing: 'border-box'
                                                }}
                                                onFocus={(e) => {
                                                    e.target.style.borderColor = '#4f46e5';
                                                    e.target.style.boxShadow = '0 0 0 3px rgba(79, 70, 229, 0.1)';
                                                }}
                                                onBlur={(e) => {
                                                    e.target.style.borderColor = '#e5e7eb';
                                                    e.target.style.boxShadow = 'none';
                                                }}
                                            />
                                        </div>
                                    </div>
                                    <div className="form-row">
                                        <div className="form-group">
                                            <label>Desconto Global (%)</label>
                                            <input
                                                type="number"
                                                value={editFormData.descontoGlobal || 0}
                                                onChange={(e) => {
                                                    const valor = parseFloat(e.target.value) || 0;
                                                    const hasItemDesconto = editFormData?.produtos?.some(item => (item.descontoPercentual || 0) > 0) || false;

                                                    if (valor > 0 && hasItemDesconto) {
                                                        showToast('Não é possível aplicar desconto global porque existem itens com desconto individual. Remova os descontos dos itens primeiro.', 'warning');
                                                        return;
                                                    }
                                                    setEditFormData({ ...editFormData, descontoGlobal: Math.min(Math.max(valor, 0), 100) });
                                                }}
                                                placeholder="0"
                                                min="0"
                                                max="100"
                                                step="0.5"
                                                disabled={editFormData?.produtos?.some(item => (item.descontoPercentual || 0) > 0) || false}
                                                style={{
                                                    width: '100%',
                                                    padding: '0.6rem 0.8rem',
                                                    border: '1.5px solid #e5e7eb',
                                                    borderRadius: '8px',
                                                    fontSize: '0.85rem',
                                                    background: editFormData?.produtos?.some(item => (item.descontoPercentual || 0) > 0) ? '#f3f4f6' : '#ffffff',
                                                    color: editFormData?.produtos?.some(item => (item.descontoPercentual || 0) > 0) ? '#6b7280' : '#1a1a2e',
                                                    outline: 'none',
                                                    transition: 'all 0.25s ease',
                                                    height: '42px',
                                                    boxSizing: 'border-box',
                                                    cursor: editFormData?.produtos?.some(item => (item.descontoPercentual || 0) > 0) ? 'not-allowed' : 'text'
                                                }}
                                                onFocus={(e) => {
                                                    if (!editFormData?.produtos?.some(item => (item.descontoPercentual || 0) > 0)) {
                                                        e.target.style.borderColor = '#4f46e5';
                                                        e.target.style.boxShadow = '0 0 0 3px rgba(79, 70, 229, 0.1)';
                                                    }
                                                }}
                                                onBlur={(e) => {
                                                    e.target.style.borderColor = '#e5e7eb';
                                                    e.target.style.boxShadow = 'none';
                                                }}
                                            />
                                            {editFormData?.produtos?.some(item => (item.descontoPercentual || 0) > 0) && (
                                                <small style={{
                                                    color: '#dc2626',
                                                    display: 'block',
                                                    marginTop: '0.25rem',
                                                    fontSize: '0.55rem'
                                                }}>
                                                    Desabilitado: remova os descontos individuais dos itens primeiro
                                                </small>
                                            )}
                                            {!editFormData?.produtos?.some(item => (item.descontoPercentual || 0) > 0) && (editFormData?.descontoGlobal || 0) > 0 && (
                                                <small style={{
                                                    color: '#059669',
                                                    display: 'block',
                                                    marginTop: '0.25rem',
                                                    fontSize: '0.7rem'
                                                }}>
                                                    ✅ Desconto global de {editFormData.descontoGlobal}% aplicado                                                </small>
                                            )}
                                        </div>
                                        <div className="form-row">
                                        <div className="form-group">
                                            <label>Status</label>
                                            <select
                                                value={editFormData.status || 'Pendente'}
                                                onChange={(e) => setEditFormData({ ...editFormData, status: e.target.value })}
                                                disabled={['Cancelado'].includes(editFormData.status)}
                                                style={{
                                                    width: '100%',
                                                    padding: '0.6rem 0.8rem',
                                                    border: '1.5px solid #e5e7eb',
                                                    borderRadius: '8px',
                                                    fontSize: '0.85rem',
                                                    background: '#ffffff',
                                                    color: '#1a1a2e',
                                                    outline: 'none',
                                                    transition: 'all 0.25s ease',
                                                    height: '42px',
                                                    boxSizing: 'border-box',
                                                    cursor: 'pointer'
                                                }}
                                                onFocus={(e) => {
                                                    e.target.style.borderColor = '#4f46e5';
                                                    e.target.style.boxShadow = '0 0 0 3px rgba(79, 70, 229, 0.1)';
                                                }}
                                                onBlur={(e) => {
                                                    e.target.style.borderColor = '#e5e7eb';
                                                    e.target.style.boxShadow = 'none';
                                                }}
                                            >
                                                <option value="Pendente">Pendente</option>
                                                <option value="Rascunho">Rascunho</option>
                                                <option value="Aprovado">Aprovado</option>
                                            </select>
                                        </div>
                                    </div>
                                    
                                        <div className="form-group">
                                            <label>Observações</label>
                                            <textarea
                                                value={editFormData.observacoes || ''}
                                                onChange={(e) => setEditFormData({ ...editFormData, observacoes: e.target.value })}
                                                placeholder="Observações adicionais"
                                                rows="2"
                                                style={{
                                                    width: '100%',
                                                    padding: '0.6rem 0.8rem',
                                                    border: '1.5px solid #e5e7eb',
                                                    borderRadius: '8px',
                                                    fontSize: '0.85rem',
                                                    background: '#ffffff',
                                                    color: '#1a1a2e',
                                                    outline: 'none',
                                                    transition: 'all 0.25s ease',
                                                    boxSizing: 'border-box',
                                                    fontFamily: 'inherit',
                                                    resize: 'vertical',
                                                    minHeight: '60px'
                                                }}
                                                onFocus={(e) => {
                                                    e.target.style.borderColor = '#4f46e5';
                                                    e.target.style.boxShadow = '0 0 0 3px rgba(79, 70, 229, 0.1)';
                                                }}
                                                onBlur={(e) => {
                                                    e.target.style.borderColor = '#e5e7eb';
                                                    e.target.style.boxShadow = 'none';
                                                }}
                                            />
                                        </div>
                                    </div>
                                </div>

                                <div className="form-section">
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                                        <h4 style={{ margin: 0 }}>Itens</h4>
                                        <ToggleProdutoServico
                                            tipo={tipoItem}
                                            onChange={(novoTipo) => {
                                                setTipoItem(novoTipo);
                                                setNovoItem({
                                                    id: '',
                                                    quantidade: 1,
                                                    precoUnitario: 0,
                                                    descontoPercentual: 0,
                                                    tipo: novoTipo
                                                });
                                                setSearchItemEdit('');
                                            }}
                                            onAddProduto={handleAddProduto}
                                            onAddServico={handleAddServico}
                                            showProduto={showProduto}
                                            showServico={showServico}
                                        />
                                    </div>

                                    <div className="itens-container">
                                        <div className="item-form-row">
                                            <div className="form-group-produto">
                                                <label>{tipoItem === 'produto' ? 'Produto' : 'Serviço'}</label>
                                                <SearchInput
                                                    value={searchItemEdit}
                                                    onChange={(val) => {
                                                        setSearchItemEdit(val);
                                                        if (val === '') limparSelecaoItemEdit();
                                                    }}
                                                    onSelect={selecionarItemEdit}
                                                    onClear={limparSelecaoItemEdit}
                                                    placeholder={`Buscar ${tipoItem === 'produto' ? 'produto' : 'serviço'}...`}
                                                    items={getItemsPorTipo()}
                                                    getItemLabel={getItemLabel}
                                                    getItemSub={getItemSub}
                                                    getItemTag={getItemTag}
                                                    getItemPrice={getItemPrice}
                                                    selectedId={novoItem.id}
                                                    iconName="search"
                                                    iconName={false}
                                                    itemsPerPage={SEARCH_ITEMS_PER_PAGE}
                                                    noResultsMessage={`Nenhum ${tipoItem === 'produto' ? 'produto' : 'serviço'} encontrado`}
                                                />
                                            </div>

                                            <div className="form-group-qtd">
                                                <label style={{ marginLeft: '0.2rem' }}>Qtd</label>
                                                <input
                                                    type="number"
                                                    value={novoItem.quantidade}
                                                    onChange={(e) => setNovoItem({ ...novoItem, quantidade: parseInt(e.target.value) || 1 })}
                                                    min="1"
                                                    step="1"
                                                    style={{ marginLeft: '0.2rem' }}
                                                />
                                            </div>

                                            <div className="form-group-desconto">
                                                <label style={{ marginLeft: '-0.7rem' }}>Desconto %</label>
                                                <input
                                                    type="number"
                                                    value={novoItem.descontoPercentual}
                                                    onChange={(e) => {
                                                        const valor = parseFloat(e.target.value) || 0;
                                                        const hasGlobalDesconto = (editFormData?.descontoGlobal || 0) > 0;

                                                        if (valor > 0 && hasGlobalDesconto) {
                                                            showToast('Não é possível adicionar desconto individual porque há um desconto global ativo. Remova o desconto global primeiro.', 'warning');
                                                            return;
                                                        }
                                                        setNovoItem({ ...novoItem, descontoPercentual: Math.min(Math.max(valor, 0), 100) });
                                                    }}
                                                    placeholder="0"
                                                    min="0"
                                                    max="100"
                                                    step="0.5"
                                                    disabled={(editFormData?.descontoGlobal || 0) > 0}
                                                    style={{
                                                        width: '100%',
                                                        padding: '0.6rem 0.8rem',
                                                        border: '1.5px solid var(--color-border, #e5e7eb)',
                                                        borderRadius: '8px',
                                                        fontSize: '0.7rem',
                                                        background: (editFormData?.descontoGlobal || 0) > 0
                                                            ? 'var(--color-light, #f3f4f6)'
                                                            : 'var(--color-white, #ffffff)',
                                                        color: (editFormData?.descontoGlobal || 0) > 0
                                                            ? 'var(--color-info-dark, #6b7280)'
                                                            : 'var(--color-dark, #1a1a2e)',
                                                        outline: 'none',
                                                        transition: 'all 0.25s ease',
                                                        height: '38px',
                                                        marginBottom: 'auto',
                                                        boxSizing: 'border-box',
                                                        cursor: (editFormData?.descontoGlobal || 0) > 0 ? 'not-allowed' : 'text',
                                                        marginLeft: '-0.7rem'
                                                    }}
                                                    onFocus={(e) => {
                                                        if ((editFormData?.descontoGlobal || 0) === 0) {
                                                            e.target.style.borderColor = 'var(--primary, #4f46e5)';
                                                            e.target.style.boxShadow = '0 0 0 3px rgba(79, 70, 229, 0.1)';
                                                        }
                                                    }}
                                                    onBlur={(e) => {
                                                        e.target.style.borderColor = 'var(--color-border, #e5e7eb)';
                                                        e.target.style.boxShadow = 'none';
                                                    }}
                                                />
                                                {(editFormData?.descontoGlobal || 0) > 0 && (
                                                    <small style={{
                                                        color: 'var(--danger, #dc2626)',
                                                        display: 'block',
                                                        marginTop: '0.15rem',
                                                        fontSize: '0.6rem'
                                                    }}>
                                                    </small>
                                                )}
                                            </div>

                                            <div className="form-group-preco">
                                                <label style={{ marginLeft: '-1.5rem' }}>Preço Unit.</label>
                                                <input
                                                    type="number"
                                                    value={novoItem.precoUnitario}
                                                    onChange={(e) => setNovoItem({ ...novoItem, precoUnitario: parseFloat(e.target.value) || 0 })}
                                                    placeholder="0.00"
                                                    min="0"
                                                    step="0.01"
                                                    style={{ marginLeft: '-1.5rem' }}
                                                />
                                            </div>

                                            <div className="form-group-add">
                                                <div style={{ marginLeft: '-2rem' }}>
                                                    <button
                                                        type="button"
                                                        className="btn-add-item"
                                                        onClick={() => adicionarItem(true)}
                                                    >
                                                        <Icon name={itemEditando.index !== null && itemEditando.isEdit ? "save" : "add"} size={16} />
                                                    </button>
                                                </div>
                                            </div>
                                        </div>

                                        {editFormData.produtos && editFormData.produtos.length > 0 && (
                                            <div className="itens-list">
                                                <table className="itens-table">
                                                    <thead>
                                                        <tr>
                                                            <th style={{ width: '30%' }}>Item</th>
                                                            <th style={{ width: '10%', textAlign: 'center' }}>Qtd</th>
                                                            <th style={{ width: '15%', textAlign: 'right' }}>Preço Unit.</th>
                                                            <th style={{ width: '12%', textAlign: 'center' }}>Desconto %</th>
                                                            <th style={{ width: '18%', textAlign: 'right' }}>Subtotal</th>
                                                            <th style={{ width: '10%', textAlign: 'center' }}>Ações</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {editFormData.produtos.map((item, index) => {
                                                            const isServico = !!item.servicoId;
                                                            const itemId = isServico ? item.servicoId : item.produtoId;

                                                            let nomeItem = '';
                                                            let codigoItem = '';
                                                            let tipoLabel = isServico ? 'Serviço' : 'Produto';

                                                            if (item.nomeItem) {
                                                                nomeItem = item.nomeItem;
                                                                codigoItem = item.codigoItem || '';
                                                            } else if (itemId) {
                                                                const id = typeof itemId === 'object' ? itemId._id || itemId : itemId;
                                                                const itemsList = isServico ? servicos : produtos;
                                                                const itemData = itemsList.find(p => p._id === id);
                                                                if (itemData) {
                                                                    nomeItem = itemData.nome || 'Item';
                                                                    codigoItem = itemData.codigo || '';
                                                                }
                                                            } else if (item.produtoData) {
                                                                nomeItem = item.produtoData.nome || 'Item';
                                                                codigoItem = item.produtoData.codigo || '';
                                                            } else if (item.servicoData) {
                                                                nomeItem = item.servicoData.nome || 'Item';
                                                                codigoItem = item.servicoData.codigo || '';
                                                            } else if (!isServico && item.produtoId && typeof item.produtoId === 'object' && item.produtoId.nome) {
                                                                nomeItem = item.produtoId.nome;
                                                                codigoItem = item.produtoId.codigo || '';
                                                            } else if (isServico && item.servicoId && typeof item.servicoId === 'object' && item.servicoId.nome) {
                                                                nomeItem = item.servicoId.nome;
                                                                codigoItem = item.servicoId.codigo || '';
                                                            } else {
                                                                nomeItem = 'Item não encontrado';
                                                            }

                                                            const subtotalItem = item.quantidade * item.precoUnitario * (1 - (item.descontoPercentual || 0) / 100);
                                                            const isEditando = itemEditando.index === index && itemEditando.isEdit;

                                                            return (
                                                                <tr key={index} className={isEditando ? 'editing' : ''}>
                                                                    <td>
                                                                        <div className="produto-info" style={{ fontSize: '0.6rem' }}>
                                                                            <span className="produto-nome" >{nomeItem}</span>
                                                                            {codigoItem && (
                                                                                <span className="produto-codigo" >{codigoItem}</span>
                                                                            )}
                                                                            {isEditando && (
                                                                                <span className="edit-badge" style={{
                                                                                    marginLeft: '0.5rem',
                                                                                    fontSize: '0.55rem',
                                                                                    color: '#4f46e5',
                                                                                    background: 'rgba(79, 70, 229, 0.1)',
                                                                                    padding: '0.05rem 0.5rem',
                                                                                    borderRadius: '100px'
                                                                                }}>
                                                                                    Editando...
                                                                                </span>
                                                                            )}
                                                                        </div>
                                                                    </td>
                                                                    <td style={{ textAlign: 'center' }}>{item.quantidade}</td>
                                                                    <td style={{ textAlign: 'right' }}>{formatarMoeda(item.precoUnitario)}</td>
                                                                    <td style={{ textAlign: 'center' }}>
                                                                        {item.descontoPercentual > 0 ? (
                                                                            <span style={{ color: '#dc2626', fontWeight: 600 }}>
                                                                                {item.descontoPercentual}%
                                                                            </span>
                                                                        ) : (
                                                                            <span style={{ color: '#9ca3af' }}>0%</span>
                                                                        )}
                                                                    </td>
                                                                    <td style={{ textAlign: 'right', fontWeight: 600, color: '#4f46e5' }}>
                                                                        {formatarMoeda(subtotalItem)}
                                                                    </td>
                                                                    <td style={{ textAlign: 'center' }}>
                                                                        <div style={{ display: 'flex', gap: '0.25rem', justifyContent: 'center' }}>
                                                                            <button
                                                                                type="button"
                                                                                className="btn-edit-item"
                                                                                onClick={() => iniciarEdicaoItem(index, true)}
                                                                                title="Editar item"
                                                                                style={{
                                                                                    background: 'none',
                                                                                    border: 'none',
                                                                                    cursor: 'pointer',
                                                                                    color: '#6b7280',
                                                                                    padding: '4px',
                                                                                    height: '25px',
                                                                                    borderRadius: '16px',
                                                                                    transition: 'all 0.2s ease'
                                                                                }}
                                                                                onMouseEnter={(e) => {
                                                                                    e.currentTarget.style.background = 'rgba(79, 70, 229, 0.1)';
                                                                                    e.currentTarget.style.color = '#4f46e5';
                                                                                }}
                                                                                onMouseLeave={(e) => {
                                                                                    e.currentTarget.style.background = 'transparent';
                                                                                    e.currentTarget.style.color = '#6b7280';
                                                                                }}
                                                                            >
                                                                                <Icon name="edit" size={16} />
                                                                            </button>
                                                                            <button
                                                                                type="button"
                                                                                className="btn-remove-item"
                                                                                onClick={() => removerItem(index, true)}
                                                                                title="Remover item"
                                                                                style={{
                                                                                    background: 'none',
                                                                                    border: 'none',
                                                                                    cursor: 'pointer',
                                                                                    color: '#ef4444',
                                                                                    padding: '4px',
                                                                                    borderRadius: '16px',
                                                                                    transition: 'all 0.2s ease'
                                                                                }}
                                                                                onMouseEnter={(e) => {
                                                                                    e.currentTarget.style.background = 'rgba(239, 68, 68, 0.1)';
                                                                                }}
                                                                                onMouseLeave={(e) => {
                                                                                    e.currentTarget.style.background = 'transparent';
                                                                                }}
                                                                            >
                                                                                <Icon name="delete" size={16} />
                                                                            </button>
                                                                        </div>
                                                                    </td>
                                                                </tr>
                                                            );
                                                        })}
                                                    </tbody>
                                                </table>
                                            </div>
                                        )}
                                    </div>
                                </div>

                                <div className="form-section">
                                    <h4>Resumo da Cotação</h4>
                                    <div className="resumo-container">
                                        {(() => {
                                            const produtos = editFormData?.produtos || [];
                                            const descontoGlobal = editFormData?.descontoGlobal || 0;
                                            const ivaPercent = editFormData?.ivaPercent ?? ivaRef.current ?? 0;

                                            let descontoItensValor = 0;
                                            let itensComDesconto = 0;

                                            produtos.forEach((item) => {
                                                const descontoPercentual = item.descontoPercentual || 0;
                                                if (descontoPercentual > 0) {
                                                    const descontoItemValor = item.precoUnitario * (descontoPercentual / 100) * item.quantidade;
                                                    descontoItensValor += descontoItemValor;
                                                    itensComDesconto++;
                                                }
                                            });

                                            const subtotalOriginal = produtos.reduce((acc, item) => {
                                                return acc + (item.quantidade * item.precoUnitario);
                                            }, 0);

                                            const subtotalComDescontosItens = subtotalOriginal - descontoItensValor;
                                            const descontoGlobalValor = subtotalComDescontosItens * (descontoGlobal / 100);
                                            const descontoTotalValor = descontoItensValor + descontoGlobalValor;
                                            const subtotalFinal = subtotalOriginal - descontoTotalValor;
                                            const percentualTotal = subtotalOriginal > 0 ? (descontoTotalValor / subtotalOriginal) * 100 : 0;
                                            const hasDesconto = descontoGlobal > 0 || itensComDesconto > 0;

                                            const isIvaIncluso = ivaIncluso || ivaPercent === 0 || ivaPercent === 15;
                                            let ivaValor = 0;
                                            let totalFinal = subtotalFinal;

                                            if (!isIvaIncluso) {
                                                ivaValor = subtotalFinal * (ivaPercent / 100);
                                                totalFinal = subtotalFinal + ivaValor;
                                            }

                                            const fmt = (valor) => formatarMoeda(valor);
                                            const isZeroDesconto = descontoTotalValor === 0;

                                            return (
                                                <>
                                                    <div className="resumo-row">
                                                        <span className="resumo-label">Subtotal</span>
                                                        <span className="resumo-valor">{fmt(subtotalOriginal)}</span>
                                                    </div>

                                                    <div className="resumo-row" style={{
                                                        color: isZeroDesconto ? '#9ca3af' : '#dc2626',
                                                        opacity: isZeroDesconto ? 0.6 : 1
                                                    }}>
                                                        <span className="resumo-label" style={{ fontWeight: 700 }}>
                                                            Desconto {!isZeroDesconto && `(${percentualTotal.toFixed(1)}%)`}
                                                            {isZeroDesconto && '(0%)'}
                                                        </span>
                                                        <span className="resumo-valor" style={{
                                                            fontWeight: 600,
                                                            color: isZeroDesconto ? '#9ca3af' : '#dc2626'
                                                        }}>
                                                            {fmt(descontoTotalValor)}
                                                        </span>
                                                    </div>

                                                    {hasDesconto && (
                                                        <div className="resumo-row">
                                                            <span className="resumo-label" style={{ fontWeight: 700 }}>Subtotal c/ Desconto</span>
                                                            <span className="resumo-valor" style={{ fontWeight: 700 }}>
                                                                {fmt(subtotalFinal)}
                                                            </span>
                                                        </div>
                                                    )}

                                                    {!isIvaIncluso && (
                                                        <div className="resumo-row">
                                                            <span className="resumo-label">IVA {ivaPercent}%</span>
                                                            <span className="resumo-valor">{fmt(ivaValor)}</span>
                                                        </div>
                                                    )}

                                                    <div className="resumo-row resumo-total">
                                                        <span className="resumo-label"><strong>Total</strong></span>
                                                        <span className="resumo-valor"><strong>{fmt(totalFinal)}</strong></span>
                                                    </div>

                                                    {isIvaIncluso && (
                                                        <div className="resumo-info" style={{ fontSize: '7px' }}>
                                                            <Icon name="info" size={9} />
                                                            {ivaPercent === 15 ? 'IVA (15%) já está incluído no preço' :
                                                                ivaPercent === 0 ? 'Operação não sujeita a IVA' :
                                                                    'IVA já está incluído no preço'}
                                                        </div>
                                                    )}

                                                    {itensComDesconto > 0 && descontoGlobal === 0 && (
                                                        <div className="resumo-info" style={{ color: '#dc2626', fontSize: '7px' }}>
                                                            <Icon name="info" size={9} />
                                                            {itensComDesconto} {itensComDesconto === 1 ? 'item' : 'itens'} com desconto individual aplicado
                                                        </div>
                                                    )}

                                                    {descontoGlobal > 0 && itensComDesconto === 0 && (
                                                        <div className="resumo-info" style={{ color: '#059669', fontSize: '7px' }}>
                                                            <Icon name="info" size={9} />
                                                            Desconto global de {descontoGlobal}% aplicado a todos os itens
                                                        </div>
                                                    )}

                                                    {descontoGlobal > 0 && itensComDesconto > 0 && (
                                                        <div className="resumo-info" style={{ color: '#dc2626', background: '#fef2f2', padding: '4px 8px', borderRadius: '4px', fontSize: '7px' }}>
                                                            <Icon name="warning" size={9} />
                                                            ⚠️ Conflito: Desconto global e descontos individuais ativos simultaneamente!
                                                        </div>
                                                    )}
                                                </>
                                            );
                                        })()}
                                    </div>
                                </div>

                                <div className="modal-actions">
                                    <button type="submit" className="modal-btn-primary" disabled={loading}>
                                        {loading ? 'A atualizar...' : 'Atualizar Cotação'}
                                    </button>
                                    <button type="button" className="modal-btn-secondary" onClick={() => { setShowEditModal(false); resetEditForm(); }}>
                                        Cancelar
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            )}

            {/* ============================================= */}
            {/* MODAIS DE AÇÃO */}
            {/* ============================================= */}

            {/* MODAL DE APROVAÇÃO */}
            {showApproveModal && cotacaoToApprove && (
                <div className="modal-overlay" onClick={() => setShowApproveModal(false)}>
                    <div className="modal-content-popups-cotacao modal-confirm" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <h3><span className="header-icon">✅</span> Aprovar Cotação</h3>
                            <button className="modal-close" onClick={() => setShowApproveModal(false)}>×</button>
                        </div>
                        <div className="modal-body">
                            <div className="confirm-icon success">
                                <Icon name="check_circle" size={32} />
                            </div>
                            <h4>Confirmar Aprovação</h4>
                            <p>Tem certeza que deseja aprovar a cotação #{cotacaoToApprove.numero}?</p>
                            
                            <div className="confirm-details" style={{
                                marginTop: '12px',
                                padding: '12px 16px',
                                background: 'rgba(59, 130, 246, 0.05)',
                                borderRadius: '8px',
                                border: '1px solid rgba(59, 130, 246, 0.15)',
                                fontSize: '0.8rem',
                                color: '#4b5563'
                            }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    <Icon name="description" size={14} color="#3b82f6" />
                                    <span>A fatura será criada com os mesmos itens, quantidades e preços da cotação.</span>
                                </div>
                            </div>
                        </div>
                        <div className="modal-actions">
                            <button className="btn-confirm-success" onClick={() => aprovarCotacao(cotacaoToApprove._id)} disabled={loading}>
                                {loading ? 'A processar...' : 'Sim, Aprovar e Gerar Fatura'}
                            </button>
                            <button className="btn-cancel" onClick={() => setShowApproveModal(false)}>Cancelar</button>
                        </div>
                    </div>
                </div>
            )}

            {/* MODAL DE CANCELAMENTO */}
            {showCancelModal && cotacaoToCancel && (
                <div className="modal-overlay" onClick={() => setShowCancelModal(false)}>
                    <div className="modal-content-popups-cotacao modal-confirm" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <h3><span className="header-icon">❌</span> Cancelar Cotação</h3>
                            <button className="modal-close" onClick={() => setShowCancelModal(false)}>×</button>
                        </div>
                        <div className="modal-body">
                            <div className="confirm-icon danger">
                                <Icon name="warning" size={32} />
                            </div>
                            <h4>Confirmar Cancelamento</h4>
                            <p>Tem certeza que deseja cancelar a cotação #{cotacaoToCancel.numero}?</p>
                            <div className="form-group-cancelamento" >
                                <label>Motivo do cancelamento <span className="required">*</span></label>
                                <textarea
                                    value={motivoCancelamento}
                                    onChange={(e) => setMotivoCancelamento(e.target.value)}
                                    placeholder="Informe o motivo do cancelamento"
                                    rows="3"

                                    onFocus={(e) => {
                                        e.target.style.borderColor = '#4f46e5';
                                        e.target.style.boxShadow = '0 0 0 3px rgba(79, 70, 229, 0.1)';
                                    }}
                                    onBlur={(e) => {
                                        e.target.style.borderColor = '#e5e7eb';
                                        e.target.style.boxShadow = 'none';
                                    }}
                                />
                            </div>
                        </div>
                        <div className="modal-actions">
                            <button className="btn-confirm-danger" onClick={() => cancelarCotacao(cotacaoToCancel._id)} disabled={loading}>
                                {loading ? 'A processar...' : 'Sim, Cancelar'}
                            </button>
                            <button className="btn-cancel" onClick={() => setShowCancelModal(false)}>Cancelar</button>
                        </div>
                    </div>
                </div>
            )}

            {/* MODAL DE EXCLUSÃO */}
            {showDeleteModal && cotacaoToDelete && (
                <div className="modal-overlay" onClick={() => setShowDeleteModal(false)}>
                    <div className="modal-content-popups-cotacao modal-confirm" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <h3><span className="header-icon">⚠️</span> Confirmar Exclusão</h3>
                            <button className="modal-close" onClick={() => setShowDeleteModal(false)}>×</button>
                        </div>
                        <div className="modal-body">
                            <div className="confirm-icon danger">
                                <Icon name="warning" size={32} />
                            </div>
                            <h4>Remover cotação?</h4>
                            <p>Tem certeza que deseja remover a cotação #{cotacaoToDelete.numero}?</p>
                            <p className="confirm-subtext">
                                <Icon name="info" size={14} /> Esta ação é irreversível.
                            </p>
                        </div>
                        <div className="modal-actions">
                            <button className="btn-confirm-danger" onClick={handleDelete} disabled={loading}>
                                {loading ? 'A processar...' : 'Eliminar'}
                            </button>
                            <button className="btn-cancel" onClick={() => setShowDeleteModal(false)}>Cancelar</button>
                        </div>
                    </div>
                </div>
            )}

            {/* ============================================= */}
            {/* MODAL DE DETALHES DA COTAÇÃO - COM SIDE PANEL */}
            {/* ============================================= */}
            {showDetailsModal && cotacaoDetalhes && (
                <div className="modal-overlay" onClick={() => setShowDetailsModal(false)}>
                    <div className="modal-container-dual" onClick={(e) => e.stopPropagation()}>
                        {/* PAINEL ESQUERDO - CONTEÚDO PDF */}
                        <div className="modal-content modal-pdf" style={{ position: 'relative', overflow: 'hidden' }}>
                            {/* WATERMARK - CANCELADO */}
                            {cotacaoDetalhes.status === 'Cancelado' && (
                                <div className="watermark-overlay" style={{
                                    position: 'absolute',
                                    top: 0,
                                    left: 0,
                                    right: 0,
                                    bottom: 0,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    pointerEvents: 'none',
                                    zIndex: 10,
                                    borderRadius: '8px',
                                    overflow: 'hidden'
                                }}>
                                    <div className="watermark-text" style={{
                                        fontSize: '60px',
                                        fontWeight: 900,
                                        color: 'rgba(220, 38, 38, 0.12)',
                                        textTransform: 'uppercase',
                                        transform: 'rotate(-30deg)',
                                        letterSpacing: '15px',
                                        userSelect: 'none',
                                        pointerEvents: 'none',
                                        fontFamily: 'Arial, Helvetica, sans-serif',
                                        textShadow: '0 2px 10px rgba(220, 38, 38, 0.05)',
                                        border: '6px solid rgba(220, 38, 38, 0.15)',
                                        padding: '20px 50px',
                                        borderRadius: '12px',
                                        background: 'rgba(220, 38, 38, 0.03)'
                                    }}>
                                        CANCELADO
                                    </div>
                                </div>
                            )}

                            {/* 🔥 WATERMARK - APROVADO */}
                            {cotacaoDetalhes.status === 'Aprovado' && (
                                <div className="watermark-overlay" style={{
                                    position: 'absolute',
                                    top: 0,
                                    left: 0,
                                    right: 0,
                                    bottom: 0,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    pointerEvents: 'none',
                                    zIndex: 10,
                                    borderRadius: '8px',
                                    overflow: 'hidden'
                                }}>
                                    <div className="watermark-text" style={{
                                        fontSize: '50px',
                                        fontWeight: 900,
                                        color: 'rgba(16, 185, 129, 0.10)',
                                        textTransform: 'uppercase',
                                        transform: 'rotate(-30deg)',
                                        letterSpacing: '15px',
                                        userSelect: 'none',
                                        pointerEvents: 'none',
                                        fontFamily: 'Arial, Helvetica, sans-serif',
                                        textShadow: '0 2px 10px rgba(16, 185, 129, 0.03)',
                                        border: '4px solid rgba(16, 185, 129, 0.10)',
                                        padding: '15px 40px',
                                        borderRadius: '12px',
                                        background: 'rgba(16, 185, 129, 0.02)'
                                    }}>
                                        APROVADO
                                    </div>
                                </div>
                            )}

                            <div className="modal-header">
                                <h3>
                                    <Icon name="receipt" size={22} />
                                    Cotação {cotacaoDetalhes.numero}
                                    {cotacaoDetalhes.status === 'Cancelado' && (
                                        <span style={{
                                            marginLeft: '10px',
                                            fontSize: '0.7rem',
                                            fontWeight: 700,
                                            color: '#dc2626',
                                            background: 'rgba(220, 38, 38, 0.1)',
                                            padding: '2px 12px',
                                            borderRadius: '100px',
                                            letterSpacing: '1px'
                                        }}>
                                            CANCELADO
                                        </span>
                                    )}
                                    {cotacaoDetalhes.status === 'Aprovado' && (
                                        <span style={{
                                            marginLeft: '10px',
                                            fontSize: '0.7rem',
                                            fontWeight: 700,
                                            color: '#059669',
                                            background: 'rgba(16, 185, 129, 0.1)',
                                            padding: '2px 12px',
                                            borderRadius: '100px',
                                            letterSpacing: '1px'
                                        }}>
                                            APROVADO
                                        </span>
                                    )}
                                </h3>
                                <button className="modal-close" onClick={() => setShowDetailsModal(false)}>×</button>
                            </div>
                            <div className="modal-body pdf-body">
                                {/* TOPO */}
                                <div className="pdf-header-top">
                                    <div className="pdf-header-left">
                                        <span className="pdf-label">Cotação</span>
                                        <span className="pdf-value">{cotacaoDetalhes.numero}</span>
                                    </div>
                                    <div className="pdf-header-right">
                                        <span className="pdf-label">Original</span>
                                        <span className="pdf-label">Data de Emissão</span>
                                        <span className="pdf-value">{formatarData(cotacaoDetalhes.dataEmissao)}</span>
                                    </div>
                                </div>

                                {/* DADOS DA EMPRESA E CLIENTE */}
                                <div className="pdf-cliente-empresa-grid">
                                    <div className="pdf-empresa-section">
                                        <div className="pdf-empresa-grid">
                                            <div className="pdf-empresa-row-logo" style={{
                                                display: 'flex',
                                                justifyContent: 'flex-start',
                                                alignItems: 'center',
                                                padding: '4px 0 10px',
                                                borderBottom: '0px solid #e9ecef',
                                                marginTop: '-10px',
                                                marginBottom: '0px',
                                                width: '100%'
                                            }}>
                                                {(() => {
                                                    const empresaData = cotacaoDetalhes?.empresa || empresaConfig;
                                                    const logoUrl = empresaData?.imagemPerfil || null;
                                                    const nomeEmpresa = empresaData?.nome || '---';

                                                    if (logoUrl && logoUrl !== 'null' && logoUrl !== 'undefined' && logoUrl !== '') {
                                                        return (
                                                            <img
                                                                src={logoUrl}
                                                                alt={nomeEmpresa}
                                                                className="pdf-empresa-logo"
                                                                style={{
                                                                    maxHeight: '70px',
                                                                    maxWidth: '280px',
                                                                    objectFit: 'contain',
                                                                    display: 'block',
                                                                    marginRight: 'auto',
                                                                    marginLeft: '-70px'
                                                                }}
                                                                onError={(e) => {
                                                                    e.target.style.display = 'none';
                                                                    const parent = e.target.parentElement;
                                                                    if (parent) {
                                                                        parent.innerHTML = `<span class="pdf-empresa-value" style="font-size:16px;font-weight:bold;color:#1a3a5c;">${nomeEmpresa}</span>`;
                                                                    }
                                                                }}
                                                            />
                                                        );
                                                    }
                                                    return (
                                                        <span className="pdf-empresa-value" style={{
                                                            fontSize: '16px',
                                                            fontWeight: 'bold',
                                                            color: '#1a3a5c',
                                                            marginRight: 'auto'
                                                        }}>
                                                            {nomeEmpresa}
                                                        </span>
                                                    );
                                                })()}
                                            </div>
                                            <div className="pdf-empresa-row">
                                                <span className="pdf-empresa-value">
                                                    {cotacaoDetalhes?.empresa?.nome || empresaConfig.nome || '---'}
                                                </span>
                                            </div>
                                            <div className="pdf-empresa-row2">
                                                <span className="pdf-empresa-value2">
                                                    {cotacaoDetalhes?.empresa?.morada?.enderecoCompleto ||
                                                        empresaConfig.morada?.enderecoCompleto ||
                                                        '---'}
                                                </span>
                                            </div>
                                            <div className="pdf-empresa-row3">
                                                <span style={{ fontWeight: 'bold', fontSize: '8px' }}>NUIT : </span><span className="pdf-empresa-value3">
                                                    {cotacaoDetalhes?.empresa?.nuit || empresaConfig.nuit || '---'}
                                                </span>
                                            </div>
                                            <div className="pdf-empresa-row4">
                                                <span className="pdf-empresa-value4">
                                                    {cotacaoDetalhes?.empresa?.email || empresaConfig.email || '---'}
                                                </span>
                                            </div>
                                            <div className="pdf-empresa-row5">
                                                <span className="pdf-empresa-value5">
                                                    {(() => {
                                                        const website = cotacaoDetalhes?.empresa?.website || empresaConfig.website || '---';
                                                        const cleanWebsite = website
                                                            .replace(/^https?:\/\//, '')
                                                            .replace(/\/$/, '');
                                                        return cleanWebsite || '---';
                                                    })()}
                                                </span>
                                            </div>
                                            <div className="pdf-empresa-row6">
                                                <span className="pdf-empresa-value6">
                                                    {cotacaoDetalhes?.empresa?.telefone || empresaConfig.telefone || '---'}
                                                </span>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="pdf-cliente-section">
                                        <div className="pdf-cliente-grid">
                                            <div className="pdf-cliente-row" style={{ borderTop: '0px solid var(--pdf-border, #d0d7de)', marginTop: '50px' }}>
                                            </div>
                                            <div className="pdf-cliente-row">
                                                <span className="pdf-cliente-value-cotacao" style={{ fontSize: '10.5px', fontWeight: 'bold', whiteSpace: 'nowrap' }}>{getClienteNome(cotacaoDetalhes)}</span>
                                            </div>
                                            <div className="pdf-cliente-row">
                                                <span className="pdf-cliente-value" style={{ fontSize: '8px', whiteSpace: 'nowrap' }}>
                                                    {(() => {
                                                        const cliente = cotacaoDetalhes?.clienteId;
                                                        if (!cliente) return '---';
                                                        const m = cliente.morada || {};
                                                        const enderecoCompleto = [
                                                            cliente.endereco,
                                                            m.quarteirao ? `Q:${m.quarteirao}` : null,
                                                            m.cidade || cliente.cidade,
                                                            m.pais || cliente.provincia,
                                                            cliente.nacionalidade || '---',
                                                        ].filter(Boolean).join(', ');
                                                        return enderecoCompleto || cliente.endereco || '---';
                                                    })()}
                                                </span>
                                            </div>

                                            <div className="pdf-cliente-row"
                                              style={{
                                                    display: 'flex',
                                                    flexDirection: 'row',
                                                    alignItems: 'baseline',
                                                    gap: '4px',
                                                    flexWrap: 'nowrap',
                                                    whiteSpace: 'nowrap'
                                                }}
                                            >
                                                <span style={{ fontWeight: 'bold', fontSize: '8px', whiteSpace: 'nowrap' }}>
                                                    NUIT :
                                                </span>
                                                <span
                                                    className="pdf-cliente-value"
                                                    style={{ whiteSpace: 'nowrap' }}
                                                >
                                                    {getClienteNif(cotacaoDetalhes) || '---'}
                                                </span>
                                            </div>
                                            <div className="pdf-cliente-row">
                                                <span className="pdf-cliente-value">{cotacaoDetalhes.clienteId?.telefone || '---'}</span>
                                            </div>
                                            <div className="pdf-cliente-row">
                                                <span className="pdf-cliente-value" style={{ fontSize: '9px' }}>{cotacaoDetalhes.clienteId?.email || '---'}</span>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Processado por computador com CAFE */}
                                <div className="pdf-processado" style={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    padding: '4px 0 8px',
                                    borderBottom: '1px solid #e9ecef',
                                    marginBottom: '10px'
                                }}>
                                    <span className="pdf-empresa-label" style={{ fontWeight: 500, color: '#6c757d', fontSize: '8px', textTransform: 'uppercase' }}>
                                        Processado por computador
                                    </span>
                                    {(cotacaoDetalhes?.empresa?.cafe || empresaConfig.cafe) && (
                                        <span className="pdf-cafe-code" style={{
                                            fontSize: '8.5px',
                                            color: 'white',
                                            fontWeight: 700,
                                            fontFamily: 'Courier New, monospace',
                                            background: 'Grey',
                                            padding: '2px 10px',
                                            borderRadius: '4px'
                                        }}>
                                            CAFE: {cotacaoDetalhes?.empresa?.cafe || empresaConfig.cafe || '---'}
                                        </span>
                                    )}
                                </div>

                                {/* VALIDADE, MOEDA, OBSERVACOES */}
                                <div className="pdf-info-row">
                                    <div className="pdf-info-item">
                                        <span className="pdf-info-label">Validade</span>
                                        <span className="pdf-info-value">
                                            {(() => {
                                                const calcularDiasValidade = () => {
                                                    if (!cotacaoDetalhes.dataEmissao || !cotacaoDetalhes.validade) {
                                                        return '7 dias';
                                                    }
                                                    try {
                                                        const dataEmissao = new Date(cotacaoDetalhes.dataEmissao);
                                                        const dataValidade = new Date(cotacaoDetalhes.validade);
                                                        if (isNaN(dataEmissao.getTime()) || isNaN(dataValidade.getTime())) {
                                                            return '7 dias';
                                                        }
                                                        const diffTime = dataValidade.getTime() - dataEmissao.getTime();
                                                        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
                                                        if (diffDays < 0) {
                                                            return `Expirado há ${Math.abs(diffDays)} dias`;
                                                        }
                                                        return `${diffDays} dias`;
                                                    } catch (error) {
                                                        console.error('Erro ao calcular dias de validade:', error);
                                                        return '7 dias';
                                                    }
                                                };
                                                return calcularDiasValidade();
                                            })()}
                                        </span>
                                    </div>
                                    <div className="pdf-info-item">
                                        <span className="pdf-info-label">Moeda</span>
                                        <span className="pdf-info-value">{empresaConfig.moeda?.codigo || cotacaoDetalhes?.empresa?.moeda?.codigo || 'MZN'}</span>
                                    </div>
                                    <div className="pdf-info-item">
                                        <span className="pdf-info-label">Observações</span>
                                        <span className="pdf-info-value">{cotacaoDetalhes.observacoes || '-'}</span>
                                    </div>
                                </div>

                                {/* TABELA DE ITENS */}
                                <div className="pdf-itens-table-wrapper">
                                    <table className="pdf-itens-table">
                                        <thead>
                                            <tr>
                                                <th className="col-codigo">Cod</th>
                                                <th className="col-nome">Nome</th>
                                                <th className="col-descricao">Descrição</th>
                                                <th className="col-quantidade">Qtd.</th>
                                                <th className="col-preco">Preço Unitário</th>
                                                <th className="col-desconto">Desconto %</th>
                                                <th className="col-total">Preço Total</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {cotacaoDetalhes.produtos?.map((item, index) => {
                                                const isServico = !!item.servicoId;
                                                const itemId = isServico ? item.servicoId : item.produtoId;
                                                const itemsList = isServico ? servicos : produtos;
                                                const itemData = itemsList.find(p => p._id === itemId);
                                                const nomeItem = itemData ? itemData.nome : getItemNome(item);
                                                const codigoItem = itemData ? itemData.codigo : getItemCodigo(item);

                                                let descricaoItem = '';
                                                if (itemData?.descricao) {
                                                    descricaoItem = itemData.descricao;
                                                } else if (item.descricao) {
                                                    descricaoItem = item.descricao;
                                                } else if (item.produtoId && typeof item.produtoId === 'object' && item.produtoId.descricao) {
                                                    descricaoItem = item.produtoId.descricao;
                                                } else if (item.servicoId && typeof item.servicoId === 'object' && item.servicoId.descricao) {
                                                    descricaoItem = item.servicoId.descricao;
                                                } else if (item.produtoInfo?.descricao) {
                                                    descricaoItem = item.produtoInfo.descricao;
                                                }

                                                const descontoItem = item.descontoPercentual || 0;
                                                const totalComDesconto = item.quantidade * item.precoUnitario * (1 - descontoItem / 100);

                                                return (
                                                    <tr key={index}>
                                                        <td className="col-codigo">{codigoItem || '---'}</td>
                                                        <td className="col-nome">{nomeItem}</td>
                                                        <td className="col-descricao">{descricaoItem || '-'}</td>
                                                        <td className="col-quantidade">{item.quantidade}</td>
                                                        <td className="col-preco">{formatarMoeda(item.precoUnitario)}</td>
                                                        <td className="col-desconto">{descontoItem > 0 ? `${descontoItem}%` : '0%'}</td>
                                                        <td className="col-total">{formatarMoeda(totalComDesconto)}</td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>

                                {/* IMPOSTO, INCIDÊNCIA, VALOR + SUMÁRIO */}
                                <div className="pdf-tax-summary-wrapper">
                                    <div className="pdf-tax-section">
                                        <div className="pdf-tax-header">
                                            <span className="pdf-tax-label">Imposto</span>
                                            <span className="pdf-tax-label">Incidência</span>
                                            <span className="pdf-tax-label">Valor</span>
                                        </div>
                                        <div className="pdf-tax-row">
                                            {(() => {
                                                const ivaPercent = cotacaoDetalhes.ivaPercent || 0;
                                                const isIvaIncluso = cotacaoDetalhes.ivaIncluso === true || ivaPercent === 0 || ivaPercent === 15;

                                                let ivaLabel = '';
                                                let ivaValue = '';

                                                const produtos = cotacaoDetalhes.produtos || [];
                                                const descontoGlobal = cotacaoDetalhes.descontoGlobal || 0;

                                                let descontoItensValor = 0;
                                                produtos.forEach((item) => {
                                                    const descontoPercentual = item.descontoPercentual || 0;
                                                    if (descontoPercentual > 0) {
                                                        descontoItensValor += item.precoUnitario * (descontoPercentual / 100) * item.quantidade;
                                                    }
                                                });

                                                const subtotalOriginal = produtos.reduce((acc, item) => {
                                                    return acc + (item.quantidade * item.precoUnitario);
                                                }, 0);

                                                const subtotalComDescontosItens = subtotalOriginal - descontoItensValor;
                                                const descontoGlobalValor = subtotalComDescontosItens * (descontoGlobal / 100);
                                                const subtotalFinal = subtotalOriginal - descontoItensValor - descontoGlobalValor;

                                                if (isIvaIncluso) {
                                                    if (ivaPercent === 0) {
                                                        ivaLabel = `Isento`;
                                                    } else {
                                                        ivaLabel = 'IVA Incluso';
                                                    }
                                                    ivaValue = '0,00 MT';
                                                } else {
                                                    ivaLabel = `IVA ${ivaPercent}%`;
                                                    ivaValue = formatarMoeda(cotacaoDetalhes.iva || 0);
                                                }

                                                return (
                                                    <>
                                                        <span className="pdf-tax-value">{ivaLabel}</span>
                                                        <span className="pdf-tax-value">{formatarMoeda(subtotalFinal)}</span>
                                                        <span className="pdf-tax-value">{ivaValue}</span>
                                                    </>
                                                );
                                            })()}
                                        </div>

                                        <div className="pdf-iva-obs" style={{ borderTop: '0px solid var(--pdf-border, #d0d7de)', marginTop: 'auto' }}>
                                        </div>

                                        <div className="pdf-iva-obs" style={{ borderTop: '1px solid var(--pdf-border, #d0d7de)' }}>
                                        </div>

                                        <div className="pdf-iva-obs" style={{ borderTop: '0px solid var(--pdf-border, #d0d7de)' }}>
                                            {(() => {
                                                const ivaPercent = cotacaoDetalhes.ivaPercent || 0;
                                                let ivaObsText = '';
                                                if (ivaPercent === 0) {
                                                    ivaObsText = 'Iva isento. Regime de ISPC';
                                                } else if (ivaPercent === 5) {
                                                    ivaObsText = 'Iva 5% - Regime CIVA';
                                                } else if (ivaPercent === 16) {
                                                    ivaObsText = 'Iva não confere de direito a dedução, Artigo 42 do civa';
                                                }
                                                return <span>{ivaObsText}</span>;
                                            })()}
                                        </div>
                                    </div>

                                    <div className="pdf-summary-section">
                                        <div className="pdf-summary-row subtotal">
                                            <span className="pdf-summary-label">Subtotal</span>
                                            <span className="pdf-summary-value">
                                                {(() => {
                                                    const subtotalCalculado = cotacaoDetalhes.produtos?.reduce((acc, item) => {
                                                        return acc + (item.quantidade * item.precoUnitario);
                                                    }, 0) || 0;
                                                    return formatarMoeda(subtotalCalculado);
                                                })()}
                                            </span>
                                        </div>

                                        {(() => {
                                            const produtos = cotacaoDetalhes.produtos || [];
                                            const descontoGlobal = cotacaoDetalhes.descontoGlobal || 0;

                                            let descontoItensValor = 0;
                                            let itensComDesconto = 0;

                                            produtos.forEach((item) => {
                                                const descontoPercentual = item.descontoPercentual || 0;
                                                if (descontoPercentual > 0) {
                                                    const descontoItemValor = item.precoUnitario * (descontoPercentual / 100) * item.quantidade;
                                                    descontoItensValor += descontoItemValor;
                                                    itensComDesconto++;
                                                }
                                            });

                                            const subtotalOriginal = produtos.reduce((acc, item) => {
                                                return acc + (item.quantidade * item.precoUnitario);
                                            }, 0);

                                            const subtotalComDescontosItens = subtotalOriginal - descontoItensValor;
                                            const descontoGlobalValor = subtotalComDescontosItens * (descontoGlobal / 100);
                                            const descontoTotalValor = descontoItensValor + descontoGlobalValor;
                                            const subtotalFinal = subtotalOriginal - descontoTotalValor;
                                            const percentualTotal = subtotalOriginal > 0 ? (descontoTotalValor / subtotalOriginal) * 100 : 0;
                                            const hasDesconto = descontoGlobal > 0 || itensComDesconto > 0;
                                            const isZeroDesconto = descontoTotalValor === 0;

                                            return (
                                                <>
                                                    <div className="pdf-summary-row discount" style={{
                                                        color: isZeroDesconto ? '#9ca3af' : '#dc2626',
                                                        opacity: isZeroDesconto ? 0.6 : 1
                                                    }}>
                                                        <span className="pdf-summary-label" style={{ fontWeight: 700 }}>
                                                            Desconto {!isZeroDesconto && `(${percentualTotal.toFixed(1)}%)`}
                                                            {isZeroDesconto && '(0%)'}
                                                        </span>
                                                        <span className="pdf-summary-value" style={{
                                                            fontWeight: 600,
                                                            color: isZeroDesconto ? '#9ca3af' : '#dc2626'
                                                        }}>
                                                            {formatarMoeda(descontoTotalValor)}
                                                        </span>
                                                    </div>
                                                    {hasDesconto && (
                                                        <div className="pdf-summary-row subtotal-com-desconto">
                                                            <span className="pdf-summary-label" style={{ fontWeight: 700 }}>Subtotal c/ Desconto</span>
                                                            <span className="pdf-summary-value" style={{ fontWeight: 700 }}>
                                                                {formatarMoeda(subtotalFinal)}
                                                            </span>
                                                        </div>
                                                    )}
                                                </>
                                            );
                                        })()}

                                        {(() => {
                                            const ivaPercent = cotacaoDetalhes.ivaPercent || 0;
                                            const isIvaIncluso = cotacaoDetalhes.ivaIncluso === true || ivaPercent === 0 || ivaPercent === 15;

                                            if (!isIvaIncluso) {
                                                return (
                                                    <div className="pdf-summary-row iva">
                                                        <span className="pdf-summary-label">IVA {ivaPercent}%</span>
                                                        <span className="pdf-summary-value">{formatarMoeda(cotacaoDetalhes.iva || 0)}</span>
                                                    </div>
                                                );
                                            }
                                            return null;
                                        })()}

                                        <div className="pdf-summary-row total" style={{ border: '0px solid #000', marginBottom: 'auto' }}>
                                        </div>
                                        <div className="pdf-summary-row total">
                                            <span className="pdf-summary-label">Total</span>
                                            <span className="pdf-summary-value">
                                                {(() => {
                                                    const ivaPercent = cotacaoDetalhes.ivaPercent || 0;
                                                    const isIvaIncluso = cotacaoDetalhes.ivaIncluso === true || ivaPercent === 0 || ivaPercent === 15;

                                                    if (isIvaIncluso) {
                                                        const produtos = cotacaoDetalhes.produtos || [];
                                                        const descontoGlobal = cotacaoDetalhes.descontoGlobal || 0;

                                                        let descontoItensValor = 0;
                                                        produtos.forEach((item) => {
                                                            const descontoPercentual = item.descontoPercentual || 0;
                                                            if (descontoPercentual > 0) {
                                                                descontoItensValor += item.precoUnitario * (descontoPercentual / 100) * item.quantidade;
                                                            }
                                                        });

                                                        const subtotalOriginal = produtos.reduce((acc, item) => {
                                                            return acc + (item.quantidade * item.precoUnitario);
                                                        }, 0);

                                                        const subtotalComDescontosItens = subtotalOriginal - descontoItensValor;
                                                        const descontoGlobalValor = subtotalComDescontosItens * (descontoGlobal / 100);
                                                        const subtotalFinal = subtotalOriginal - descontoItensValor - descontoGlobalValor;

                                                        return formatarMoeda(subtotalFinal);
                                                    } else {
                                                        return getTotalDisplay(cotacaoDetalhes);
                                                    }
                                                })()}
                                            </span>
                                        </div>


                                    </div>
                                </div>

                                {/* MÉTODOS DE PAGAMENTO E DADOS BANCÁRIOS */}
                                <div className="pdf-footer-bottom">
                                    <div className="pdf-metodos-pagamento">
                                        <span className="pdf-metodos-label">Métodos de pagamento &amp; Relação das nossas contas</span>
                                    </div>
                                    <div className="pdf-dados-bancarios-wrapper">
                                        {(() => {
                                            const empresaData = cotacaoDetalhes?.empresa || empresaConfig;
                                            const bancos = empresaData?.dadosBancarios || [];

                                            if (bancos && bancos.length > 0) {
                                                const numBancos = bancos.length;
                                                let cols = 2;
                                                if (numBancos === 1) cols = 1;
                                                else if (numBancos === 2) cols = 2;
                                                else if (numBancos <= 4) cols = 2;
                                                else cols = 3;

                                                return (
                                                    <div
                                                        className="pdf-dados-bancarios-grid"
                                                        style={{
                                                            display: 'grid',
                                                            gridTemplateColumns: `repeat(${cols}, 1fr)`,
                                                            gap: '12px 20px',
                                                            padding: '0px 0px',
                                                            background: 'none',
                                                            borderRadius: '4px',
                                                            border: '0px solid #e9ecef'
                                                        }}
                                                    >
                                                        {bancos.map((banco, index) => (
                                                            <div
                                                                key={index}
                                                                className={`pdf-banco-card ${banco.principal ? 'principal' : ''}`}
                                                                style={{
                                                                    padding: '6px 10px',
                                                                    border: banco.principal ? '1.5px solid #059669' : '1px solid #e9ecef',
                                                                    borderRadius: '6px',
                                                                    background: banco.principal ? 'rgba(5, 150, 105, 0.05)' : 'none',
                                                                    position: 'relative'
                                                                }}
                                                            >
                                                                {banco.principal && (
                                                                    <div className="pdf-banco-principal-badge" style={{
                                                                        position: 'absolute',
                                                                        top: '-6px',
                                                                        right: '6px',
                                                                        fontSize: '7px',
                                                                        fontWeight: 'bold',
                                                                        color: '#ffffff',
                                                                        background: '#059669',
                                                                        padding: '1px 8px',
                                                                        borderRadius: '10px',
                                                                        textTransform: 'uppercase',
                                                                        letterSpacing: '0.3px'
                                                                    }}>
                                                                        Principal
                                                                    </div>
                                                                )}
                                                                <div className="pdf-banco-row" style={{ display: 'grid', gridTemplateColumns: '70px 1fr', gap: '2px 8px', fontSize: '9px', padding: '1px 0' }}>
                                                                    <span className="pdf-banco-nome" style={{ fontWeight: 600, fontSize: '8px', textTransform: 'uppercase' }}>Banco</span>
                                                                    <span className="pdf-banco-conta" style={{ fontFamily: 'Courier New, monospace', letterSpacing: '0.3px', fontWeight: 500, fontSize: '9px' }}>{banco.banco || '---'}</span>
                                                                </div>
                                                                {banco.titular && (
                                                                    <div className="pdf-banco-row" style={{ display: 'grid', gridTemplateColumns: '70px 1fr', gap: '2px 8px', fontSize: '9px', padding: '1px 0' }}>
                                                                        <span className="pdf-banco-nome" style={{ fontWeight: 600, fontSize: '8px', textTransform: 'uppercase' }}>Titular</span>
                                                                        <span className="pdf-banco-conta" style={{ fontFamily: 'Courier New, monospace', letterSpacing: '0.3px', fontWeight: 500, fontSize: '9px' }}>{banco.titular}</span>
                                                                    </div>
                                                                )}
                                                                <div className="pdf-banco-row" style={{ display: 'grid', gridTemplateColumns: '70px 1fr', gap: '2px 8px', fontSize: '9px', padding: '1px 0' }}>
                                                                    <span className="pdf-banco-nome" style={{ fontWeight: 600, fontSize: '8px', textTransform: 'uppercase' }}>Conta</span>
                                                                    <span className="pdf-banco-conta" style={{ fontFamily: 'Courier New, monospace', letterSpacing: '0.3px', fontWeight: 500, fontSize: '9px' }}>{banco.conta || '---'}</span>
                                                                </div>
                                                                <div className="pdf-banco-row" style={{ display: 'grid', gridTemplateColumns: '70px 1fr', gap: '2px 8px', fontSize: '9px', padding: '1px 0' }}>
                                                                    <span className="pdf-banco-nome" style={{ fontWeight: 600, fontSize: '8px', textTransform: 'uppercase' }}>NIB</span>
                                                                    <span className="pdf-banco-conta" style={{ fontFamily: 'Courier New, monospace', letterSpacing: '0.3px', fontWeight: 500, fontSize: '9px' }}>{banco.nib || '---'}</span>
                                                                </div>
                                                                <div className="pdf-banco-row" style={{ display: 'grid', gridTemplateColumns: '70px 1fr', gap: '2px 8px', fontSize: '9px', padding: '1px 0' }}>
                                                                    <span className="pdf-banco-nome" style={{ fontWeight: 600, fontSize: '8px', textTransform: 'uppercase' }}>Swift</span>
                                                                    <span className="pdf-banco-conta" style={{ fontFamily: 'Courier New, monospace', letterSpacing: '0.3px', fontWeight: 500, fontSize: '9px' }}>{banco.swift || '---'}</span>
                                                                </div>
                                                            </div>
                                                        ))}
                                                    </div>
                                                );
                                            }
                                            return (
                                                <div className="pdf-dados-bancarios-grid" style={{
                                                    padding: '8px 12px',
                                                    background: '#f8f9fa',
                                                    borderRadius: '4px',
                                                    border: '1px solid #e9ecef',
                                                    textAlign: 'center'
                                                }}>
                                                    <div className="pdf-banco-row" style={{ fontSize: '10px', color: '#6c757d' }}>
                                                        <span>Sem dados bancários cadastrados</span>
                                                    </div>
                                                </div>
                                            );
                                        })()}
                                    </div>

                                    <div className="pdf-footer-info" style={{
                                        display: 'grid',
                                        gridTemplateColumns: '1fr 1fr',
                                        gap: '4px 20px',
                                        padding: '6px 0',
                                        fontSize: '9px',
                                        color: '#6c757d',
                                        borderTop: '1px solid #e9ecef',
                                        marginTop: '6px'
                                    }}>
                                        <div style={{ textAlign: 'left' }}>
                                            <span className="pdf-doc-emitido-text">Documento emitido pelo Software RAF ERP</span>
                                        </div>
                                        <div style={{ textAlign: 'right' }}>
                                            <span className="pdf-usuario-text">Utilizador: <strong className="pdf-usuario-nome">{cotacaoDetalhes.criadoPor?.nome || '---'}</strong></span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* PAINEL DIREITO - SIDE PANEL DE AÇÕES */}
                        <div className="modal-side-panel">
                            <div className="side-panel-header">
                                <h4>
                                    <Icon name="settings" size={18} />
                                    Ações
                                </h4>
                            </div>

                            <div className="side-panel-body">
                                <div className="side-panel-status">
                                    <span className="status-label">Status</span>
                                    <span className={`status-badge ${getStatusClass(cotacaoDetalhes)}`}>
                                        <Icon name={getStatusIcon(cotacaoDetalhes)} size={12} />
                                        {getStatusFormatado(cotacaoDetalhes)}
                                    </span>
                                </div>

                                {cotacaoDetalhes.status === 'Cancelado' && (
                                    <>
                                        <div className="side-panel-divider"></div>
                                        <div className="side-panel-cancel-motivo" style={{
                                            background: '#fef2f2',
                                            border: '1px solid #fecaca',
                                            borderRadius: '8px',
                                            padding: '12px 14px',
                                            marginTop: '4px'
                                        }}>
                                            <div style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '8px',
                                                marginBottom: '6px'
                                            }}>
                                                <Icon name="cancel" size={16} color="#dc2626" />
                                                <span style={{
                                                    fontSize: '0.75rem',
                                                    fontWeight: 700,
                                                    color: '#dc2626',
                                                    textTransform: 'uppercase',
                                                    letterSpacing: '0.5px'
                                                }}>
                                                    Motivo do Cancelamento
                                                </span>
                                            </div>
                                            <p style={{
                                                margin: 0,
                                                fontSize: '0.85rem',
                                                color: '#1a1a2e',
                                                lineHeight: 1.5,
                                                wordBreak: 'break-word',
                                                whiteSpace: 'pre-wrap'
                                            }}>
                                                {cotacaoDetalhes.observacoesInternas || cotacaoDetalhes.motivoCancelamento || 'Motivo não informado'}
                                            </p>
                                        </div>
                                    </>
                                )}

                                <div className="side-panel-divider"></div>

                                <button
                                    className="side-panel-btn btn-pdf"
                                    onClick={async () => {
                                        if (cotacaoDetalhes.status === 'Cancelado') {
                                            showToast('Esta cotação foi cancelada e não pode ser exportada', 'warning');
                                            return;
                                        }
                                        try {
                                            setLoading(true);
                                            const token = localStorage.getItem('token');
                                            if (!token) {
                                                showToast('Faça login novamente', 'danger');
                                                setLoading(false);
                                                return;
                                            }

                                            const response = await fetch(
                                                `/api/cotacoes/${cotacaoDetalhes._id}/pdf`,
                                                {
                                                    headers: {
                                                        'Authorization': `Bearer ${token}`
                                                    }
                                                }
                                            );

                                            if (!response.ok) {
                                                const errorText = await response.text();
                                                throw new Error(`Erro ${response.status}: ${errorText}`);
                                            }

                                            const blob = await response.blob();
                                            const nomeArquivo = `Cotacao_${cotacaoDetalhes.numero}_${getClienteNome(cotacaoDetalhes)}.pdf`;

                                            const url = window.URL.createObjectURL(blob);
                                            const link = document.createElement('a');
                                            link.href = url;
                                            link.setAttribute('download', nomeArquivo);
                                            document.body.appendChild(link);
                                            link.click();

                                            setTimeout(() => {
                                                link.parentNode.removeChild(link);
                                                window.URL.revokeObjectURL(url);
                                            }, 100);

                                            showToast('PDF baixado com sucesso!', 'success');
                                        } catch (error) {
                                            console.error('❌ Erro ao baixar PDF:', error);
                                            showToast(error.message || 'Erro ao gerar PDF', 'danger');
                                        } finally {
                                            setLoading(false);
                                        }
                                    }}
                                    disabled={loading || cotacaoDetalhes.status === 'Cancelado'}
                                    style={{
                                        opacity: cotacaoDetalhes.status === 'Cancelado' ? 0.5 : 1,
                                        cursor: cotacaoDetalhes.status === 'Cancelado' ? 'not-allowed' : 'pointer'
                                    }}
                                >
                                    {loading ? (
                                        <>
                                            <span className="spinner">⏳</span>
                                            <span>Gerando...</span>
                                        </>
                                    ) : (
                                        <>
                                            <Icon name="picture_as_pdf" size={18} />
                                            <span>Baixar PDF</span>
                                        </>
                                    )}
                                </button>

                                <button
                                    className="side-panel-btn btn-print"
                                    onClick={async () => {
                                        if (cotacaoDetalhes.status === 'Cancelado') {
                                            showToast('Esta cotação foi cancelada e não pode ser impressa', 'warning');
                                            return;
                                        }

                                        try {
                                            setLoading(true);
                                            const token = localStorage.getItem('token');
                                            if (!token) {
                                                showToast('Faça login novamente', 'danger');
                                                setLoading(false);
                                                return;
                                            }

                                            // 🔥 Buscar o PDF da cotação
                                            const response = await fetch(
                                                `/api/cotacoes/${cotacaoDetalhes._id}/pdf`,
                                                {
                                                    headers: {
                                                        'Authorization': `Bearer ${token}`
                                                    }
                                                }
                                            );

                                            if (!response.ok) {
                                                const errorText = await response.text();
                                                throw new Error(`Erro ${response.status}: ${errorText}`);
                                            }

                                            // 🔥 Converter para Blob
                                            const blob = await response.blob();

                                            // 🔥 Criar URL para o Blob
                                            const url = window.URL.createObjectURL(blob);

                                            // 🔥 Abrir em nova janela para impressão
                                            const printWindow = window.open(url, '_blank');

                                            if (!printWindow) {
                                                // Se o navegador bloqueou a abertura, mostrar toast
                                                showToast('Por favor, permita pop-ups para imprimir', 'warning');
                                                window.URL.revokeObjectURL(url);
                                                setLoading(false);
                                                return;
                                            }

                                            // 🔥 Aguardar o carregamento do PDF e disparar impressão
                                            printWindow.onload = function () {
                                                setTimeout(() => {
                                                    printWindow.print();
                                                    // Não revogar a URL imediatamente, o navegador precisa dela para imprimir
                                                }, 500);
                                            };

                                            // 🔥 Fechar a janela após a impressão (opcional)
                                            // O usuário pode fechar manualmente

                                            showToast('Documento enviado para impressão', 'success');

                                        } catch (error) {
                                            console.error('❌ Erro ao imprimir:', error);
                                            showToast(error.message || 'Erro ao gerar documento para impressão', 'danger');
                                        } finally {
                                            setLoading(false);
                                        }
                                    }}
                                    disabled={loading || cotacaoDetalhes.status === 'Cancelado'}
                                    style={{
                                        opacity: cotacaoDetalhes.status === 'Cancelado' ? 0.5 : 1,
                                        cursor: cotacaoDetalhes.status === 'Cancelado' ? 'not-allowed' : 'pointer'
                                    }}
                                >
                                    {loading ? (
                                        <>
                                            <span className="spinner">⏳</span>
                                            <span>A preparar...</span>
                                        </>
                                    ) : (
                                        <>
                                            <Icon name="print" size={18} />
                                            <span>Imprimir</span>
                                        </>
                                    )}
                                </button>

                                <button
                                    className="side-panel-btn btn-duplicate"
                                    onClick={async () => {
                                        if (cotacaoDetalhes.status === 'Cancelado') {
                                            showToast('Esta cotação foi cancelada e não pode ser duplicada', 'warning');
                                            return;
                                        }
                                        try {
                                            setLoading(true);
                                            const response = await api.get(`/cotacoes/${cotacaoDetalhes._id}`);
                                            if (response.data && response.data.data) {
                                                const cotacaoOriginal = response.data.data;
                                                const dadosDuplicados = {
                                                    clienteId: cotacaoOriginal.clienteId?._id || cotacaoOriginal.clienteId,
                                                    produtos: cotacaoOriginal.produtos.map(item => ({
                                                        produtoId: item.produtoId?._id || item.produtoId || null,
                                                        servicoId: item.servicoId?._id || item.servicoId || null,
                                                        quantidade: item.quantidade,
                                                        precoUnitario: item.precoUnitario,
                                                        descontoPercentual: item.descontoPercentual || 0
                                                    })),
                                                    ivaPercent: cotacaoOriginal.ivaPercent || 0,
                                                    ivaIncluso: cotacaoOriginal.ivaIncluso || false,
                                                    descontoGlobal: cotacaoOriginal.descontoGlobal || 0,
                                                    dataEmissao: new Date().toISOString().split('T')[0],
                                                    validade: (() => {
                                                        if (cotacaoOriginal.validade) {
                                                            const dataValidade = new Date(cotacaoOriginal.validade);
                                                            const dataEmissao = new Date(cotacaoOriginal.dataEmissao);
                                                            const diffDias = Math.ceil((dataValidade - dataEmissao) / (1000 * 60 * 60 * 24));
                                                            const novaData = new Date();
                                                            novaData.setDate(novaData.getDate() + diffDias);
                                                            return novaData.toISOString().split('T')[0];
                                                        }
                                                        const novaData = new Date();
                                                        novaData.setDate(novaData.getDate() + 30);
                                                        return novaData.toISOString().split('T')[0];
                                                    })(),
                                                    observacoes: `Cotação duplicada de ${cotacaoOriginal.numero} - ${new Date().toLocaleDateString()}`,
                                                    status: 'Duplicado'
                                                };

                                                const createResponse = await api.post('/cotacoes', dadosDuplicados);
                                                if (createResponse.data) {
                                                    showToast(`Cotação duplicada com sucesso! Nova cotação: ${createResponse.data.data?.numero || ''}`, 'success');
                                                    setShowDetailsModal(false);
                                                    fetchCotacoes(false);
                                                }
                                            }
                                        } catch (error) {
                                            console.error('❌ Erro ao duplicar cotação:', error);
                                            showToast(error.response?.data?.erro || 'Erro ao duplicar cotação', 'danger');
                                        } finally {
                                            setLoading(false);
                                        }
                                    }}
                                    disabled={loading || cotacaoDetalhes.status === 'Cancelado'}
                                    style={{
                                        opacity: cotacaoDetalhes.status === 'Cancelado' ? 0.5 : 1,
                                        cursor: cotacaoDetalhes.status === 'Cancelado' ? 'not-allowed' : 'pointer'
                                    }}
                                >
                                    <Icon name="content_copy" size={18} />
                                    <span>Duplicar</span>
                                </button>

                                {cotacaoDetalhes.status !== 'Cancelado' && (
                                    <div className="side-panel-divider"></div>
                                )}

                                {cotacaoDetalhes.status !== 'Cancelado' && (
                                    <>
                                        {cotacaoDetalhes.status === 'Pendente' && (
                                            <button
                                                className="side-panel-btn btn-approve"
                                                onClick={() => {
                                                    setCotacaoToApprove(cotacaoDetalhes);
                                                    setShowDetailsModal(false);
                                                    setShowApproveModal(true);
                                                }}
                                            >
                                                <Icon name="check_circle" size={18} />
                                                <span>Aprovar Cotação</span>
                                            </button>
                                        )}

                                        {!['Cancelado'].includes(cotacaoDetalhes.status) && (
                                            <button
                                                className="side-panel-btn btn-cancel-action"
                                                onClick={() => {
                                                    setCotacaoToCancel(cotacaoDetalhes);
                                                    setShowDetailsModal(false);
                                                    setShowCancelModal(true);
                                                }}
                                            >
                                                <Icon name="cancel" size={18} />
                                                <span>Cancelar</span>
                                            </button>
                                        )}

                                        <div className="side-panel-divider"></div>
                                    </>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* MODAIS DE CLIENTE, PRODUTO E SERVIÇO */}
            {showClienteModal && (
                <ClienteModal
                    onClose={() => setShowClienteModal(false)}
                    onClienteCriado={(cliente) => {
                        fetchClientes();
                        setFormData(prev => ({
                            ...prev,
                            clienteId: cliente._id
                        }));
                        setSearchCliente(cliente.nome);
                        setShowClienteModal(false);
                        showToast(`Cliente "${cliente.nome}" adicionado com sucesso!`, 'success');
                    }}
                    empresaId={empresaId}
                />
            )}

            {showProdutoModal && (
                <ProdutoModal
                    onClose={() => setShowProdutoModal(false)}
                    onProdutoCriado={handleProdutoCriado}
                    empresaId={empresaId}
                />
            )}

            {showServicoModal && (
                <ServicoModal
                    onClose={() => setShowServicoModal(false)}
                    onServicoCriado={handleServicoCriado}
                    empresaId={empresaId}
                />
            )}

            <Toast
                message={toast.message}
                type={toast.type}
                onClose={closeToast}
            />
        </div>
    );
}